import { useState, useEffect } from "react";
import { ComposableMap, Geographies, Geography, Annotation } from "react-simple-maps";
import { useParams, Link, useNavigate } from "react-router-dom";
import { apiFetch } from "../utils/apiClient";
import VendorScreenPanel from "../components/VendorScreenPanel";
import {
  formatCurrency,
  getCompanyProfile,
  getOwnershipByCompanyId,
  searchByCompany,
  checkExclusionsBatch,
  checkPaymentsBatch,
  checkOwnershipBatch,
  checkResearchBatch,
  checkDualInterestBatch,
  checkPaymentsByCompanyBatch,
  checkResearchByCompanyBatch,
  getProviderNamesBatch,
  getResearchByCompany,
  getResearchByCompanyByYear,
  getResearchCountByCompany,
  getResearchByCompanyFull,
  getResearchProductSummaryByCompany,
  getDirectResearchByCompany,
  getDirectResearchByCompanyFull,
  getDirectResearchByCompanyByYear,
  getDirectResearchByCompanyYearlyTotals,
  getPiResearchByCompanyByYear,
  getOwnershipDetailByCompany,
  getResearchGeneralDualInterestCountByCompany,
  getCompanyAliases,
  getFilteredCompanyYearlyTotals,
  getFilteredTopProducts,
  getFilteredSpecialtyBreakdown,
  getFilteredStateBreakdown,
  getFilteredPaymentTypeBreakdown,
  getResearchProductSummaryByCompanyByYear,
  getResearchStateBreakdownByCompany,
  getPodRiskByCompany,
} from "../utils/cmsApi";
import { useSubscription } from "../context/SubscriptionContext";
import { useMyProviders } from "../context/MyProvidersContext";
import { useUser } from "@clerk/react";
import WatchProviderButton from "../components/WatchProviderButton";
import CompareButton from "../components/CompareButton";
import { watchCompany, unwatchCompany, getWatchedCompanies, queueConfirmation } from "../utils/alertsApi";
import CompanyCompareButton from "../components/CompanyCompareButton";

const ALL_YEARS = [2016, 2017, 2018, 2019, 2020, 2021, 2022, 2023, 2024, 2025];

// ── Dual interest badge logic ─────────────────────────────────────────────────
// Fires if NPI has any combination of dual interest
function hasDualInterestFlag(npi, dualInterestNpis) {
  return dualInterestNpis.has(npi);
}

// ── Provider badge row — shared across all three tab provider lists ────────────
function ProviderBadges({ npi, excludedNpis, paymentNpis, ownershipNpis, researchNpis, dualInterestNpis }) {
  const excl = excludedNpis.get(npi);
  const hasPayments = paymentNpis ? paymentNpis.has(npi) : false;
  const hasOwnership = ownershipNpis.has(npi);
  const hasResearch = researchNpis.has(npi);

  return (
    <div className="flex gap-2 flex-wrap">
      {hasPayments && (
        <span className="badge bg-gold-500/10 border border-gold-500/20 text-gold-400/80 text-xs">
          💊 Payments
        </span>
      )}
      {hasOwnership && (
        <span className="badge bg-amber-500/10 border border-amber-500/20 text-amber-400/80 text-xs">
          🏦 Ownership
        </span>
      )}
      {hasResearch && (
        <span className="badge bg-teal-500/10 border border-teal-500/20 text-teal-400/80 text-xs">
          🔬 Research
        </span>
      )}
      {excl?.oig && (
        <span className="badge bg-red-500/10 border border-red-500/30 text-red-400 text-xs">
          ⚠ OIG Excluded
        </span>
      )}
      {excl?.sam && (
        <span className="badge bg-orange-500/10 border border-orange-500/30 text-orange-400 text-xs">
          ⚠ SAM Excluded
        </span>
      )}
    </div>
  );
}

// ── Loading skeleton ──────────────────────────────────────────────────────────
function LoadingSkeleton() {
  return (
    <main className="max-w-3xl mx-auto px-4 py-10">
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="card animate-pulse">
            <div className="h-4 bg-white/10 rounded w-2/3 mb-3" />
            <div className="h-3 bg-white/5 rounded w-1/3" />
          </div>
        ))}
      </div>
    </main>
  );
}

// ── Pro gate overlay — reusable ───────────────────────────────────────────────
function ProGateOverlay({ message, subMessage, onUpgrade }) {
  return (
    <div className="absolute inset-0 backdrop-blur-md bg-navy-900/60 z-10 rounded-2xl flex flex-col items-center justify-center gap-3 p-6">
      <p className="text-white/70 font-medium text-sm text-center">
        🔒 {message}
      </p>
      <p className="text-white/40 text-xs text-center max-w-xs">{subMessage}</p>
      <button
        onClick={onUpgrade}
        className="btn-primary text-xs px-5 py-2 mt-1"
      >
        Upgrade to Pro
      </button>
    </div>
  );
}

// ── POD Risk Banner (collapsible, reused on General + Ownership tabs) ─────────
function PodRiskBanner({ podData, formatCurrency }) {
  const [open, setOpen] = useState(false);
  if (!podData || podData.length === 0) return null;

  const pod = podData[0];
  const totalOwnershipValue = podData.reduce((s, p) => s + (p.totalOwnershipValue || 0), 0);

  return (
    <div className="mb-4 bg-blue-500/[0.06] border border-blue-500/25 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start gap-2 px-4 py-3 text-left"
      >
        <span className="text-blue-400 text-sm shrink-0 mt-0.5">⚠</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-blue-400 font-medium text-sm">Potential POD Risk Detected</p>
            <span className={`text-xs px-1.5 py-0.5 rounded-full border ${
              pod.podTier === 1
                ? "bg-blue-500/20 border-blue-500/30 text-blue-300"
                : "bg-blue-500/10 border-blue-500/20 text-blue-400"
            }`}>
              Tier {pod.podTier} — {pod.podTier === 1 ? "High Confidence" : "Elevated Risk"}
            </span>
            {pod.hasDistributorName && (
              <span className="bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs px-1.5 py-0.5 rounded-full">
                Distributor name signal
              </span>
            )}
            {pod.hasDistributionTerm && (
              <span className="bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs px-1.5 py-0.5 rounded-full">
                Distribution term signal
              </span>
            )}
          </div>
          <p className="text-white/50 text-xs mt-0.5 leading-relaxed">
            This company has been flagged as a potential Physician Owned Distributorship (POD) — a high-risk arrangement identified in the OIG Work Plan 2026.
          </p>
        </div>
        <span className="text-blue-400/40 text-xs mt-0.5 shrink-0 transition-transform duration-200" style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}>▼</span>
      </button>

      {open && (
        <div className="px-4 pb-4 pt-0 border-t border-blue-500/15">
          {/* Stat row */}
          <div className="flex gap-4 flex-wrap mt-3 mb-3">
            <div>
              <p className="text-blue-400 text-xs font-mono font-medium">{podData.length}</p>
              <p className="text-white/30 text-xs">physician owners</p>
            </div>
            <div>
              <p className="text-blue-400 text-xs font-mono font-medium">
                {pod.physiciansWithPaymentsCount} of {pod.physicianOwnerCount}
              </p>
              <p className="text-white/30 text-xs">owners also receive payments</p>
            </div>
            <div>
              <p className="text-blue-400 text-xs font-mono font-medium">{formatCurrency(totalOwnershipValue)}</p>
              <p className="text-white/30 text-xs">total ownership value</p>
            </div>
            {podData.some((p) => p.hasSameYearOverlap) && (
              <span className="self-center bg-blue-500/20 border border-blue-500/30 text-blue-300 text-xs px-1.5 py-0.5 rounded-full">
                Same-year overlap confirmed
              </span>
            )}
          </div>

          {/* Physician owner rows */}
          <div className="space-y-1.5">
            {podData.slice(0, 5).map((p, i) => (
              <div key={i} className="flex items-center justify-between gap-2 py-1.5 border-t border-blue-500/10">
                <div className="flex items-center gap-2 min-w-0">
                  <Link
                    to={`/physician/${p.npi}?name=${encodeURIComponent(`${p.firstName || ""} ${p.lastName || ""}`.trim())}&state=${encodeURIComponent(p.state || "")}`}
                    className="text-blue-300/80 hover:text-blue-300 text-xs font-medium transition-colors truncate"
                  >
                    {p.firstName && p.lastName ? `${p.firstName} ${p.lastName}`.trim() : `NPI: ${p.npi}`}
                  </Link>
                  {p.specialty && (
                    <span className="text-white/30 text-xs truncate hidden sm:inline">
                      {p.specialty.split("|").pop().trim()}
                    </span>
                  )}
                  {p.hasSameYearOverlap && (
                    <span className="bg-blue-500/15 border border-blue-500/20 text-blue-400 text-xs px-1.5 py-0.5 rounded-full shrink-0">
                      Same-year
                    </span>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <p className="text-blue-400/80 text-xs font-mono">{formatCurrency(p.totalOwnershipValue || 0)}</p>
                  {p.generalPaymentsTotal > 0 && (
                    <p className="text-white/30 text-xs font-mono">{formatCurrency(p.generalPaymentsTotal)} paid</p>
                  )}
                </div>
              </div>
            ))}
            {podData.length > 5 && (
              <p className="text-blue-300/30 text-xs pt-1.5 border-t border-blue-500/10">
                Showing 5 of {podData.length} physician owners · see full equity holder list on the Ownership tab
              </p>
            )}
          </div>

          <p className="text-blue-300/25 text-xs mt-3 pt-3 border-t border-blue-500/10">
            POD flags are risk-review indicators, not legal determinations. Source: CMS Open Payments Ownership data · OIG Work Plan 2026. Consult qualified legal and compliance professionals for final determinations.
          </p>
        </div>
      )}
    </div>
  );
}

// ── Conflict Banners ──────────────────────────────────────────────────────────
function ConflictBanners({ tripleCount, generalOwnershipCount, generalResearchCount, ownershipResearchCount, totalOwners, hasOwnershipData }) {
  const [expanded, setExpanded] = useState(false);

  const hasAny =
    tripleCount > 0 ||
    generalOwnershipCount > 0 ||
    generalResearchCount > 0 ||
    ownershipResearchCount > 0;

  if (!hasAny) return null;

  const hasTriple = tripleCount > 0;

  // Build all flags for detail rendering
  const flags = [];
  if (tripleCount > 0) flags.push({
    key: "triple",
    icon: "⚠",
    color: "text-red-400",
    border: "border-red-500/25",
    bg: "bg-red-500/[0.05]",
    title: "Triple Interest Conflict",
    detail: `${tripleCount} of ${totalOwners} equity holders also received general payments and research funding from this company. This combination carries simultaneous Stark Law, Anti-Kickback Statute, and research integrity exposure.`,
  });
  if (generalOwnershipCount > 0) flags.push({
    key: "genown",
    icon: "⚑",
    color: "text-amber-400",
    border: "border-amber-500/25",
    bg: "bg-amber-500/[0.05]",
    title: "General & Ownership Conflict",
    detail: `${generalOwnershipCount} of ${totalOwners} equity holders in this company also received general payments from this company. Review individual physician profiles for Stark Law and Anti-Kickback Statute exposure.`,
  });
  if (ownershipResearchCount > 0) flags.push({
    key: "ownres",
    icon: "⚑",
    color: "text-teal-400",
    border: "border-teal-500/25",
    bg: "bg-teal-500/[0.05]",
    title: "Ownership & Research Conflict",
    detail: `${ownershipResearchCount} of ${totalOwners} equity holders in this company also received research funding from this company. Review individual physician profiles for research integrity exposure.`,
  });
  if (generalResearchCount > 0) flags.push({
    key: "genres",
    icon: "⚑",
    color: "text-blue-400",
    border: "border-blue-500/25",
    bg: "bg-blue-500/[0.05]",
    title: "General & Research Conflict",
    detail: `${generalResearchCount.toLocaleString()} provider${generalResearchCount !== 1 ? "s" : ""} associated with this company received both general payments and research funding from this company. Review individual physician profiles for full conflict detail.`,
  });

  // Collapsed: show only Triple Interest when present; show all otherwise
  const collapsedFlags = hasTriple ? flags.filter((f) => f.key === "triple") : flags;

  return (
    <div className="mb-4">
      {/* Collapsed view — always visible */}
      <div className="space-y-2">
        {collapsedFlags.map((f) => (
          <div
            key={f.key}
            className={`flex items-start gap-2 px-4 py-3 rounded-xl border ${f.border} ${f.bg}`}
          >
            <span className={`${f.color} text-sm shrink-0`}>{f.icon}</span>
            <div className="flex-1 min-w-0">
              <p className={`${f.color} font-medium text-sm`}>{f.title}</p>
              <p className="text-white/50 text-xs mt-0.5 leading-relaxed">{f.detail}</p>
            </div>
            {/* Toggle button only on the first (or only) banner */}
          </div>
        ))}
        {hasTriple && flags.length > 1 && (
          <div className="flex justify-end">
            <button
              onClick={() => setExpanded((e) => !e)}
              className="text-white/30 hover:text-white/60 text-xs transition-colors"
              title={expanded ? "Hide details" : "Show details"}
            >
              {expanded ? "▲" : "▼"}
            </button>
          </div>
        )}
      </div>

      {/* Expanded additional flags */}
      {expanded && hasTriple && (
        <div className="mt-2 space-y-2">
          {flags.filter((f) => f.key !== "triple").map((f) => (
            <div
              key={f.key}
              className={`flex items-start gap-2 px-4 py-3 rounded-xl border ${f.border} ${f.bg}`}
            >
              <span className={`${f.color} text-sm shrink-0`}>{f.icon}</span>
              <div>
                <p className={`${f.color} font-medium text-sm`}>{f.title}</p>
                <p className="text-white/50 text-xs mt-0.5 leading-relaxed">{f.detail}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function CompanyProfilePage() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { isPro, isEnterprise, setShowUpgradeModal, setUpgradeMessage } = useSubscription();
  const EXPORT_LIMIT = isEnterprise ? Infinity : 500;
  const { user, isSignedIn } = useUser();
  const { filterActive, selectedListIdsString, lists: providerLists } = useMyProviders();
  const [screenPanelOpen, setScreenPanelOpen] = useState(false);
  const filterUserId = filterActive && user?.id ? user.id : "";
  const filterListIds = filterActive && selectedListIdsString ? selectedListIdsString : "";
  const filterParams = filterUserId && filterListIds
    ? { userId: filterUserId, listIds: filterListIds }
    : {};

  // Tracked-NPI set for the Known Provider/Market Provider export column.
  // Deliberately independent of BOTH the Activate Filter toggle AND the
  // multi-list checkbox selection (selectedListIdsString) — this should
  // reflect a passive fact ("is this physician in any of my lists at
  // all"), not require the user to have actively selected anything.
  // Driven by every list the user owns (providerLists, from useMyProviders).
  const allListIdsString = providerLists.map((l) => l.id).join(",");
  const [trackedNpiSet, setTrackedNpiSet] = useState(new Set());
  useEffect(() => {
    if (!allListIdsString || !user?.id) { setTrackedNpiSet(new Set()); return; }
    apiFetch(`/api/user-data?action=getProviders&userId=${user.id}&listIds=${allListIdsString}&page=0&pageSize=50000`)
      .then((res) => res.json())
      // Normalized to strings — Supabase's user_providers.npi is text, but
      // BigQuery research/ownership tables can return npi as INT64.
      // Set.has() is strict equality, so "1851334569" !== 1851334569
      // would silently mismatch every real match without this.
      .then((data) => setTrackedNpiSet(new Set((data.providers || []).map((p) => String(p.npi)))))
      .catch(() => setTrackedNpiSet(new Set()));
  }, [allListIdsString, user?.id]);

  // ── Active tab — defaults after profile loads based on data availability ─
  const [activeTab, setActiveTab] = useState("general");

  // ── Profile data ─────────────────────────────────────────────────────────
  const [profile, setProfile] = useState(null);      // getCompanyProfile result
  const [ownership, setOwnership] = useState([]);    // getOwnershipByCompany result
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── Provider list shared state ────────────────────────────────────────────
  const [excludedNpis, setExcludedNpis] = useState(new Map());
  const [ownershipNpis, setOwnershipNpis] = useState(new Set());
  const [researchNpis, setResearchNpis] = useState(new Set());
  const [dualInterestNpis, setDualInterestNpis] = useState(new Set());
  const [tripleInterestNpis, setTripleInterestNpis] = useState(new Set());
  const [watchedNpis, setWatchedNpis] = useState(new Map());

  // ── Conflict state lifted from OwnershipTab ───────────────────────────────
  const [ownershipConflictCounts, setOwnershipConflictCounts] = useState(null);

  // ── Payment+Research dual interest — for companies with no equity data ────
  const [paymentResearchConflictNpis, setPaymentResearchConflictNpis] = useState(new Set());
  const [researchGeneralDualCount, setResearchGeneralDualCount] = useState(0);
  const [companyAliases, setCompanyAliases] = useState([]);

  // ── Payment NPI set — built from all years' provider loads ───────────────
  const [paymentNpis, setPaymentNpis] = useState(new Set());
  const [companyPaymentNpis, setCompanyPaymentNpis] = useState(new Set());
  const [companyResearchNpis, setCompanyResearchNpis] = useState(new Set());

  // ── Company watch state ───────────────────────────────────────────────────
  const [isWatchingCompany, setIsWatchingCompany] = useState(false);
  const [companyWatchLoading, setCompanyWatchLoading] = useState(false);

  // ── POD risk state ────────────────────────────────────────────────────────
  const [podData, setPodData] = useState(null);

  // ── Copied link state ─────────────────────────────────────────────────────
  const [copied, setCopied] = useState(false);

  // ── Initial data fetch ────────────────────────────────────────────────────
  useEffect(() => {
    if (!companyId) return;
    setLoading(true);
    setError(null);

    // Fetch accurate dual interest count for non-ownership companies
    getResearchGeneralDualInterestCountByCompany(companyId, filterParams)
      .then((data) => setResearchGeneralDualCount(data.dualInterestCount || 0))
      .catch(() => {});

    // Fetch related company entities from alias table
    getCompanyAliases(companyId)
      .then((data) => setCompanyAliases(data || []))
      .catch(() => {});

    // Fetch POD risk data for this company independently
    getPodRiskByCompany(companyId)
      .then((data) => setPodData(data && data.length > 0 ? data : null))
      .catch(() => setPodData(null));

    Promise.all([
      getCompanyProfile(companyId),
      getOwnershipByCompanyId(companyId, filterParams),
    ])
      .then(([profileData, ownershipData]) => {
        setProfile(profileData);
        const ownData = ownershipData || [];
        setOwnership(ownData);

        // Run dual interest batch on ownership NPIs immediately at page load
        // so Triple/Dual Interest flags are available on all tabs regardless
        // of which year's general payment list the provider appears in
        const ownershipNpiList = ownData.map((o) => o.npi).filter(Boolean);
        if (ownershipNpiList.length > 0) {
          Promise.all([
            checkOwnershipBatch(ownershipNpiList),
            checkResearchBatch(ownershipNpiList),
            checkDualInterestBatch(ownershipNpiList),
            // Company-scoped checks for accurate conflict counts
            checkPaymentsByCompanyBatch(ownershipNpiList, companyId),
            checkResearchByCompanyBatch(ownershipNpiList, companyId),
          ])
            .then(([ownNpis, resNpis, dualResults, companyPayNpis, companyResNpis]) => {
              const dualSet = new Set(dualResults.map((d) => d.npi));
              const tripleSet = new Set(dualResults.filter((d) => d.hasTripleInterest).map((d) => d.npi));
              setOwnershipNpis(new Set(ownNpis));
              setResearchNpis(new Set(resNpis));
              setDualInterestNpis(dualSet);
              setTripleInterestNpis(tripleSet);
              setCompanyPaymentNpis(new Set(companyPayNpis));
              setCompanyResearchNpis(new Set(companyResNpis));

              // Compute conflict counts using company-scoped data
              const ownershipNpiSet = new Set(ownershipNpiList);
              const companyPaySet = new Set(companyPayNpis);
              const companyResSet = new Set(companyResNpis);
              // Dual = equity holder who also has payments OR research from THIS company
              const dualCount = [...ownershipNpiSet].filter((npi) =>
                companyPaySet.has(npi) || companyResSet.has(npi)
              ).length;
              // Research = equity holder who also has research from THIS company
              const researchCount = [...ownershipNpiSet].filter((npi) =>
                companyResSet.has(npi)
              ).length;
              // Triple = equity holder who has BOTH payments AND research from THIS company
              const tripleCount = [...ownershipNpiSet].filter((npi) =>
                companyPaySet.has(npi) && companyResSet.has(npi)
              ).length;
              setOwnershipConflictCounts({
                dualCount,
                researchCount,
                tripleCount,
                total: ownData.length,
              });
            })
            .catch(() => {});
        }
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [companyId, filterUserId, filterListIds]);

  // ── Set default tab based on data availability ────────────────────────────
  useEffect(() => {
    if (!profile) return;
    if (profile.hasGeneralPayments) {
      setActiveTab("general");
    } else if (profile.hasOwnership) {
      setActiveTab("ownership");
    } else if (profile.hasResearch) {
      setActiveTab("research");
    }
  }, [profile]);

  // ── Load company watch status ─────────────────────────────────────────────
  useEffect(() => {
    if (!isSignedIn || !user || !profile) return;
    getWatchedCompanies(user.id)
      .then((data) => {
        const isWatched = data.some(
          (c) => c.company_name?.toUpperCase() === profile.companyName?.toUpperCase()
        );
        setIsWatchingCompany(isWatched);
      })
      .catch(() => {});
  }, [isSignedIn, user, profile]);

  // ── Batch flag checks once general providers are known ────────────────────
  // Called from GeneralTab after providers load
  function runBatchChecks(providers) {
    const npis = providers.map((p) => p.npi).filter(Boolean);
    if (!npis.length) return;

    Promise.all([
      checkExclusionsBatch(npis),
      checkPaymentsBatch(npis),
      checkOwnershipBatch(npis),
      checkResearchBatch(npis),
      checkDualInterestBatch(npis),
    ])
      .then(([exclusions, payNpis, ownNpis, resNpis, dualResults]) => {
        setExcludedNpis(new Map(exclusions.map((e) => [e.npi, { oig: e.oig, sam: e.sam }])));
        setPaymentNpis((prev) => {
          const next = new Set(prev);
          payNpis.forEach((npi) => next.add(npi));
          return next;
        });
        setOwnershipNpis(new Set(ownNpis));
        setResearchNpis(new Set(resNpis));
        const dualSet = new Set(dualResults.map((d) => d.npi));
        const tripleSet = new Set(dualResults.filter((d) => d.hasTripleInterest).map((d) => d.npi));
        // Merge into existing sets rather than replacing — preserves ownership-based
        // conflict NPIs that may not appear in the current year's payment provider list
        setDualInterestNpis((prev) => new Set([...prev, ...dualSet]));
        setTripleInterestNpis((prev) => new Set([...prev, ...tripleSet]));

        // Track payment+research dual interest NPIs for banner on non-ownership companies
        // Use union merge so switching years accumulates known conflicts
        const researchGeneralNpis = new Set(
          dualResults.filter((d) => d.hasResearchGeneral).map((d) => d.npi)
        );
        if (researchGeneralNpis.size > 0) {
          setPaymentResearchConflictNpis((prev) => new Set([...prev, ...researchGeneralNpis]));
        }
        // Note: ownershipConflictCounts is set at page load from ownership NPIs only
        // and must not be touched here
      })
      .catch(() => {});
  }

  // ── Company watch toggle ──────────────────────────────────────────────────
  async function handleCompanyWatchToggle() {
    if (!isSignedIn) {
      setUpgradeMessage("Sign in to watch companies and receive email alerts.");
      setShowUpgradeModal(true);
      return;
    }
    if (!isPro) {
      setUpgradeMessage("Watching companies is a Pro feature. Upgrade to get notified when new payment data is published.");
      setShowUpgradeModal(true);
      return;
    }
    if (!profile) return;
    setCompanyWatchLoading(true);
    try {
      if (isWatchingCompany) {
        await unwatchCompany(user.id, profile.companyName);
        setIsWatchingCompany(false);
      } else {
        await watchCompany(user.id, profile.companyName, companyId);
        await queueConfirmation(user.id, "company", profile.companyName, profile.companyName, { alert_payments: true });
        setIsWatchingCompany(true);
      }
    } catch (err) {
      console.error("Company watch toggle error:", err);
    } finally {
      setCompanyWatchLoading(false);
    }
  }

  // ── Derived values ────────────────────────────────────────────────────────
  const companyName = profile?.companyName || "";
  const years = profile?.years || [];
  const allTimeTotal = years.reduce((s, y) => s + (y.totalPaid || 0), 0);
  const allTimePhysicians = years.length
    ? Math.max(...years.map((y) => y.totalPhysicians || 0))
    : 0;
  const allTimePayments = years.reduce((s, y) => s + (y.paymentCount || 0), 0);
  const activeYears = years.filter((y) => y.totalPaid > 0);
  const firstYear = activeYears.length ? activeYears[0].year : null;
  const latestYear = activeYears.length ? activeYears[activeYears.length - 1].year : null;

  // ── Guards ────────────────────────────────────────────────────────────────
  if (loading) return <LoadingSkeleton />;

  if (error) {
    return (
      <main className="max-w-3xl mx-auto px-4 py-10">
        <div className="card border-red-500/20 bg-red-500/5 text-red-300 text-sm">
          ⚠ {error}
        </div>
      </main>
    );
  }

  if (!profile) {
    return (
      <main className="max-w-3xl mx-auto px-4 py-10">
        <div className="card text-center py-12">
          <p className="text-white/50 text-sm">Company not found.</p>
          <Link to="/" className="text-gold-400 text-xs mt-2 inline-block hover:text-gold-300">
            ← Back to search
          </Link>
        </div>
      </main>
    );
  }

  // ── Pro gate — entire page blocked for non-Pro ────────────────────────────
  if (!isPro) {
    return (
      <main className="max-w-3xl mx-auto px-4 py-10">
        <Link to="/" className="text-white/40 hover:text-white/70 text-sm transition-colors">
          ← Home
        </Link>
        <div className="mt-6 card text-center py-16 relative overflow-hidden">
          <div className="absolute inset-0 backdrop-blur-sm bg-navy-900/40 z-10 rounded-2xl flex flex-col items-center justify-center gap-4 p-8">
            <span className="text-4xl">🔒</span>
            <p className="text-white/80 font-display text-2xl">Company Profile</p>
            <p className="text-white/50 text-sm max-w-sm text-center">
              Full company profiles — including payment trends, ownership data, research
              payments, and provider lists — are available to Pro and Enterprise subscribers.
            </p>
            <button
              onClick={() => {
                setUpgradeMessage("Upgrade to Pro to access full company profiles with payment trends, ownership data, and provider lists.");
                setShowUpgradeModal(true);
              }}
              className="btn-primary px-6 py-2.5 mt-2"
            >
              Upgrade to Pro
            </button>
          </div>
          {/* Blurred teaser behind the gate */}
          <div className="blur-sm select-none">
            <p className="font-display text-3xl text-white mb-2">Company Name</p>
            <p className="text-white/30 text-sm">$00M total · 0,000 providers · 2016–2025</p>
          </div>
        </div>
      </main>
    );
  }

  // ── Full Pro/Enterprise page ──────────────────────────────────────────────
  return (
    <main className="max-w-3xl mx-auto px-4 py-10">
      {/* ── Print styles ──────────────────────────────────────────────────── */}
      <style>{`
        @media print {
          @page { margin: 0.75in; size: letter; }
          body {
            background: white !important;
            color: #111 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          nav, header, footer, .print-hide { display: none !important; }
          main { background: white !important; max-width: 100% !important; padding: 0 !important; }
          .card {
            background: white !important;
            border: 1px solid #e5e7eb !important;
            border-radius: 8px !important;
            box-shadow: none !important;
            break-inside: avoid;
          }
          .text-white { color: #111 !important; }
          .text-white\\/80, .text-white\\/70, .text-white\\/60, .text-white\\/50 { color: #374151 !important; }
          .text-white\\/40, .text-white\\/30, .text-white\\/25, .text-white\\/20 { color: #6b7280 !important; }
          .text-gold-400 { color: #b45309 !important; }
          .text-amber-400 { color: #b45309 !important; }
          .text-teal-400 { color: #0f766e !important; }
          .text-red-400 { color: #dc2626 !important; }
          .text-orange-400 { color: #ea580c !important; }
          .text-green-400 { color: #16a34a !important; }
          .text-blue-400 { color: #2563eb !important; }
          .text-violet-400 { color: #7c3aed !important; }
          .bg-red-500\\/10 { background: #fef2f2 !important; border-color: #fca5a5 !important; }
          .bg-amber-500\\/10 { background: #fffbeb !important; border-color: #fcd34d !important; }
          .bg-teal-500\\/10 { background: #f0fdfa !important; border-color: #5eead4 !important; }
          .bg-blue-500\\/10 { background: #eff6ff !important; border-color: #93c5fd !important; }
          .bg-orange-500\\/10 { background: #fff7ed !important; border-color: #fdba74 !important; }
          svg text { fill: #374151 !important; }

          /* Force bar colors to print */
          div[style] { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          div[style*="background-color: rgba(251,191,36"] { background-color: #d97706 !important; opacity: 1 !important; }
          div[style*="background-color: rgba(139,92,246"] { background-color: #7c3aed !important; opacity: 1 !important; }
          div[style*="background-color: rgba(34,197,94"]  { background-color: #16a34a !important; opacity: 1 !important; }
          div[style*="background-color: rgba(59,130,246"] { background-color: #2563eb !important; opacity: 1 !important; }
          div[style*="background-color: rgba(239,68,68"]  { background-color: #dc2626 !important; opacity: 1 !important; }
          div[style*="background-color: rgba(249,115,22"] { background-color: #ea580c !important; opacity: 1 !important; }
          div[style*="background-color: rgba(20,184,166"] { background-color: #0f766e !important; opacity: 1 !important; }
          div[style*="background-color: rgba(236,72,153"] { background-color: #db2777 !important; opacity: 1 !important; }
          div[style*="background-color: rgba(245,158,11"] { background-color: #d97706 !important; opacity: 1 !important; }
          div[style*="background-color: rgba(14,165,233"]  { background-color: #0369a1 !important; opacity: 1 !important; }
          div[style*="background-color: rgba(16,185,129"] { background-color: #059669 !important; opacity: 1 !important; }
          div[style*="background-color: rgba(100,116,139"] { background-color: #475569 !important; opacity: 1 !important; }
          div[style*="rgba(255,255,255,0.04"] { display: none !important; }
          div[style*="rgba(255,255,255,0.07"] { display: none !important; }

          /* Hide chart controls and export buttons */
          select { display: none !important; }
          .card button { display: none !important; }
          .card .border-t.border-white\\/5 { display: none !important; }
          .card a[href*="clinicaltrials"] { display: none !important; }
        }
      `}</style>

      {/* ── Print-only report header ───────────────────────────────────────── */}
      <div className="hidden print:block mb-6 pb-4 border-b border-gray-200">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-widest mb-1">
              ProviderPayWatch · Company Profile Report
            </p>
            <h1 className="text-2xl font-bold text-gray-900">{companyName}</h1>
            <p className="text-sm text-gray-500 mt-1">
              Company ID: {companyId}
              {firstYear && latestYear ? ` · ${firstYear === latestYear ? firstYear : `${firstYear}–${latestYear}`}` : ""}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-400">Generated {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>
            <p className="text-xs text-gray-400 mt-0.5">providerpaywatch.com</p>
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-3">
          Source: CMS Open Payments (2016–2025) · Data reported by applicable manufacturers and GPOs as required by the Physician Payments Sunshine Act · This report supports compliance research and risk review — consult qualified legal or compliance counsel for final determinations.
        </p>
      </div>

      {/* Back link */}
      <Link to="/" className="print:hidden text-white/40 hover:text-white/70 text-sm transition-colors">
        ← Home
      </Link>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="mt-4 mb-6">
        <div className="print:hidden flex items-center gap-2 mb-1">
          <span className="text-gold-400/60 text-xs uppercase tracking-widest">
            Company Profile
          </span>
        </div>
        <h1 className="print:hidden font-display text-4xl text-white leading-tight">
          {companyName}
        </h1>

        {/* Meta row */}
        <div className="print:hidden flex items-center gap-3 mt-2 flex-wrap">
          {firstYear && latestYear && (
            <p className="text-white/30 text-xs">
              {firstYear === latestYear ? firstYear : `${firstYear}–${latestYear}`}
            </p>
          )}
          <p className="text-white/20 text-xs font-mono">ID: {companyId}</p>

          {/* Copy link */}
          <button
            onClick={() => {
              navigator.clipboard.writeText(window.location.href);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }}
            className={`flex items-center gap-1.5 text-xs px-3 py-1 rounded-lg border transition-all ${
              copied
                ? "text-green-400 border-green-500/30 bg-green-500/10"
                : "text-white/60 border-white/20 hover:text-white hover:border-white/40 bg-white/5"
            }`}
          >
            {copied
              ? <><span>✓</span><span>Link copied!</span></>
              : <><span>⎘</span><span>Share</span></>}
          </button>

          {/* Compare company */}
          <CompanyCompareButton companyId={companyId} companyName={companyName} />

          {/* Watch company */}
          <button
            onClick={handleCompanyWatchToggle}
            disabled={companyWatchLoading}
            className={`flex items-center gap-1.5 text-xs px-3 py-1 rounded-lg border transition-all ${
              isWatchingCompany
                ? "bg-gold-500/20 border-gold-500/40 text-gold-300"
                : "border-white/20 text-white/40 hover:border-gold-500/40 hover:text-gold-300"
            }`}
          >
            {companyWatchLoading
              ? "..."
              : isWatchingCompany
              ? "🔔 Watching"
              : "🔕 Watch"}
          </button>

          {/* Screen this vendor — Enterprise-only. Works for any company,
              confirmed vendor or not, once unlocked. */}
          {isSignedIn && (
            isEnterprise ? (
              <button
                onClick={() => setScreenPanelOpen(!screenPanelOpen)}
                className="flex items-center gap-1.5 text-xs px-3 py-1 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-all"
              >
                🛡 Screen
              </button>
            ) : (
              <button
                onClick={() => {
                  setUpgradeMessage("Vendor screening is an Enterprise feature. Upgrade to screen any company for conflicts against your tracked providers.");
                  setShowUpgradeModal(true);
                }}
                className="flex items-center gap-1.5 text-xs px-3 py-1 rounded-lg border border-red-500/30 bg-red-500/5 text-red-400/70 hover:bg-red-500/10 hover:text-red-400 transition-all"
              >
                🔒 Screen <span className="text-[9px] px-1 py-0.5 rounded-full bg-red-500/20 border border-red-500/30 text-red-300 uppercase tracking-wide">Enterprise</span>
              </button>
            )
          )}

          {/* Print / Save PDF */}
          {screenPanelOpen && isEnterprise && (
            <VendorScreenPanel
              companyId={companyId}
              companyName={companyName}
              providerLists={providerLists}
              userId={user?.id}
              fetchFn={apiFetch}
              onCancel={() => setScreenPanelOpen(false)}
            />
          )}
          <button
            onClick={() => window.print()}
            className="flex items-center gap-1.5 text-xs px-3 py-1 rounded-lg border border-white/20 text-white/60 hover:text-white hover:border-white/40 bg-white/5 transition-all"
          >
            <span>⎙</span><span>Print / Save PDF</span>
          </button>
        </div>

        </div>

      {/* ── Related entities banner ─────────────────────────────────────────── */}
      {companyAliases.length > 0 && (
        <div className="print:hidden flex items-start gap-2 px-4 py-3 rounded-xl border border-white/10 bg-white/[0.03] mb-4">
          <span className="text-white/40 text-sm shrink-0">⚠</span>
          <div>
            <p className="text-white/60 font-medium text-sm">Related entities in CMS Open Payments</p>
            <p className="text-white/35 text-xs mt-0.5 leading-relaxed">
              CMS data for this company may also be reported under related legal entities.
              Data on this page reflects only what was reported under "{companyName}".
            </p>
            <div className="flex flex-wrap gap-2 mt-2">
              {companyAliases.map((alias) => (
                <Link
                  key={alias.companyId}
                  to={`/company/${alias.companyId}`}
                  className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg border border-white/10 text-white/50 hover:text-gold-300 hover:border-gold-500/30 transition-all"
                >
                  {alias.companyName} →
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Tab navigation ──────────────────────────────────────────────────── */}
      <div className="print:hidden flex gap-1 bg-navy-800 border border-white/10 rounded-xl p-1 mb-6">
        {[
          { id: "general",   label: "General Payments", icon: "💊", available: profile.hasGeneralPayments },
          { id: "ownership", label: "Ownership",         icon: "🏦", available: profile.hasOwnership },
          { id: "research",  label: "Research",          icon: "🔬", available: profile.hasResearch },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-xs font-medium transition-all ${
              activeTab === tab.id
                ? "bg-navy-700 text-white border border-white/10"
                : tab.available
                ? "text-white/40 hover:text-white/60"
                : "text-white/20 cursor-default"
            }`}
          >
            <span>{tab.icon}</span>
            <span>{tab.label}</span>
            {!tab.available && (
              <span className="text-white/20" style={{ fontSize: "9px" }}>—</span>
            )}
          </button>
        ))}
      </div>

      {/* ── Tab content ─────────────────────────────────────────────────────── */}
      {activeTab === "general" && (
        <GeneralTab
          profile={profile}
          companyId={companyId}
          companyName={companyName}
          allTimeTotal={allTimeTotal}
          allTimePhysicians={allTimePhysicians}
          allTimePayments={allTimePayments}
          excludedNpis={excludedNpis}
          paymentNpis={paymentNpis}
          ownershipNpis={ownershipNpis}
          researchNpis={researchNpis}
          dualInterestNpis={companyPaymentNpis}
          tripleInterestNpis={new Set([...companyPaymentNpis].filter(npi => companyResearchNpis.has(npi)))}
          watchedNpis={watchedNpis}
          setWatchedNpis={setWatchedNpis}
          onProvidersLoaded={runBatchChecks}
          isPro={isPro}
          isEnterprise={isEnterprise}
          isSignedIn={isSignedIn}
          user={user}
          setUpgradeMessage={setUpgradeMessage}
          setShowUpgradeModal={setShowUpgradeModal}
          conflictCounts={ownershipConflictCounts}
          researchGeneralDualCount={researchGeneralDualCount}
          filterParams={filterParams}
          filterActive={filterActive}
          filterUserId={filterUserId}
          filterListIds={filterListIds}
          podData={podData}
          trackedNpiSet={trackedNpiSet}
        />
      )}

      {activeTab === "ownership" && (
        <OwnershipTab
          ownership={ownership}
          companyId={companyId}
          companyName={companyName}
          excludedNpis={excludedNpis}
          paymentNpis={paymentNpis}
          ownershipNpis={ownershipNpis}
          researchNpis={researchNpis}
          dualInterestNpis={dualInterestNpis}
          watchedNpis={watchedNpis}
          setWatchedNpis={setWatchedNpis}
          isPro={isPro}
          isEnterprise={isEnterprise}
          setUpgradeMessage={setUpgradeMessage}
          setShowUpgradeModal={setShowUpgradeModal}
          onConflictCounts={setOwnershipConflictCounts}
          setPaymentNpis={setPaymentNpis}
          conflictCounts={ownershipConflictCounts}
          filterActive={filterActive}
          researchGeneralDualCount={researchGeneralDualCount}
          podData={podData}
          trackedNpiSet={trackedNpiSet}
        />
      )}

      {activeTab === "research" && (
        <ResearchTab
          companyId={companyId}
          companyName={companyName}
          excludedNpis={excludedNpis}
          paymentNpis={paymentNpis}
          ownershipNpis={ownershipNpis}
          researchNpis={researchNpis}
          dualInterestNpis={dualInterestNpis}
          tripleInterestNpis={tripleInterestNpis}
          watchedNpis={watchedNpis}
          setWatchedNpis={setWatchedNpis}
          isPro={isPro}
          isEnterprise={isEnterprise}
          setUpgradeMessage={setUpgradeMessage}
          setShowUpgradeModal={setShowUpgradeModal}
          setPaymentNpis={setPaymentNpis}
          conflictCounts={ownershipConflictCounts}
          researchGeneralDualCount={researchGeneralDualCount}
          filterParams={filterParams}
          filterActive={filterActive}
          filterUserId={filterUserId}
          filterListIds={filterListIds}
          trackedNpiSet={trackedNpiSet}
        />
      )}

      {/* Source footer */}
      <p className="print:hidden text-white/20 text-xs text-center mt-8">
        Source: CMS Open Payments · Data reported by applicable manufacturers and GPOs
        as required by the Physician Payments Sunshine Act · 2016–2025
      </p>
      <p className="hidden print:block text-gray-400 text-xs text-center mt-8">
        Source: CMS Open Payments (2016–2025) · providerpaywatch.com · Generated {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
      </p>
    </main>
  );
}

// ── Payment type label shortener ─────────────────────────────────────────────
function shortPaymentType(raw) {
  const map = {
    "Food and Beverage": "Food & Beverage",
    "Consulting Fee": "Consulting",
    "Travel and Lodging": "Travel & Lodging",
    "Education": "Education",
    "Royalty or License": "Royalties",
    "Gift": "Gift",
    "Compensation for services other than consulting, including serving as faculty or as a speaker at a venue other than a continuing education program": "Speaking Fees",
    "Compensation for serving as faculty or as a speaker at an unaccredited and noncertified continuing education program": "Faculty/Speaking (Non-CME)",
    "Compensation for serving as faculty or as a speaker at a non-accredited and noncertified continuing education program": "Faculty/Speaking (Non-CME)",
    "Compensation for serving as faculty or as a speaker for a non-accredited and noncertified continuing education program": "Faculty/Speaking (Non-CME)",
    "Compensation for serving as faculty or as a speaker for a medical education program": "Faculty/Speaking (CME)",
    "Compensation for serving as faculty or as a speaker at an accredited or certified continuing education program": "Faculty/Speaking (CME)",
    "Compensation for serving as faculty or as a speaker for an accredited or certified continuing education program": "Faculty/Speaking (CME)",
    "Space rental or facility fees (teaching hospital only)": "Space Rental",
    "Acquisitions": "Acquisition",
    "Debt forgiveness": "Debt Forgiveness",
    "Long term medical supply or device loan": "Device Loan",
    "Acquisition": "Acquisition",
    "Honoraria": "Honoraria",
    "Research": "Research",
    "Current or prospective ownership or investment interest": "Ownership Interest",
    "Grant": "Grant",
    "Entertainment": "Entertainment",
    "Charitable Contribution": "Charity",
  };
  return map[raw] || raw;
}

// ── Specialty label shortener ─────────────────────────────────────────────────
function shortSpecialty(raw) {
  if (!raw) return "Other";
  // Take the last pipe-delimited segment, clean it up
  const parts = raw.split("|");
  const last = parts[parts.length - 1].trim();
  // Shorten common long names
  const map = {
    "Orthopaedic Surgery of the Spine": "Spine Surgery",
    "Orthopaedic Surgery": "Orthopaedics",
    "Neurological Surgery": "Neurosurgery",
    "Pain Medicine": "Pain Medicine",
    "Interventional Pain Medicine": "Interventional Pain",
    "Physical Medicine & Rehabilitation": "PM&R",
    "Anesthesiology": "Anesthesiology",
    "Emergency Medicine": "Emergency Medicine",
    "Diagnostic Radiology": "Radiology",
    "Family Medicine": "Family Medicine",
    "Internal Medicine": "Internal Medicine",
    "Physician Assistant": "Physician Assistant",
    "Surgical": "PA – Surgical",
    "Medical": "PA – Medical",
    "Nurse Practitioner": "Nurse Practitioner",
    "Family": "NP – Family",
    "Acute Care": "NP – Acute Care",
    "Orthopaedic Trauma": "Ortho Trauma",
    "Sports Medicine": "Sports Medicine",
    "Hand Surgery": "Hand Surgery",
    "Foot and Ankle Surgery": "Foot & Ankle",
    "Chiropractor": "Chiropractic",
    "Podiatrist": "Podiatry",
    "Specialist": "Specialist",
    "Neurology": "Neurology",
    "General Practice": "General Practice",
    "Neuroradiology": "Neuroradiology",
    "Clinical Neurophysiology": "Clinical Neurophysiology",
    "Obstetrics & Gynecology": "OB/GYN",
    "Surgery": "Surgery",
  };
  return map[last] || last;
}

// ── Color palette for payment types ──────────────────────────────────────────
const PAYMENT_TYPE_COLORS = {
  "Consulting":         { bar: "rgba(251,191,36,0.85)",  legend: "bg-gold-400/80"     },
  "Food & Beverage":    { bar: "rgba(34,197,94,0.75)",   legend: "bg-green-500/80"    },
  "Travel & Lodging":   { bar: "rgba(59,130,246,0.75)",  legend: "bg-blue-500/80"     },
  "Royalties":          { bar: "rgba(139,92,246,0.85)",  legend: "bg-violet-500/80"   },
  "Speaking Fees":      { bar: "rgba(249,115,22,0.80)",  legend: "bg-orange-500/80"   },
  "Education":          { bar: "rgba(20,184,166,0.75)",  legend: "bg-teal-500/80"     },
  "Gift":               { bar: "rgba(236,72,153,0.75)",  legend: "bg-pink-500/80"     },
  "Research":           { bar: "rgba(14,165,233,0.75)",  legend: "bg-sky-500/80"      },
  "Honoraria":          { bar: "rgba(245,158,11,0.75)",  legend: "bg-amber-500/80"    },
  "Grant":              { bar: "rgba(16,185,129,0.80)",  legend: "bg-emerald-500/80"  },
  "Acquisition":        { bar: "rgba(239,68,68,0.75)",   legend: "bg-red-500/80"      },
  "Charity":            { bar: "rgba(168,85,247,0.75)",  legend: "bg-purple-500/80"   },
  "Debt Forgiveness":   { bar: "rgba(251,146,60,0.75)",  legend: "bg-orange-400/80"   },
  "Device Loan":        { bar: "rgba(6,182,212,0.75)",   legend: "bg-cyan-500/80"     },
  "Space Rental":       { bar: "rgba(132,204,22,0.75)",  legend: "bg-lime-500/80"     },
  "Ownership Interest": { bar: "rgba(251,191,36,0.50)",  legend: "bg-gold-400/50"     },
  "Entertainment":      { bar: "rgba(244,63,94,0.75)",   legend: "bg-rose-500/80"     },
  "Faculty/Speaking (CME)":     { bar: "rgba(99,102,241,0.80)", legend: "bg-indigo-500/80" },
  "Faculty/Speaking (Non-CME)": { bar: "rgba(147,51,234,0.75)", legend: "bg-purple-600/80" },
  "Other":              { bar: "rgba(100,116,139,0.70)", legend: "bg-slate-500/80"    },
};

function getPaymentColor(label) {
  return PAYMENT_TYPE_COLORS[label] || PAYMENT_TYPE_COLORS["Other"];
}

// ── Payment Trend Chart (stacked bar by type, all years) ─────────────────────
function PaymentTrendChart({ years, filteredYearlyTotals, filteredPaymentTypes, companyName, companyId }) {
  const [hoveredYear, setHoveredYear] = useState(null);

  // Collect all payment types across all years
  const yearParsed = years.map((y) => {
    let types = [];
    // Use filtered payment types when available, otherwise fall back to pre-aggregated
    const filteredTypes = filteredPaymentTypes?.[y.year];
    if (filteredTypes && filteredTypes.length > 0) {
      types = filteredTypes;
    } else {
      try {
        types = Array.isArray(y.paymentTypes)
          ? y.paymentTypes
          : JSON.parse(y.paymentTypes || "[]");
      } catch {}
    }
    // Override total with filtered value when filter is active
    const filteredYear = filteredYearlyTotals?.find((f) => f.year === y.year);
    return { year: y.year, total: filteredYear ? filteredYear.totalPaid : y.totalPaid, types };
  });

  // Compute total per type across all years to determine top 8
  const typeTotals = {};
  yearParsed.forEach(({ types }) => {
    types.forEach((t) => {
      const label = shortPaymentType(t.payment_type || t.paymentType || "");
      if (label) typeTotals[label] = (typeTotals[label] || 0) + (t.amount || 0);
    });
  });

  const sortedTypes = Object.entries(typeTotals)
    .sort((a, b) => b[1] - a[1])
    .map(([label]) => label);

  const top7 = sortedTypes.slice(0, 7);
  const restTypes = new Set(sortedTypes.slice(7));
  // typeList for display: top 7 + "Other" if there are remaining types
  const typeList = restTypes.size > 0 ? [...top7, "Other"] : top7;

  const maxTotal = Math.max(...yearParsed.map((y) => y.total), 1);

  return (
    <div className="card mb-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white/60 text-xs uppercase tracking-widest">Payment trend · all years</h2>
        {hoveredYear && (() => {
          const yd = yearParsed.find((y) => y.year === hoveredYear);
          return yd ? (
            <div className="text-right">
              <p className="text-white/40 text-xs font-medium">{hoveredYear}</p>
              <p className="text-gold-400 text-xs font-mono">{formatCurrency(yd.total)}</p>
            </div>
          ) : null;
        })()}
      </div>

      <div className="relative" style={{ height: "180px" }}>
        {/* Y-axis */}
        <div className="absolute left-0 top-0 bottom-6 flex flex-col justify-between items-end pr-1 pointer-events-none" style={{ width: "44px" }}>
          {[1, 0.75, 0.5, 0.25, 0].map((pct) => {
            const val = maxTotal * pct;
            return (
              <span key={pct} className="text-white/20" style={{ fontSize: "8px" }}>
                {val >= 1000000 ? `$${(val / 1000000).toFixed(1)}M`
                  : val >= 1000 ? `$${(val / 1000).toFixed(0)}K`
                  : val > 0 ? `$${val.toFixed(0)}` : "$0"}
              </span>
            );
          })}
        </div>

        {/* Bars */}
        <div className="absolute inset-0 flex items-end gap-1" style={{ left: "46px", paddingBottom: "24px" }}>
          {yearParsed.map(({ year, total, types }) => {
            const heightPct = (total / maxTotal) * 100;
            const isHovered = hoveredYear === year;
            const typeMap = {};
            types.forEach((t) => {
              const label = shortPaymentType(t.payment_type || t.paymentType || "");
              if (!label) return;
              if (restTypes.has(label)) {
                typeMap["Other"] = (typeMap["Other"] || 0) + (t.amount || 0);
              } else {
                typeMap[label] = (t.amount || 0);
              }
            });

            return (
              <div
                key={year}
                className="flex-1 flex flex-col justify-end h-full relative group"
                onMouseEnter={() => setHoveredYear(year)}
                onMouseLeave={() => setHoveredYear(null)}
                style={{ cursor: "pointer" }}
              >
                {/* Tooltip */}
                {isHovered && (
                  <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-navy-900 border border-white/10 rounded-lg px-3 py-2 z-20 pointer-events-none" style={{ minWidth: "150px" }}>
                    <p className="text-white/50 text-xs font-medium mb-1.5 pb-1.5 border-b border-white/10">{year} · {formatCurrency(total)}</p>
                    {Object.entries(typeMap).sort((a, b) => b[1] - a[1]).map(([label, amount]) => (
                      <div key={label} className="flex items-center justify-between gap-2 mt-1">
                        <div className="flex items-center gap-1.5">
                          <div className="w-2 h-2 rounded-sm shrink-0" style={{ backgroundColor: getPaymentColor(label).bar }} />
                          <span className="text-white/40 text-xs">{label}</span>
                        </div>
                        <span className="text-white/60 text-xs">{formatCurrency(amount)}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Total label */}
                {total > 0 && (
                  <div className="absolute w-full text-center pointer-events-none" style={{ bottom: `${Math.max(heightPct, 2)}%`, paddingBottom: "2px" }}>
                    <span className="text-gold-400/70 font-medium" style={{ fontSize: "8px" }}>
                      {total >= 1000000 ? `$${(total / 1000000).toFixed(1)}M`
                        : total >= 1000 ? `$${(total / 1000).toFixed(0)}K`
                        : `$${total.toFixed(0)}`}
                    </span>
                  </div>
                )}

                {/* Stacked bar */}
                <div
                  className="w-full rounded-t overflow-hidden flex flex-col-reverse transition-opacity duration-150"
                  style={{
                    height: total > 0 ? `${Math.max(heightPct, 2)}%` : "2px",
                    opacity: hoveredYear && !isHovered ? 0.4 : 1,
                  }}
                >
                  {typeList.map((label) => {
                    const amount = typeMap[label] || 0;
                    if (!amount) return null;
                    const pct = (amount / total) * 100;
                    return (
                      <div
                        key={label}
                        style={{ height: `${pct}%`, backgroundColor: getPaymentColor(label).bar, minHeight: "2px" }}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Year labels */}
        <div className="absolute bottom-0 flex gap-1" style={{ left: "46px", right: "0" }}>
          {yearParsed.map(({ year }) => (
            <div key={year} className="flex-1 text-center">
              <span className={`transition-colors ${hoveredYear === year ? "text-white/60" : "text-white/25"}`} style={{ fontSize: "10px" }}>
                {year}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Legend + export */}
      <div className="flex items-end justify-between gap-4 mt-3 pt-3 border-t border-white/5">
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {typeList.map((label) => (
            <div key={label} className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: getPaymentColor(label).bar }} />
              <span className="text-white/40 text-xs">{label}</span>
            </div>
          ))}
        </div>
        <button
          onClick={() => exportPaymentTrendCsv(yearParsed, typeList, companyName, companyId)}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white/70 hover:border-white/20 transition-all shrink-0"
        >
          ⬇ Export
        </button>
      </div>
    </div>
  );
}

function exportPaymentTrendCsv(yearParsed, typeList, companyName, companyId) {
  // Export uses all individual types, not the bucketed display list
  const allExportTypes = new Set();
  yearParsed.forEach(({ types }) => {
    types.forEach((t) => {
      const label = shortPaymentType(t.payment_type || t.paymentType || "");
      if (label) allExportTypes.add(label);
    });
  });
  const exportTypeList = [...allExportTypes].sort();
  const rows = [["Company", "Company ID", "Year", "Total ($)", ...exportTypeList]];
  for (const { year, total, types } of yearParsed) {
    const typeMap = {};
    types.forEach((t) => {
      const label = shortPaymentType(t.payment_type || t.paymentType || "");
      if (label) typeMap[label] = t.amount || 0;
    });
    rows.push([companyName, `'${companyId}`, year, total.toFixed(2), ...exportTypeList.map((l) => (typeMap[l] || 0).toFixed(2))]);
  }
  downloadCsv(rows, `${companyName.replace(/\s+/g, "_")}_payment_trend_by_type.csv`);
}

// ── Top Products Chart (horizontal bars, selected year) ───────────────────────
function TopProductsChart({ years, selectedYear, filteredProducts, companyName, companyId }) {
  const [showAll, setShowAll] = useState(false);

  const yearData = years.find((y) => y.year === selectedYear);

  let products = [];
  if (filteredProducts !== null && filteredProducts !== undefined) {
    // filteredProducts from BigQuery: { product_name, product_type, cnt, amount }
    products = filteredProducts.filter((p) => p.product_name && p.product_name.trim() !== "");
  } else if (yearData) {
    try {
      products = Array.isArray(yearData.productNameBreakdown)
        ? yearData.productNameBreakdown
        : JSON.parse(yearData.productNameBreakdown || "[]");
    } catch {}
    products = products.filter((p) => p.product_name && p.product_name.trim() !== "");
  }

  if (!products.length) return null;

  const sorted = [...products].sort((a, b) => b.amount - a.amount);
  const visible = showAll ? sorted : sorted.slice(0, 10);
  const maxAmount = sorted[0]?.amount || 1;
  const totalAmount = sorted.reduce((s, p) => s + p.amount, 0);

  const PRODUCT_TYPE_COLORS = {
    "Drug":           { bar: "bg-purple-500/70", badge: "bg-purple-500/20 border-purple-500/30 text-purple-300" },
    "Biological":     { bar: "bg-teal-500/70",   badge: "bg-teal-500/20 border-teal-500/30 text-teal-300" },
    "Device":         { bar: "bg-blue-500/70",   badge: "bg-blue-500/20 border-blue-500/30 text-blue-300" },
    "Medical Supply": { bar: "bg-green-500/70",  badge: "bg-green-500/20 border-green-500/30 text-green-300" },
  };

  function getProductColors(type) {
    return PRODUCT_TYPE_COLORS[type] || { bar: "bg-white/20", badge: "bg-white/10 border-white/20 text-white/40" };
  }

  return (
    <div className="card mb-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white/60 text-xs uppercase tracking-widest">
          Top products · {selectedYear}
        </h2>
        <span className="text-white/25 text-xs">
          {sorted.length} product{sorted.length !== 1 ? "s" : ""}
        </span>
      </div>

      <div className="space-y-3">
        {visible.map((p, i) => {
          const pct = (p.amount / maxAmount) * 100;
          const sharePct = (p.amount / totalAmount) * 100;
          const colors = getProductColors(p.product_type);
          return (
            <div key={i} className="group/product">
              <div className="flex items-center justify-between mb-1 gap-2">
                <div className="relative flex items-center gap-2 min-w-0 flex-1">
                  <span className="text-white/70 text-xs truncate">{p.product_name}</span>
                  {/* Full name tooltip */}
                  <div className="absolute bottom-full left-0 mb-1.5 hidden group-hover/product:block z-20 pointer-events-none">
                    <div className="bg-navy-900 border border-white/10 rounded-lg px-3 py-1.5 shadow-xl">
                      <p className="text-white/80 text-xs whitespace-nowrap">{p.product_name}</p>
                      <div className="flex items-center gap-3 mt-1">
                        {p.product_type && (
                          <p className="text-white/35 text-xs">{p.product_type}</p>
                        )}
                        <p className="text-white/35 text-xs">{(p.cnt || p.count || 0).toLocaleString()} payments</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {p.product_type && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full border shrink-0 ${colors.badge}`}
                      style={{ fontSize: "10px" }}>
                      {p.product_type}
                    </span>
                  )}
                  <span className="text-white/25 text-xs">{sharePct.toFixed(1)}%</span>
                  <span className="text-gold-400/80 text-xs font-mono w-20 text-right">
                    {p.amount >= 1000000
                      ? `$${(p.amount / 1000000).toFixed(2)}M`
                      : formatCurrency(p.amount)}
                  </span>
                </div>
              </div>
              <div className="w-full bg-white/5 rounded-full h-1.5">
                <div className={`h-1.5 rounded-full ${colors.bar}`} style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>

      {sorted.length > 10 && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="mt-4 flex items-center gap-1.5 text-xs text-white/40 hover:text-white/70 transition-colors"
        >
          <span>{showAll ? "▲" : "▼"}</span>
          <span>{showAll ? "Show top 10 only" : `Show all ${sorted.length} products`}</span>
        </button>
      )}

      <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
        <p className="text-white/20 text-xs">
          Excludes payments where no product was reported · sorted by amount
        </p>
        <button
          onClick={() => exportTopProductsCsv(sorted, selectedYear, companyName, companyId)}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white/70 hover:border-white/20 transition-all shrink-0"
        >
          ⬇ Export
        </button>
      </div>
    </div>
  );
}

function exportTopProductsCsv(products, year, companyName, companyId) {
  const rows = [["Company", "Company ID", "Year", "Product Name", "Product Type", "Payment Count", "Amount ($)", "% of Total"]];
  const total = products.reduce((s, p) => s + p.amount, 0);
  for (const p of products) {
    rows.push([
      companyName,
      `'${companyId}`,
      year,
      p.product_name,
      p.product_type || "",
      p.cnt || 0,
      p.amount.toFixed(2),
      ((p.amount / total) * 100).toFixed(1) + "%",
    ]);
  }
  downloadCsv(rows, `${companyName.replace(/\s+/g, "_")}_top_products_${year}.csv`);
}

// ── Specialty Breakdown (horizontal bars, selected year) ─────────────────────
function SpecialtyBreakdown({ years, selectedYear, filteredSpecialties, companyName, companyId }) {
  const yearData = years.find((y) => y.year === selectedYear);

  let all = [];
  if (filteredSpecialties !== null && filteredSpecialties !== undefined) {
    // filteredSpecialties from BigQuery: { specialty, physician_count, amount }
    all = filteredSpecialties
      .filter((s) => s.specialty && s.specialty.trim())
      .map((s) => ({ label: shortSpecialty(s.specialty), amount: s.amount, count: s.physician_count }))
      .sort((a, b) => b.amount - a.amount);
  } else if (yearData) {
    let specialties = [];
    try {
      specialties = Array.isArray(yearData.specialtyBreakdown)
        ? yearData.specialtyBreakdown
        : JSON.parse(yearData.specialtyBreakdown || "[]");
    } catch {}
    all = specialties
      .filter((s) => s.specialty && s.specialty.trim())
      .map((s) => ({ label: shortSpecialty(s.specialty), amount: s.amount, count: s.physician_count }))
      .sort((a, b) => b.amount - a.amount);
  }

  if (!all.length) return null;
  const top = all.slice(0, 10);
  if (!top.length) return null;
  const max = top[0].amount;

  return (
    <div className="card mb-4">
      <h2 className="text-white/60 text-xs uppercase tracking-widest mb-4">Specialty breakdown · {selectedYear}</h2>
      <div className="space-y-2.5">
        {top.map((s, i) => {
          const pct = (s.amount / max) * 100;
          return (
            <div key={i}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-white/60 text-xs truncate max-w-52">{s.label}</span>
                <div className="flex items-center gap-3 shrink-0 ml-3">
                  <span className="text-white/30 text-xs">{s.count.toLocaleString()} providers</span>
                  <span className="text-gold-400/80 text-xs font-mono w-20 text-right">{formatCurrency(s.amount)}</span>
                </div>
              </div>
              <div className="w-full bg-white/5 rounded-full h-1.5">
                <div
                  className="h-1.5 rounded-full bg-gold-400/60 transition-all duration-300"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between mt-3">
        <p className="text-white/20 text-xs">Top 10 shown · export includes all {all.length} specialties</p>
        <button
          onClick={() => exportSpecialtyCsv(all, selectedYear, companyName, companyId)}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white/70 hover:border-white/20 transition-all shrink-0"
        >
          ⬇ Export
        </button>
      </div>
    </div>
  );
}

function exportSpecialtyCsv(specialties, year, companyName, companyId) {
  const total = specialties.reduce((s, sp) => s + sp.amount, 0);
  const rows = [["Company", "Company ID", "Year", "Specialty", "Provider Count", "Amount ($)", "% of Total"]];
  for (const s of specialties) {
    const pct = total > 0 ? ((s.amount / total) * 100).toFixed(1) + "%" : "0.0%";
    rows.push([companyName, `'${companyId}`, year, s.label, s.count, s.amount.toFixed(2), pct]);
  }
  downloadCsv(rows, `${companyName.replace(/\s+/g, "_")}_specialty_breakdown_${year}.csv`);
}

// ── React Simple Maps ────────────────────────────────────────────────────────
const GEO_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

const STATE_CENTROIDS = {
  AL:{lon:-86.8,lat:32.8},AK:{lon:-153.4,lat:61.4},AZ:{lon:-111.7,lat:34.3},
  AR:{lon:-92.4,lat:34.9},CA:{lon:-119.7,lat:37.2},CO:{lon:-105.5,lat:39.0},
  CT:{lon:-72.7,lat:41.6},DE:{lon:-75.5,lat:39.0},FL:{lon:-81.5,lat:28.5},
  GA:{lon:-83.4,lat:32.7},HI:{lon:-157.8,lat:20.3},ID:{lon:-114.7,lat:44.4},
  IL:{lon:-89.2,lat:40.0},IN:{lon:-86.3,lat:39.9},IA:{lon:-93.5,lat:42.1},
  KS:{lon:-98.4,lat:38.5},KY:{lon:-84.9,lat:37.5},LA:{lon:-91.8,lat:31.2},
  ME:{lon:-69.2,lat:45.4},MD:{lon:-76.6,lat:39.1},MA:{lon:-71.5,lat:42.3},
  MI:{lon:-85.4,lat:44.3},MN:{lon:-94.3,lat:46.4},MS:{lon:-89.7,lat:32.7},
  MO:{lon:-92.3,lat:38.4},MT:{lon:-110.4,lat:47.0},NE:{lon:-99.9,lat:41.5},
  NV:{lon:-116.4,lat:39.3},NH:{lon:-71.6,lat:43.7},NJ:{lon:-74.5,lat:40.1},
  NM:{lon:-106.1,lat:34.5},NY:{lon:-75.5,lat:43.0},NC:{lon:-79.4,lat:35.6},
  ND:{lon:-100.5,lat:47.5},OH:{lon:-82.8,lat:40.4},OK:{lon:-97.5,lat:35.5},
  OR:{lon:-120.5,lat:44.1},PA:{lon:-77.2,lat:40.9},RI:{lon:-71.5,lat:41.7},
  SC:{lon:-80.9,lat:33.8},SD:{lon:-100.2,lat:44.4},TN:{lon:-86.3,lat:35.9},
  TX:{lon:-99.3,lat:31.5},UT:{lon:-111.1,lat:39.4},VT:{lon:-72.7,lat:44.0},
  VA:{lon:-78.7,lat:37.5},WA:{lon:-120.5,lat:47.4},WV:{lon:-80.6,lat:38.6},
  WI:{lon:-89.6,lat:44.6},WY:{lon:-107.6,lat:43.0},DC:{lon:-77.0,lat:38.9},
};

// Small states that need offset labels to avoid overlap
const LABEL_OFFSETS = {
  CT:{dx:15,dy:-8},DE:{dx:18,dy:5},DC:{dx:18,dy:5},MA:{dx:18,dy:-8},
  MD:{dx:18,dy:8},NH:{dx:15,dy:-8},NJ:{dx:18,dy:5},RI:{dx:18,dy:5},
  VT:{dx:15,dy:-8},
};

const FIPS_TO_ABBR = {
  "01":"AL","02":"AK","04":"AZ","05":"AR","06":"CA","08":"CO","09":"CT",
  "10":"DE","11":"DC","12":"FL","13":"GA","15":"HI","16":"ID","17":"IL",
  "18":"IN","19":"IA","20":"KS","21":"KY","22":"LA","23":"ME","24":"MD",
  "25":"MA","26":"MI","27":"MN","28":"MS","29":"MO","30":"MT","31":"NE",
  "32":"NV","33":"NH","34":"NJ","35":"NM","36":"NY","37":"NC","38":"ND",
  "39":"OH","40":"OK","41":"OR","42":"PA","44":"RI","45":"SC","46":"SD",
  "47":"TN","48":"TX","49":"UT","50":"VT","51":"VA","53":"WA","54":"WV",
  "55":"WI","56":"WY",
};

function StateGrid({ years, selectedYear, filteredStates, companyName, companyId }) {
  const [hoveredState, setHoveredState] = useState(null);

  const yearData = years.find((y) => y.year === selectedYear);

  let states = [];
  if (filteredStates !== null && filteredStates !== undefined) {
    // filteredStates from BigQuery: { state, physician_count, payment_count, total_paid }
    states = filteredStates.filter((s) => s.state && s.state.trim());
  } else if (yearData) {
    try {
      states = Array.isArray(yearData.stateBreakdown)
        ? yearData.stateBreakdown
        : JSON.parse(yearData.stateBreakdown || "[]");
    } catch {}
  }
  if (!states.length) return null;

  const stateMap = {};
  states.forEach((s) => { if (s.state) stateMap[s.state] = s; });

  const maxPaid = Math.max(...states.map((s) => s.total_paid || 0), 1);

  function getFill(abbr) {
    const s = stateMap[abbr];
    if (!s || !s.total_paid) return "rgba(255,255,255,0.05)";
    const i = s.total_paid / maxPaid;
    if (i > 0.75) return "rgba(251,191,36,0.92)";
    if (i > 0.50) return "rgba(251,191,36,0.68)";
    if (i > 0.25) return "rgba(251,191,36,0.40)";
    if (i > 0.08) return "rgba(251,191,36,0.20)";
    return "rgba(251,191,36,0.08)";
  }

  const hovered = hoveredState ? stateMap[hoveredState] : null;
  const topStates = [...states]
    .filter((s) => s.state && s.total_paid > 0)
    .sort((a, b) => b.total_paid - a.total_paid)
    .slice(0, 5);

  return (
    <div className="card mb-4">
      {/* Print-only top-5 states table */}
      <div className="hidden print:block mb-4">
        <h2 className="text-gray-500 text-xs uppercase tracking-widest mb-3">
          Top States · General Payments · {selectedYear}
        </h2>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
              <th style={{ textAlign: "left", padding: "4px 8px", color: "#6b7280", fontWeight: 600 }}>State</th>
              <th style={{ textAlign: "right", padding: "4px 8px", color: "#6b7280", fontWeight: 600 }}>Total Paid</th>
              <th style={{ textAlign: "right", padding: "4px 8px", color: "#6b7280", fontWeight: 600 }}>Providers</th>
              <th style={{ textAlign: "right", padding: "4px 8px", color: "#6b7280", fontWeight: 600 }}>Payments</th>
            </tr>
          </thead>
          <tbody>
            {[...states]
              .filter((s) => s.state && (s.total_paid || 0) > 0)
              .sort((a, b) => b.total_paid - a.total_paid)
              .slice(0, 5)
              .map((s) => (
                <tr key={s.state} style={{ borderBottom: "1px solid #f3f4f6" }}>
                  <td style={{ padding: "5px 8px", color: "#111827", fontWeight: 500 }}>{s.state}</td>
                  <td style={{ padding: "5px 8px", textAlign: "right", color: "#b45309", fontFamily: "monospace" }}>{formatCurrency(s.total_paid)}</td>
                  <td style={{ padding: "5px 8px", textAlign: "right", color: "#374151" }}>{(s.physician_count || 0).toLocaleString()}</td>
                  <td style={{ padding: "5px 8px", textAlign: "right", color: "#374151" }}>{(s.payment_count || 0).toLocaleString()}</td>
                </tr>
              ))}
          </tbody>
        </table>
        {states.filter((s) => s.state && s.total_paid > 0).length > 5 && (
          <p className="text-gray-400 text-xs mt-2">
            Showing 5 of {states.filter((s) => s.state && s.total_paid > 0).length} states · export full list from ProviderPayWatch
          </p>
        )}
      </div>

      <div className="flex items-center justify-between mb-3">
        <h2 className="text-white/60 text-xs uppercase tracking-widest">
          Geographic general payment distribution · {selectedYear}
        </h2>
        {hovered ? (
          <div className="text-right">
            <p className="text-white/70 text-xs font-semibold mb-1">{hoveredState}</p>
            <div className="space-y-0.5">
              <div className="flex items-baseline justify-end gap-2">
                <span className="text-white/30 text-xs">total</span>
                <span className="text-gold-400 text-xs font-mono">{formatCurrency(hovered.total_paid)}</span>
              </div>
              <div className="flex items-baseline justify-end gap-2">
                <span className="text-white/30 text-xs">providers</span>
                <span className="text-white/60 text-xs font-mono">{(hovered.physician_count || 0).toLocaleString()}</span>
              </div>
              <div className="flex items-baseline justify-end gap-2">
                <span className="text-white/30 text-xs">payments</span>
                <span className="text-white/60 text-xs font-mono">{(hovered.payment_count || 0).toLocaleString()}</span>
              </div>
              {hovered.physician_count > 0 && (
                <div className="flex items-baseline justify-end gap-2">
                  <span className="text-white/30 text-xs">avg / provider</span>
                  <span className="text-white/60 text-xs font-mono">{formatCurrency(hovered.total_paid / hovered.physician_count)}</span>
                </div>
              )}
              {hovered.payment_count > 0 && (
                <div className="flex items-baseline justify-end gap-2">
                  <span className="text-white/30 text-xs">avg / payment</span>
                  <span className="text-white/60 text-xs font-mono">{formatCurrency(hovered.total_paid / hovered.payment_count)}</span>
                </div>
              )}
            </div>
          </div>
        ) : (
          <p className="print:hidden text-white/20 text-xs">hover a state</p>
        )}
      </div>

      <div className="w-full rounded-lg overflow-hidden" style={{ background: "rgba(255,255,255,0.02)", marginLeft: "-8px", marginRight: "-8px", width: "calc(100% + 16px)" }}>
        <ComposableMap
          projection="geoAlbersUsa"
          style={{ width: "100%", height: "auto", display: "block" }}
          projectionConfig={{ scale: 1020, translate: [400, 240] }}
        >
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const fips = geo.id?.toString().padStart(2, "0");
                const abbr = FIPS_TO_ABBR[fips];
                const isHovered = hoveredState === abbr;
                const s = stateMap[abbr];
                const intensity = s ? s.total_paid / maxPaid : 0;
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={getFill(abbr)}
                    stroke={isHovered ? "rgba(251,191,36,0.8)" : "rgba(255,255,255,0.10)"}
                    strokeWidth={isHovered ? 1.5 : 0.5}
                    style={{
                      default: { outline: "none", cursor: stateMap[abbr] ? "pointer" : "default", transition: "fill 0.15s" },
                      hover:   { outline: "none" },
                      pressed: { outline: "none" },
                    }}
                    onMouseEnter={() => abbr && setHoveredState(abbr)}
                    onMouseLeave={() => setHoveredState(null)}
                  />
                );
              })
            }
          </Geographies>

          {/* State abbreviation labels */}
          {Object.entries(STATE_CENTROIDS).map(([abbr, { lon, lat }]) => {
            const s = stateMap[abbr];
            const intensity = s ? s.total_paid / maxPaid : 0;
            const offset = LABEL_OFFSETS[abbr];
            const dx = offset?.dx || 0;
            const dy = offset?.dy || 0;
            const textColor = intensity > 0.5
              ? "rgba(0,0,0,0.75)"
              : "rgba(255,255,255,0.55)";
            return (
              <Annotation
                key={abbr}
                subject={[lon, lat]}
                dx={dx}
                dy={dy}
                connectorProps={offset ? {
                  stroke: "rgba(255,255,255,0.25)",
                  strokeWidth: 0.5,
                  strokeLinecap: "round",
                } : {}}
              >
                <text
                  textAnchor="middle"
                  alignmentBaseline="middle"
                  style={{
                    fontSize: abbr === "DC" ? "5px" : "7px",
                    fontWeight: 600,
                    fill: textColor,
                    pointerEvents: "none",
                    userSelect: "none",
                    fontFamily: "ui-monospace, monospace",
                  }}
                >
                  {abbr}
                </text>
              </Annotation>
            );
          })}
        </ComposableMap>
      </div>

      <div className="mt-3 pt-3 border-t border-white/5 space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-white/20 text-xs shrink-0">More</span>
          <div className="flex gap-0.5 flex-1">
            {[0.92, 0.68, 0.40, 0.20, 0.08].map((v) => (
              <div key={v} className="flex-1 h-2 rounded-sm"
                style={{ backgroundColor: `rgba(251,191,36,${v})` }} />
            ))}
          </div>
          <span className="text-white/20 text-xs shrink-0">Less</span>
        </div>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex gap-x-4 gap-y-1 flex-wrap">
            {topStates.map((s) => (
              <span key={s.state} className="text-white/30 text-xs">
                <span className="text-white/50">{s.state}</span> {formatCurrency(s.total_paid)}
              </span>
            ))}
          </div>
          <button
            onClick={() => exportGeographicCsv(states, selectedYear, companyName, companyId)}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white/70 hover:border-white/20 transition-all shrink-0"
          >
            ⬇ Export
          </button>
        </div>
      </div>
    </div>
  );
}

function exportGeographicCsv(states, year, companyName, companyId) {
  const rows = [
    ["Note: Provider counts by state may sum to more than the company total because providers who received payments in multiple states are counted once per state."],
    [],
    ["Company", "Company ID", "Year", "State", "Total Paid ($)", "Payment Count", "Provider Count", "Avg Per Payment ($)", "Avg Per Provider ($)"],
  ];
  const sorted = [...states]
    .filter((s) => s.state && s.total_paid > 0)
    .sort((a, b) => b.total_paid - a.total_paid);
  for (const s of sorted) {
    const avgPerPayment = s.payment_count > 0 ? (s.total_paid / s.payment_count).toFixed(2) : "0.00";
    const avgPerProvider = s.physician_count > 0 ? (s.total_paid / s.physician_count).toFixed(2) : "0.00";
    rows.push([
      companyName,
      `'${companyId}`,   // prefix with ' to force Excel to treat as text
      year,
      s.state,
      s.total_paid.toFixed(2),
      s.payment_count || 0,
      s.physician_count || 0,
      avgPerPayment,
      avgPerProvider,
    ]);
  }
  downloadCsv(rows, `${companyName.replace(/\s+/g, "_")}_geographic_${year}.csv`);
}

// ── General Charts wrapper ────────────────────────────────────────────────────
function GeneralCharts({ years, filteredYearlyTotals, filteredPaymentTypes, filteredTopProducts, filteredSpecialties, filteredStates, filterActive, selectedYear, companyName, companyId }) {
  if (!years || !years.length) return null;
  return (
    <div className="mb-6">
      <PaymentTrendChart
        years={years}
        filteredYearlyTotals={filteredYearlyTotals}
        filteredPaymentTypes={filteredPaymentTypes}
        companyName={companyName}
        companyId={companyId}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <TopProductsChart
          years={years}
          selectedYear={selectedYear}
          filteredProducts={filteredTopProducts}
          companyName={companyName}
          companyId={companyId}
        />
        <SpecialtyBreakdown
          years={years}
          selectedYear={selectedYear}
          filteredSpecialties={filteredSpecialties}
          companyName={companyName}
          companyId={companyId}
        />
      </div>
      <StateGrid
        years={years}
        selectedYear={selectedYear}
        filteredStates={filteredStates}
        companyName={companyName}
        companyId={companyId}
      />
    </div>
  );
}

// ── General Tab ───────────────────────────────────────────────────────────────
function GeneralTab({
  profile, companyId, companyName,
  allTimeTotal, allTimePhysicians, allTimePayments,
  excludedNpis, paymentNpis, ownershipNpis, researchNpis, dualInterestNpis, tripleInterestNpis,
  watchedNpis, setWatchedNpis,
  onProvidersLoaded,
  isPro, isEnterprise = false, isSignedIn, user,
  setUpgradeMessage, setShowUpgradeModal,
  conflictCounts, researchGeneralDualCount,
  filterParams = {}, filterActive = false,
  filterUserId = "", filterListIds = "",
  podData = null,
  trackedNpiSet = new Set(),
}) {
  const EXPORT_LIMIT = isEnterprise ? Infinity : 500;
  const [selectedYear, setSelectedYear] = useState(2025);
  const [providers, setProviders] = useState([]);
  const [providerSummary, setProviderSummary] = useState(null);
  const [providersLoading, setProvidersLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [showMore, setShowMore] = useState(false);
  const [filteredYearlyTotals, setFilteredYearlyTotals] = useState([]);
  const [filteredTopProducts, setFilteredTopProducts] = useState(null);
  const [filteredSpecialties, setFilteredSpecialties] = useState(null);
  const [filteredStates, setFilteredStates] = useState(null);
  const [filteredPaymentTypes, setFilteredPaymentTypes] = useState({}); // keyed by year

  const years = profile?.years || [];
  const yearData = years.find((y) => y.year === selectedYear);

  // ── Fetch filtered yearly totals (all years, for stat cards + trend chart) ─
  useEffect(() => {
    if (!companyId || !filterUserId || !filterListIds) {
      setFilteredYearlyTotals([]);
      setFilteredPaymentTypes({});
      return;
    }
    const fp = { userId: filterUserId, listIds: filterListIds };
    getFilteredCompanyYearlyTotals(companyId, fp)
      .then((data) => setFilteredYearlyTotals(data || []))
      .catch(() => setFilteredYearlyTotals([]));

    // Fetch payment type breakdown for every year in parallel
    Promise.all(
      ALL_YEARS.map((yr) =>
        getFilteredPaymentTypeBreakdown(companyId, yr, fp)
          .then((types) => ({ year: yr, types: types || [] }))
          .catch(() => ({ year: yr, types: [] }))
      )
    ).then((results) => {
      const map = {};
      results.forEach(({ year, types }) => { if (types.length) map[year] = types; });
      setFilteredPaymentTypes(map);
    });
  }, [companyId, filterUserId, filterListIds]);

  // ── Fetch filtered top products + specialty + states for selected year ────
  useEffect(() => {
    if (!companyId || !filterUserId || !filterListIds) {
      setFilteredTopProducts(null);
      setFilteredSpecialties(null);
      setFilteredStates(null);
      return;
    }
    const fp = { userId: filterUserId, listIds: filterListIds };
    Promise.all([
      getFilteredTopProducts(companyId, selectedYear, fp),
      getFilteredSpecialtyBreakdown(companyId, selectedYear, fp),
      getFilteredStateBreakdown(companyId, selectedYear, fp),
    ])
      .then(([products, specialties, states]) => {
        setFilteredTopProducts(products || []);
        setFilteredSpecialties(specialties || []);
        setFilteredStates(states || []);
      })
      .catch(() => {
        setFilteredTopProducts(null);
        setFilteredSpecialties(null);
        setFilteredStates(null);
      });
  }, [companyId, selectedYear, filterUserId, filterListIds]);

  // ── Load providers for selected year ─────────────────────────────────────
  useEffect(() => {
    if (!companyId) return;
    setProvidersLoading(true);
    searchByCompany(companyName, selectedYear, { state: "ALL", specialty: "ALL", ...filterParams })
      .then((data) => {
        setProviderSummary({ totalPhysicians: data.totalPhysicians, totalPaid: data.totalPaid });
        const providerList = data.providers || [];
        setProviders(providerList);
        onProvidersLoaded(providerList);
        // Seed watched state from Supabase if signed in
        if (isSignedIn && user && providerList.length > 0) {
          const { getWatchedProvider } = require("../utils/alertsApi");
          Promise.all(
            providerList.map((p) =>
              getWatchedProvider(user.id, p.npi)
                .then((w) => ({ npi: p.npi, watched: !!w }))
                .catch(() => ({ npi: p.npi, watched: false }))
            )
          ).then((results) => {
            setWatchedNpis(new Map(results.map((r) => [r.npi, r.watched])));
          });
        }
      })
      .catch(() => setProviders([]))
      .finally(() => setProvidersLoading(false));
  }, [companyId, companyName, selectedYear, filterUserId, filterListIds]);

  async function handleFullExport() {
    setExportLoading(true);
    setExportError(null);
    try {
      const filterQuery = filterUserId && filterListIds
        ? `&filterUserId=${encodeURIComponent(filterUserId)}&filterListIds=${encodeURIComponent(filterListIds)}`
        : "";
      const res = await apiFetch(
        `/api/export-company?companyName=${encodeURIComponent(companyName)}&year=${selectedYear}&userId=${user?.id || ""}&isEnterprise=${isEnterprise ? "1" : "0"}${filterQuery}`
      );
      const data = await res.json();
      if (data.url) {
        const a = document.createElement("a");
        a.href = data.url;
        a.download = data.fileName;
        a.click();
      } else {
        setExportError(data.message || data.error || "Export failed. Please try again.");
      }
    } catch {
      setExportError("Export failed. Please try again.");
    } finally {
      setExportLoading(false);
    }
  }

  return (
    <>
      {/* ── My Providers filter banner ───────────────────────────────────── */}
      {filterActive && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gold-500/10 border border-gold-500/30 mb-4 text-xs">
          <span className="text-gold-400">◉</span>
          <span className="text-gold-400/80">Filtered to your providers · all data reflects your provider list only</span>
        </div>
      )}

      {/* ── POD Risk banner ──────────────────────────────────────────── */}
      <PodRiskBanner podData={podData} formatCurrency={formatCurrency} />

      {/* ── Conflict banners ─────────────────────────────────────────── */}
      <ConflictBanners
        tripleCount={conflictCounts?.tripleCount || 0}
        generalOwnershipCount={
          conflictCounts?.total > 0
            ? (conflictCounts.dualCount - (conflictCounts.researchCount || 0) + conflictCounts.tripleCount)
            : 0
        }
        generalResearchCount={researchGeneralDualCount || 0}
        ownershipResearchCount={conflictCounts?.researchCount || 0}
        totalOwners={conflictCounts?.total || 0}
        hasOwnershipData={conflictCounts?.total > 0}
      />

      {/* ── Year selector ──────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white/40 text-xs uppercase tracking-widest">
          General Payments
        </h2>
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          className="bg-navy-700 border border-white/10 text-white rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-gold-500/60"
        >
          {ALL_YEARS.slice().reverse().map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>

      {/* ── Combined stat cards — all years + selected year ───────────────── */}
      <div className="mb-6">
        {(() => {
          const useFiltered = filterActive && filteredYearlyTotals.length > 0;
          const filteredYearData = useFiltered ? filteredYearlyTotals.find((y) => y.year === selectedYear) : null;
          const displayAllTimeTotal = useFiltered ? filteredYearlyTotals.reduce((s, y) => s + (y.totalPaid || 0), 0) : allTimeTotal;
          const displayPeakProviders = useFiltered ? Math.max(...filteredYearlyTotals.map((y) => y.totalPhysicians || 0)) : allTimePhysicians;
          const displayAllTimePayments = useFiltered ? filteredYearlyTotals.reduce((s, y) => s + (y.paymentCount || 0), 0) : allTimePayments;
          const displayYearPaid = filteredYearData?.totalPaid ?? yearData?.totalPaid ?? 0;
          const displayYearProviders = filteredYearData?.totalPhysicians ?? yearData?.totalPhysicians ?? 0;
          const displayYearPayments = filteredYearData?.paymentCount ?? yearData?.paymentCount ?? 0;
          return (
            <>
              <p className="text-white/25 text-xs uppercase tracking-widest mb-2">All years</p>
              <div className="flex gap-3 flex-wrap mb-4">
                <div className="card py-3 px-4 text-center">
                  <div className="text-gold-400 font-display text-xl">{formatCurrency(displayAllTimeTotal)}</div>
                  <div className="text-white/30 text-xs mt-0.5">total paid</div>
                </div>
                <div className="card py-3 px-4 text-center">
                  <div className="text-white font-display text-xl">{displayPeakProviders.toLocaleString()}</div>
                  <div className="text-white/30 text-xs mt-0.5">peak providers / year</div>
                </div>
                <div className="card py-3 px-4 text-center">
                  <div className="text-white font-display text-xl">{displayAllTimePayments.toLocaleString()}</div>
                  <div className="text-white/30 text-xs mt-0.5">total payments</div>
                </div>
              </div>
              {(yearData || filteredYearData) && (
                <>
                  <p className="text-white/25 text-xs uppercase tracking-widest mb-2">{selectedYear}</p>
                  <div className="flex gap-3 flex-wrap">
                    <div className="card py-3 px-4 text-center">
                      <div className="text-gold-400/80 font-display text-xl">{formatCurrency(displayYearPaid)}</div>
                      <div className="text-white/30 text-xs mt-0.5">paid</div>
                    </div>
                    <div className="card py-3 px-4 text-center">
                      <div className="text-white/80 font-display text-xl">{displayYearProviders.toLocaleString()}</div>
                      <div className="text-white/30 text-xs mt-0.5">providers</div>
                    </div>
                    <div className="card py-3 px-4 text-center">
                      <div className="text-white/80 font-display text-xl">{displayYearPayments.toLocaleString()}</div>
                      <div className="text-white/30 text-xs mt-0.5">payments</div>
                    </div>
                  </div>
                </>
              )}
            </>
          );
        })()}
      </div>

      {/* ── Charts ────────────────────────────────────────────────────────── */}
      <GeneralCharts
        years={years}
        filteredYearlyTotals={filterActive && filteredYearlyTotals.length > 0 ? filteredYearlyTotals : null}
        filteredPaymentTypes={filterActive && Object.keys(filteredPaymentTypes).length > 0 ? filteredPaymentTypes : null}
        filteredTopProducts={filterActive ? filteredTopProducts : null}
        filteredSpecialties={filterActive ? filteredSpecialties : null}
        filteredStates={filterActive ? filteredStates : null}
        filterActive={filterActive}
        selectedYear={selectedYear}
        companyName={companyName}
        companyId={companyId}
      />

      {/* ── Print-only top-5 provider list ────────────────────────────────── */}
      {providers.length > 0 && (
        <div className="hidden print:block mt-6 mb-4">
          <h2 className="text-gray-500 text-xs uppercase tracking-widest mb-3">
            Top Providers · General Payments · {selectedYear}
          </h2>
          <div className="space-y-2">
            {providers.slice(0, 5).map((p) => {
              const isTriple = tripleInterestNpis?.has(p.npi);
              const isDual = !isTriple && dualInterestNpis?.has(p.npi);
              return (
                <div key={p.npi} className="border border-gray-200 rounded-lg px-4 py-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-gray-900 text-sm font-medium">{p.firstName} {p.lastName}</p>
                        {isTriple && <span className="text-xs text-red-600 border border-red-200 bg-red-50 px-1.5 py-0.5 rounded-full">⚠ Triple Interest</span>}
                        {isDual && <span className="text-xs text-orange-600 border border-orange-200 bg-orange-50 px-1.5 py-0.5 rounded-full">⚑ Dual Interest</span>}
                      </div>
                      <p className="text-gray-500 text-xs mt-0.5">
                        {p.specialty} · {p.city}{p.state ? `, ${p.state}` : ""} · NPI: {p.npi}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-amber-700 text-sm font-mono font-medium">{formatCurrency(p.totalPayments)}</p>
                      <p className="text-gray-400 text-xs">{p.paymentCount} payment{p.paymentCount !== 1 ? "s" : ""}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {providers.length > 5 && (
            <p className="text-gray-400 text-xs mt-2">Showing 5 of {providers.length.toLocaleString()} providers · export full list from ProviderPayWatch</p>
          )}
        </div>
      )}

      {/* ── Provider list ──────────────────────────────────────────────────── */}
      <div className="print:hidden flex items-center justify-between mb-3">
        <h3 className="text-white/40 text-xs uppercase tracking-widest">
          Top providers · {selectedYear}
        </h3>
        {isPro ? (
          <div className="flex items-center gap-3">
            <button
              onClick={handleFullExport}
              className="text-gold-400/80 hover:text-gold-400 text-xs transition-colors font-medium"
            >
              ⬇ Export all
            </button>
          </div>
        ) : (
          <button
            onClick={() => {
              setUpgradeMessage("Exporting provider data is a Pro feature. Upgrade to download the full provider list as a CSV.");
              setShowUpgradeModal(true);
            }}
            className="text-white/20 hover:text-white/40 text-xs transition-colors"
          >
            🔒 Export CSV
          </button>
        )}
      </div>

      {/* Show more / less toggle — top */}
      {providers.length > 10 && (
        <div className="print:hidden mb-3">
          <button
            onClick={() => setShowMore(!showMore)}
            className="text-xs text-white/40 hover:text-white/70 transition-colors flex items-center gap-1.5"
          >
            <span>{showMore ? "▲" : "▼"}</span>
            {(() => {
              const total = providerSummary?.totalPhysicians || yearData?.totalPhysicians || providers.length;
              return (
                <span>
                  {showMore
                    ? `Show top 10 of ${total.toLocaleString()} providers`
                    : total <= 50
                      ? `Show all ${total.toLocaleString()} providers`
                      : `Show top 50 of ${total.toLocaleString()} providers`}
                </span>
              );
            })()}
          </button>
        </div>
      )}

      {exportLoading && (
        <div className="card bg-gold-500/10 border border-gold-500/20 text-center py-3 mb-3">
          <p className="text-gold-400 text-xs animate-pulse">⏳ Preparing export...</p>
        </div>
      )}
      {exportError && (
        <div className="card border-red-500/20 bg-red-500/5 text-red-300 text-xs mb-3">
          ⚠ {exportError}
        </div>
      )}

      {providersLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card animate-pulse">
              <div className="h-4 bg-white/10 rounded w-1/2 mb-3" />
              <div className="h-3 bg-white/5 rounded w-3/4" />
            </div>
          ))}
        </div>
      )}

      {!providersLoading && providers.length === 0 && (
        <div className="card text-center py-10">
          <p className="text-white/40 text-sm">No general payment records for {selectedYear}.</p>
          {ALL_YEARS.some((y) => y !== selectedYear && (profile?.years || []).find((yr) => yr.year === y && yr.totalPaid > 0)) && (
            <p className="text-white/25 text-xs mt-2">
              Try a different year — this company has data in other years.
            </p>
          )}
        </div>
      )}

      <div className="print:hidden space-y-3">
        {providers.slice(0, showMore ? 50 : 10).map((physician) => (
          <div
            key={physician.npi}
            className="card hover:border-gold-500/30 hover:bg-navy-700/50 transition-all group relative"
          >
            <Link
              to={`/physician/${physician.npi || "unknown"}?name=${encodeURIComponent(
                physician.firstName + " " + physician.lastName
              )}&state=${physician.state || ""}`}
              className="block"
            >
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-white font-medium group-hover:text-gold-300 transition-colors">
                      {physician.firstName} {physician.lastName}
                    </h2>
                    {tripleInterestNpis?.has(physician.npi) ? (
                      <span className="badge bg-red-500/10 border border-red-500/30 text-red-400 text-xs shrink-0">
                        ⚠ Triple Interest
                      </span>
                    ) : dualInterestNpis?.has(physician.npi) ? (
                      <span className="badge bg-orange-500/10 border border-orange-500/30 text-orange-400 text-xs shrink-0">
                        ⚑ Dual Interest
                      </span>
                    ) : null}
                    {podData && (() => {
                      const podEntry = podData.find((p) => p.npi === physician.npi);
                      return podEntry ? (
                        <span className="badge bg-blue-500/10 border border-blue-500/30 text-blue-400 text-xs shrink-0">
                          ⚠ POD Risk Tier {podEntry.podTier}
                        </span>
                      ) : null;
                    })()}
                  </div>
                  <p className="text-white/40 text-sm mt-0.5">
                    {physician.specialty} · {physician.city}
                    {physician.state ? `, ${physician.state}` : ""}
                  </p>
                  {physician.npi && (
                    <p className="text-white/20 text-xs mt-0.5 font-mono">
                      NPI: {physician.npi}
                    </p>
                  )}
                </div>
                <span className="text-white/30 group-hover:text-white/60 ml-4 mt-1">›</span>
              </div>
              <div className="flex gap-3 mt-3 flex-wrap items-center">
                <span className="badge bg-gold-500/10 border border-gold-500/20 text-gold-300">
                  {formatCurrency(physician.totalPayments)}
                </span>
                <span className="badge bg-white/5 border border-white/10 text-white/50">
                  {physician.paymentCount} payment{physician.paymentCount !== 1 ? "s" : ""}
                </span>
                {physician.products?.length > 0 && (
                  <span className="badge bg-white/5 border border-white/10 text-white/50">
                    {physician.products[0]}
                    {physician.products.length > 1 ? ` +${physician.products.length - 1}` : ""}
                  </span>
                )}
              </div>
            </Link>

            {/* Badge row + actions */}
            <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
              <ProviderBadges
                npi={physician.npi}
                excludedNpis={excludedNpis}
                paymentNpis={paymentNpis}
                ownershipNpis={ownershipNpis}
                researchNpis={researchNpis}
                dualInterestNpis={dualInterestNpis}
              />
              <div className="flex gap-2 shrink-0">
                <WatchProviderButton
                  npi={physician.npi}
                  providerName={physician.firstName + " " + physician.lastName}
                  initialWatched={watchedNpis.get(physician.npi) || false}
                  onWatchChange={(npi, watched) =>
                    setWatchedNpis((prev) => new Map(prev).set(npi, watched))
                  }
                />
                <CompareButton provider={physician} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Show more / less toggle */}
      {providers.length > 10 && (
        <div className="print:hidden mt-3 flex items-center justify-between flex-wrap gap-2">
          <button
            onClick={() => setShowMore(!showMore)}
            className="text-xs text-white/40 hover:text-white/70 transition-colors flex items-center gap-1.5"
          >
            <span>{showMore ? "▲" : "▼"}</span>
            {(() => {
              const total = providerSummary?.totalPhysicians || yearData?.totalPhysicians || providers.length;
              return (
                <span>
                  {showMore
                    ? `Show top 10 of ${total.toLocaleString()} providers`
                    : total <= 50
                      ? `Show all ${total.toLocaleString()} providers`
                      : `Show top 50 of ${total.toLocaleString()} providers`}
                </span>
              );
            })()}
          </button>
          {isPro && showMore && (
            <p className="text-white/20 text-xs">
              Showing top 50 · use Export all for full list
            </p>
          )}
        </div>
      )}

      {/* Footer count + export */}
      {providers.length > 0 && (
        <div className="print:hidden mt-4 flex items-center justify-between flex-wrap gap-2 border-t border-white/5 pt-3">
          <p className="text-white/20 text-xs">
            {isPro
              ? `Showing top ${Math.min(showMore ? 50 : 10, providers.length)} of ${(providerSummary?.totalPhysicians || yearData?.totalPhysicians || providers.length).toLocaleString()} providers · ${selectedYear}`
              : `Showing top ${Math.min(showMore ? 50 : 10, providers.length)} of ${(providerSummary?.totalPhysicians || yearData?.totalPhysicians || providers.length).toLocaleString()} providers · upgrade to Pro to export`}
          </p>
          {isPro ? (
            <div className="flex items-center gap-3">
              <button
                onClick={handleFullExport}
                disabled={exportLoading}
                className="text-gold-400/80 hover:text-gold-400 text-xs transition-colors font-medium"
              >
                {exportLoading ? "⏳ Exporting..." : "⬇ Export all"}
              </button>
            </div>
          ) : (
            <button
              onClick={() => {
                setUpgradeMessage("Exporting provider data is a Pro feature. Upgrade to download the full provider list as a CSV.");
                setShowUpgradeModal(true);
              }}
              className="text-white/20 hover:text-white/40 text-xs transition-colors"
            >
              🔒 Export CSV
            </button>
          )}
        </div>
      )}
    </>
  );
}

// ── Ownership Tab ─────────────────────────────────────────────────────────────
function OwnershipTab({
  ownership, companyId, companyName,
  excludedNpis, paymentNpis, ownershipNpis, researchNpis, dualInterestNpis,
  watchedNpis, setWatchedNpis,
  isPro, isEnterprise = false, setUpgradeMessage, setShowUpgradeModal,
  onConflictCounts, setPaymentNpis,
  conflictCounts, filterActive = false,
  researchGeneralDualCount = 0,
  podData = null,
  trackedNpiSet = new Set(),
}) {
  const EXPORT_LIMIT = isEnterprise ? Infinity : 500;
  const [providerNames, setProviderNames] = useState({});
  const [providerDetails, setProviderDetails] = useState({});
  const [ownExcludedNpis, setOwnExcludedNpis] = useState(new Map());
  const [ownResearchNpis, setOwnResearchNpis] = useState(new Set());
  const [ownDualInterestNpis, setOwnDualInterestNpis] = useState(new Set());
  const [companyPaymentNpis, setCompanyPaymentNpis] = useState(new Set());
  const [companyResearchNpis, setCompanyResearchNpis] = useState(new Set());
  const [namesLoading, setNamesLoading] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [ownershipYearlyData, setOwnershipYearlyData] = useState([]);

  // Fetch real per-year ownership values from raw OWNRSHP tables
  useEffect(() => {
    if (!companyId) return;
    getOwnershipDetailByCompany(companyId)
      .then((data) => setOwnershipYearlyData(data || []))
      .catch(() => setOwnershipYearlyData([]));
  }, [companyId]);

  useEffect(() => {
    if (!ownership.length) return;
    const npis = ownership.map((o) => o.npi).filter(Boolean);

    // Single batch call for all names
    setNamesLoading(true);
    getProviderNamesBatch(npis)
      .then((results) => {
        const nameMap = {};
        const detailMap = {};
        for (const r of results) {
          nameMap[r.npi] = `${r.firstName} ${r.lastName}`.trim();
          detailMap[r.npi] = r;
        }
        setProviderNames(nameMap);
        setProviderDetails(detailMap);
      })
      .catch(() => {})
      .finally(() => setNamesLoading(false));

    // Independent badge checks for ownership tab NPIs
    Promise.all([
      checkExclusionsBatch(npis),
      checkPaymentsBatch(npis),
      checkResearchBatch(npis),
      checkDualInterestBatch(npis),
      // Company-scoped checks for accurate conflict counts
      checkPaymentsByCompanyBatch(npis, companyId),
      checkResearchByCompanyBatch(npis, companyId),
    ])
      .then(([exclusions, payNpis, resNpis, dualResults, companyPayNpis, companyResNpis]) => {
        const resSet = new Set(resNpis);
        const dualSet = new Set(dualResults.map((d) => d.npi));
        setOwnExcludedNpis(new Map(exclusions.map((e) => [e.npi, { oig: e.oig, sam: e.sam }])));
        setOwnResearchNpis(resSet);
        setOwnDualInterestNpis(dualSet);
        setCompanyPaymentNpis(new Set(companyPayNpis));
        setCompanyResearchNpis(new Set(companyResNpis));
        if (payNpis.length > 0 && setPaymentNpis) {
          setPaymentNpis((prev) => {
            const next = new Set(prev);
            payNpis.forEach((npi) => next.add(npi));
            return next;
          });
        }
        // Lift conflict counts to parent using company-scoped data
        if (onConflictCounts) {
          const ownershipNpiSet = new Set(npis);
          const companyPaySet = new Set(companyPayNpis);
          const companyResSet = new Set(companyResNpis);
          const dualCount = [...ownershipNpiSet].filter((npi) =>
            companyPaySet.has(npi) || companyResSet.has(npi)
          ).length;
          const researchCount = [...ownershipNpiSet].filter((npi) =>
            companyResSet.has(npi)
          ).length;
          const tripleCount = [...ownershipNpiSet].filter((npi) =>
            companyPaySet.has(npi) && companyResSet.has(npi)
          ).length;
          onConflictCounts({ dualCount, researchCount, tripleCount, total: ownership.length });
        }
      })
      .catch(() => {});
  }, [ownership]);

  const activeOwnership = ownership.filter((o) => o.hasActiveOwnership);
  const totalValue = ownership.reduce((s, o) => s + (o.valueOfInterest || 0), 0);
  const totalInvested = ownership.reduce((s, o) => s + (o.totalAmountInvested || 0), 0);

  // Conflict counts — computed from company-scoped batch check results
  const ownershipNpiSet = new Set(ownership.map((o) => o.npi).filter(Boolean));
  const dualCount = [...ownershipNpiSet].filter((npi) =>
    companyPaymentNpis.has(npi) || companyResearchNpis.has(npi)
  ).length;
  const researchCount = [...ownershipNpiSet].filter((npi) =>
    companyResearchNpis.has(npi)
  ).length;
  const tripleCount = [...ownershipNpiSet].filter((npi) =>
    companyPaymentNpis.has(npi) && companyResearchNpis.has(npi)
  ).length;

  if (ownership.length === 0) {
    return (
      <div className="card text-center py-12">
        <p className="text-white/50 text-sm">No ownership or investment interest records found for this company.</p>
        <p className="text-white/25 text-xs mt-2">CMS Open Payments Ownership data covers 2016–2025.</p>
      </div>
    );
  }

  return (
    <>
      {/* ── My Providers filter banner ───────────────────────────────────── */}
      {filterActive && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gold-500/10 border border-gold-500/30 mb-4 text-xs">
          <span className="text-gold-400">◉</span>
          <span className="text-gold-400/80">Filtered to your providers · showing only equity holders from your provider list</span>
        </div>
      )}

      {/* ── POD Risk banner ──────────────────────────────────────────── */}
      <PodRiskBanner podData={podData} formatCurrency={formatCurrency} />

      {/* ── Conflict banners ─────────────────────────────────────────── */}
      <ConflictBanners
        tripleCount={conflictCounts?.tripleCount || tripleCount}
        generalOwnershipCount={
          (conflictCounts?.dualCount || dualCount) - (conflictCounts?.researchCount || researchCount) + (conflictCounts?.tripleCount || tripleCount)
        }
        generalResearchCount={researchGeneralDualCount || 0}
        ownershipResearchCount={conflictCounts?.researchCount || researchCount}
        totalOwners={conflictCounts?.total || ownership.length}
        hasOwnershipData={true}
      />

      {/* Stat cards */}
      <div className="flex gap-3 mb-6 flex-wrap">
        <div className="card py-3 px-4 text-center">
          <div className="text-amber-400 font-display text-xl">
            {formatCurrency(totalValue)}
          </div>
          <div className="text-white/30 text-xs mt-0.5">total interest value</div>
        </div>
        <div className="card py-3 px-4 text-center">
          <div className="text-amber-400 font-display text-xl">
            {ownership.length.toLocaleString()}
          </div>
          <div className="text-white/30 text-xs mt-0.5">physicians with equity</div>
        </div>
        <div className="card py-3 px-4 text-center">
          <div className="text-green-400 font-display text-xl">
            {activeOwnership.length.toLocaleString()}
          </div>
          <div className="text-white/30 text-xs mt-0.5">active (2025)</div>
        </div>
        {tripleCount > 0 && (
          <div className="card py-3 px-4 text-center border-red-500/20 bg-red-500/[0.03]">
            <div className="text-red-400 font-display text-xl">{tripleCount}</div>
            <div className="text-white/30 text-xs mt-0.5">triple interest</div>
          </div>
        )}
        {dualCount > 0 && (
          <div className="card py-3 px-4 text-center border-orange-500/20 bg-orange-500/[0.03]">
            <div className="text-orange-400 font-display text-xl">{dualCount}</div>
            <div className="text-white/30 text-xs mt-0.5">dual interest</div>
          </div>
        )}
      </div>

      <OwnershipCharts ownership={ownership} ownershipYearlyData={ownershipYearlyData} companyName={companyName} companyId={companyId} />

      {/* Print-only top-5 ownership list */}
      <div className="hidden print:block mt-6 mb-4">
        <h2 className="text-gray-500 text-xs uppercase tracking-widest mb-3">
          Physicians with Equity · Top 5
        </h2>
        <div className="space-y-2">
          {ownership.slice(0, 5).map((o, i) => {
            const providerName = (o.firstName || o.lastName)
              ? `${o.firstName} ${o.lastName}`.trim()
              : providerNames[o.npi] || `NPI: ${o.npi}`;
            const isTriple = companyPaymentNpis.has(o.npi) && companyResearchNpis.has(o.npi);
            const isDual = !isTriple && (companyPaymentNpis.has(o.npi) || companyResearchNpis.has(o.npi));
            return (
              <div key={i} className={`border rounded-lg px-4 py-3 ${o.hasActiveOwnership ? "border-amber-200 bg-amber-50" : "border-gray-200"}`}>
                <div className="flex justify-between items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-gray-900 text-sm font-medium">{providerName}</p>
                      {o.hasActiveOwnership && <span className="text-xs text-green-700 border border-green-200 bg-green-50 px-1.5 py-0.5 rounded-full">Active 2025</span>}
                      {isTriple && <span className="text-xs text-red-600 border border-red-200 bg-red-50 px-1.5 py-0.5 rounded-full">⚠ Triple Interest</span>}
                      {isDual && <span className="text-xs text-orange-600 border border-orange-200 bg-orange-50 px-1.5 py-0.5 rounded-full">⚑ Dual Interest</span>}
                    </div>
                    <p className="text-gray-500 text-xs mt-0.5">
                      {o.interestHolder || "Interest holder unknown"}
                      {o.firstYear && o.latestYear ? ` · ${o.firstYear === o.latestYear ? o.firstYear : `${o.firstYear}–${o.latestYear}`}` : ""}
                      {o.npi ? ` · NPI: ${o.npi}` : ""}
                    </p>
                    {o.termsOfInterest && <p className="text-gray-400 text-xs mt-0.5 italic">{o.termsOfInterest}</p>}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-amber-700 text-sm font-mono font-medium">{formatCurrency(o.valueOfInterest)}</p>
                    <p className="text-gray-400 text-xs">interest value</p>
                    {o.totalAmountInvested > 0 && (
                      <>
                        <p className="text-gray-600 text-xs font-mono mt-1">{formatCurrency(o.totalAmountInvested)}</p>
                        <p className="text-gray-400 text-xs">invested</p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {ownership.length > 5 && (
          <p className="text-gray-400 text-xs mt-2">Showing 5 of {ownership.length.toLocaleString()} physicians · export full list from ProviderPayWatch</p>
        )}
      </div>

      {/* Provider list */}
      <div className="print:hidden flex items-center justify-between mb-3">
        <h3 className="text-white/40 text-xs uppercase tracking-widest">
          Physicians with equity · all years
        </h3>
        <button
          onClick={() => exportOwnershipCsv(ownership, companyName, providerNames, companyPaymentNpis, companyResearchNpis, EXPORT_LIMIT, new Map((podData || []).map((p) => [p.npi, p.podTier])), excludedNpis, trackedNpiSet)}
          className="text-gold-400/60 hover:text-gold-400 text-xs transition-colors"
        >
          ⬇ Export all
        </button>
      </div>

      {/* Show more / less toggle — top */}
      {ownership.length > 10 && (
        <div className="print:hidden mb-3">
          <button
            onClick={() => setShowMore(!showMore)}
            className="text-xs text-white/40 hover:text-white/70 transition-colors flex items-center gap-1.5"
          >
            <span>{showMore ? "▲" : "▼"}</span>
            <span>
              {showMore
                ? `Show top 10 of ${ownership.length.toLocaleString()} physician${ownership.length !== 1 ? "s" : ""}`
                : ownership.length <= 50
                  ? `Show all ${ownership.length.toLocaleString()} physician${ownership.length !== 1 ? "s" : ""}`
                  : `Show top 50 of ${ownership.length.toLocaleString()} physicians`}
            </span>
          </button>
        </div>
      )}

      <div className="print:hidden space-y-3">
        {ownership.slice(0, showMore ? 50 : 10).map((o, i) => (
          <div
            key={i}
            className={`card hover:border-amber-500/30 transition-all group relative ${
              o.hasActiveOwnership
                ? "bg-amber-500/[0.03] border-amber-500/20"
                : ""
            }`}
          >
            <Link
              to={`/physician/${o.npi}?name=${encodeURIComponent(
                providerNames[o.npi] || ""
              )}`}
              className="block"
            >
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-white font-medium group-hover:text-amber-300 transition-colors">
                      {(o.firstName || o.lastName)
                        ? `${o.firstName} ${o.lastName}`.trim()
                        : providerNames[o.npi] || `NPI: ${o.npi}`}
                    </h2>
                    {o.hasActiveOwnership && (
                      <span className="badge bg-green-500/20 border border-green-500/30 text-green-400 text-xs">
                        Active 2025
                      </span>
                    )}
                    {companyPaymentNpis.has(o.npi) && companyResearchNpis.has(o.npi) ? (
                      <span className="badge bg-red-500/10 border border-red-500/30 text-red-400 text-xs">
                        ⚠ Triple Interest
                      </span>
                    ) : (companyPaymentNpis.has(o.npi) || companyResearchNpis.has(o.npi)) ? (
                      <span className="badge bg-orange-500/10 border border-orange-500/30 text-orange-400/80 text-xs">
                        ⚑ Dual Interest
                      </span>
                    ) : null}
                    {podData && (() => {
                      const podEntry = podData.find((p) => p.npi === o.npi);
                      return podEntry ? (
                        <span className="badge bg-blue-500/10 border border-blue-500/30 text-blue-400 text-xs">
                          ⚠ POD Risk Tier {podEntry.podTier}
                        </span>
                      ) : null;
                    })()}
                  </div>
                  <p className="text-white/40 text-sm mt-0.5">
                    {(o.specialty || providerDetails[o.npi]?.specialty)
                      ? `${o.specialty || providerDetails[o.npi]?.specialty} · `
                      : ""}
                    {(o.city || providerDetails[o.npi]?.city)
                      ? `${o.city || providerDetails[o.npi]?.city}${(o.state || providerDetails[o.npi]?.state) ? `, ${o.state || providerDetails[o.npi]?.state}` : ""} · `
                      : ""}
                    {o.interestHolder || "Interest holder unknown"}
                    {o.firstYear && o.latestYear
                      ? ` · ${o.firstYear === o.latestYear ? o.firstYear : `${o.firstYear}–${o.latestYear}`}`
                      : ""}
                  </p>
                  {o.termsOfInterest && (
                    <p className="text-amber-300/30 text-xs mt-1 italic line-clamp-2">
                      {o.termsOfInterest}
                    </p>
                  )}
                </div>
                <div className="text-right shrink-0 ml-4">
                  <p className="text-amber-400 text-sm font-mono font-medium">
                    {formatCurrency(o.valueOfInterest)}
                  </p>
                  <p className="text-white/30 text-xs">interest value</p>
                  {o.totalAmountInvested > 0 && (
                    <>
                      <p className="text-white/40 text-xs font-mono mt-1">
                        {formatCurrency(o.totalAmountInvested)}
                      </p>
                      <p className="text-white/25 text-xs">invested</p>
                    </>
                  )}
                </div>
              </div>
            </Link>

            {/* Badge row */}
            <div className="mt-3">
              <ProviderBadges
                npi={o.npi}
                excludedNpis={ownExcludedNpis.size ? ownExcludedNpis : excludedNpis}
                paymentNpis={paymentNpis}
                ownershipNpis={ownershipNpis}
                researchNpis={ownResearchNpis.size ? ownResearchNpis : researchNpis}
                dualInterestNpis={ownDualInterestNpis.size ? ownDualInterestNpis : dualInterestNpis}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Show more toggle */}
      {ownership.length > 10 && (
        <div className="print:hidden mt-3 flex items-center justify-between flex-wrap gap-2">
          <button
            onClick={() => setShowMore(!showMore)}
            className="text-xs text-white/40 hover:text-white/70 transition-colors flex items-center gap-1.5"
          >
            <span>{showMore ? "▲" : "▼"}</span>
            <span>
              {showMore
                ? `Show top 10 of ${ownership.length.toLocaleString()} physician${ownership.length !== 1 ? "s" : ""}`
                : ownership.length <= 50
                  ? `Show all ${ownership.length.toLocaleString()} physician${ownership.length !== 1 ? "s" : ""}`
                  : `Show top 50 of ${ownership.length.toLocaleString()} physicians`}
            </span>
          </button>
          {showMore && (
            <p className="text-white/20 text-xs">
              Showing top 50 · use Export CSV for full list
            </p>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="print:hidden mt-4 flex items-center justify-between flex-wrap gap-2 border-t border-white/5 pt-3">
        <p className="text-white/20 text-xs">
          Showing {Math.min(showMore ? 50 : 10, ownership.length)} of{" "}
          {ownership.length.toLocaleString()} physicians with equity · 2016–2025
        </p>
        <button
          onClick={() => exportOwnershipCsv(ownership, companyName, providerNames, companyPaymentNpis, companyResearchNpis, EXPORT_LIMIT, new Map((podData || []).map((p) => [p.npi, p.podTier])), excludedNpis, trackedNpiSet)}
          className="text-gold-400/60 hover:text-gold-400 text-xs transition-colors"
        >
          ⬇ Export all
        </button>
      </div>

      <p className="text-white/20 text-xs text-center mt-6">
        Source: CMS Open Payments Ownership & Investment Interest · 2016–2025
      </p>
    </>
  );
}

// ── Ownership Charts ──────────────────────────────────────────────────────────
function OwnershipCharts({ ownership, ownershipYearlyData, companyName, companyId }) {
  if (!ownership || ownership.length === 0) return null;
  return (
    <div className="mb-6">
      <OwnershipActiveTimeline ownership={ownership} ownershipYearlyData={ownershipYearlyData} companyName={companyName} companyId={companyId} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <OwnershipHolderDonut ownership={ownership} companyName={companyName} companyId={companyId} />
        <OwnershipValueByHolder ownership={ownership} companyName={companyName} companyId={companyId} />
      </div>
      <OwnershipInterestTypeChart ownership={ownership} companyName={companyName} companyId={companyId} />
    </div>
  );
}

// ── Holder type color map ─────────────────────────────────────────────────────
const HOLDER_COLORS = {
  "Physician":               { fill: "rgba(251,191,36,0.85)",  legend: "bg-gold-400/80",    text: "text-gold-400"    },
  "Immediate Family Member": { fill: "rgba(249,115,22,0.80)",  legend: "bg-orange-500/80",  text: "text-orange-400"  },
  "Third Party":             { fill: "rgba(139,92,246,0.85)",  legend: "bg-violet-500/80",  text: "text-violet-400"  },
  "Other":                   { fill: "rgba(100,116,139,0.70)", legend: "bg-slate-500/80",   text: "text-slate-400"   },
};
function getHolderColor(label) {
  return HOLDER_COLORS[label] || HOLDER_COLORS["Other"];
}

function normalizeHolder(raw) {
  if (!raw) return "Other";
  const r = raw.trim();
  if (/physician/i.test(r) && !/family/i.test(r)) return "Physician";
  if (/family/i.test(r)) return "Immediate Family Member";
  if (/third\s*party/i.test(r)) return "Third Party";
  return r || "Other";
}

// ── Chart 1: Holder Type Donut ────────────────────────────────────────────────
function OwnershipHolderDonut({ ownership, companyName, companyId }) {
  const [hovered, setHovered] = useState(null);

  // Group by normalized holder type — count of physicians
  const groups = {};
  for (const o of ownership) {
    const label = normalizeHolder(o.interestHolder);
    if (!groups[label]) groups[label] = { count: 0, value: 0 };
    groups[label].count += 1;
    groups[label].value += o.valueOfInterest || 0;
  }

  const segments = Object.entries(groups)
    .sort((a, b) => b[1].count - a[1].count)
    .map(([label, d]) => ({ label, count: d.count, value: d.value }));

  const total = segments.reduce((s, seg) => s + seg.count, 0);
  if (!total) return null;

  // SVG donut — cx/cy = 60, r = 48 (inner = 30)
  const cx = 60, cy = 60, R = 48, r = 30;
  let cumAngle = -Math.PI / 2;

  function arc(seg) {
    const angle = (seg.count / total) * 2 * Math.PI;
    const x1 = cx + R * Math.cos(cumAngle);
    const y1 = cy + R * Math.sin(cumAngle);
    cumAngle += angle;
    const x2 = cx + R * Math.cos(cumAngle);
    const y2 = cy + R * Math.sin(cumAngle);
    const ix1 = cx + r * Math.cos(cumAngle - angle);
    const iy1 = cy + r * Math.sin(cumAngle - angle);
    const ix2 = cx + r * Math.cos(cumAngle);
    const iy2 = cy + r * Math.sin(cumAngle);
    const large = angle > Math.PI ? 1 : 0;
    return `M ${x1} ${y1} A ${R} ${R} 0 ${large} 1 ${x2} ${y2} L ${ix2} ${iy2} A ${r} ${r} 0 ${large} 0 ${ix1} ${iy1} Z`;
  }

  // Re-run arc calculation cleanly for rendering
  let runAngle = -Math.PI / 2;
  const paths = segments.map((seg) => {
    const angle = (seg.count / total) * 2 * Math.PI;
    const x1 = cx + R * Math.cos(runAngle);
    const y1 = cy + R * Math.sin(runAngle);
    runAngle += angle;
    const x2 = cx + R * Math.cos(runAngle);
    const y2 = cy + R * Math.sin(runAngle);
    const ix1 = cx + r * Math.cos(runAngle - angle);
    const iy1 = cy + r * Math.sin(runAngle - angle);
    const ix2 = cx + r * Math.cos(runAngle);
    const iy2 = cy + r * Math.sin(runAngle);
    const large = angle > Math.PI ? 1 : 0;
    const d = `M ${x1} ${y1} A ${R} ${R} 0 ${large} 1 ${x2} ${y2} L ${ix2} ${iy2} A ${r} ${r} 0 ${large} 0 ${ix1} ${iy1} Z`;
    return { ...seg, d };
  });

  const hov = hovered ? segments.find((s) => s.label === hovered) : null;

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-white/60 text-xs uppercase tracking-widest">Interest holder type</h2>
      </div>

      <div className="flex items-center gap-4">
        {/* Donut SVG */}
        <div className="shrink-0" style={{ width: 120, height: 120 }}>
          <svg viewBox="0 0 120 120" width="120" height="120">
            {paths.map((seg) => (
              <path
                key={seg.label}
                d={seg.d}
                fill={getHolderColor(seg.label).fill}
                opacity={hovered && hovered !== seg.label ? 0.35 : 1}
                stroke="rgba(15,23,42,0.6)"
                strokeWidth="1"
                style={{ cursor: "pointer", transition: "opacity 0.15s" }}
                onMouseEnter={() => setHovered(seg.label)}
                onMouseLeave={() => setHovered(null)}
              />
            ))}
            {/* Center label */}
            <text x="60" y="56" textAnchor="middle" style={{ fontSize: "14px", fontWeight: 700, fill: "rgba(255,255,255,0.85)", fontFamily: "ui-sans-serif, system-ui" }}>
              {hov ? hov.count : total}
            </text>
            <text x="60" y="68" textAnchor="middle" style={{ fontSize: "7px", fill: "rgba(255,255,255,0.35)", fontFamily: "ui-sans-serif, system-ui" }}>
              {hov ? hov.label.split(" ")[0].toLowerCase() : "physicians"}
            </text>
          </svg>
        </div>

        {/* Legend */}
        <div className="flex flex-col gap-2 flex-1 min-w-0">
          {segments.map((seg) => {
            const pct = ((seg.count / total) * 100).toFixed(1);
            const colors = getHolderColor(seg.label);
            return (
              <div
                key={seg.label}
                className="flex items-center justify-between gap-2 cursor-pointer"
                onMouseEnter={() => setHovered(seg.label)}
                onMouseLeave={() => setHovered(null)}
                style={{ opacity: hovered && hovered !== seg.label ? 0.4 : 1, transition: "opacity 0.15s" }}
              >
                <div className="flex items-center gap-1.5 min-w-0">
                  <div className="w-2 h-2 rounded-sm shrink-0" style={{ backgroundColor: getHolderColor(seg.label).fill }} />
                  <span className="text-white/50 text-xs truncate">{seg.label}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-white/25 text-xs">{pct}%</span>
                  <span className={`text-xs font-medium ${colors.text}`}>{seg.count}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
        <p className="text-white/20 text-xs">By number of physicians · all years</p>
        <button
          onClick={() => exportHolderDonutCsv(segments, companyName, companyId)}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white/70 hover:border-white/20 transition-all shrink-0"
        >
          ⬇ Export
        </button>
      </div>
    </div>
  );
}

function exportHolderDonutCsv(segments, companyName, companyId) {
  const total = segments.reduce((s, seg) => s + seg.count, 0);
  const rows = [["Company", "Company ID", "Interest Holder Type", "Physician Count", "% of Total", "Total Interest Value ($)"]];
  for (const seg of segments) {
    rows.push([
      companyName,
      `'${companyId}`,
      seg.label,
      seg.count,
      ((seg.count / total) * 100).toFixed(1) + "%",
      seg.value.toFixed(2),
    ]);
  }
  downloadCsv(rows, `${companyName.replace(/\s+/g, "_")}_ownership_holder_types.csv`);
}

// ── Chart 2: Value by Holder Type (horizontal bars) ───────────────────────────
function OwnershipValueByHolder({ ownership, companyName, companyId }) {
  const [hovered, setHovered] = useState(null);

  const groups = {};
  for (const o of ownership) {
    const label = normalizeHolder(o.interestHolder);
    if (!groups[label]) groups[label] = { value: 0, invested: 0, count: 0 };
    groups[label].value += o.valueOfInterest || 0;
    groups[label].invested += o.totalAmountInvested || 0;
    groups[label].count += 1;
  }

  const segments = Object.entries(groups)
    .sort((a, b) => b[1].value - a[1].value)
    .map(([label, d]) => ({ label, ...d }));

  if (!segments.length) return null;
  const maxVal = segments[0].value || 1;
  const totalVal = segments.reduce((s, seg) => s + seg.value, 0);

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-white/60 text-xs uppercase tracking-widest">Interest value by holder</h2>
      </div>

      <div className="space-y-3">
        {segments.map((seg) => {
          const pct = (seg.value / maxVal) * 100;
          const sharePct = (seg.value / totalVal) * 100;
          const colors = getHolderColor(seg.label);
          const isHov = hovered === seg.label;
          return (
            <div
              key={seg.label}
              style={{ opacity: hovered && !isHov ? 0.4 : 1, transition: "opacity 0.15s", cursor: "default" }}
              onMouseEnter={() => setHovered(seg.label)}
              onMouseLeave={() => setHovered(null)}
            >
              <div className="flex items-center justify-between mb-1 gap-2">
                <div className="flex items-center gap-1.5 min-w-0">
                  <div className="w-2 h-2 rounded-sm shrink-0" style={{ backgroundColor: colors.fill }} />
                  <span className="text-white/60 text-xs truncate">{seg.label}</span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-white/25 text-xs">{sharePct.toFixed(1)}%</span>
                  <span className={`text-xs font-mono font-medium ${colors.text}`}>
                    {seg.value >= 1000000
                      ? `$${(seg.value / 1000000).toFixed(2)}M`
                      : formatCurrency(seg.value)}
                  </span>
                </div>
              </div>
              <div className="w-full bg-white/5 rounded-full h-1.5">
                <div
                  className="h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${pct}%`, backgroundColor: colors.fill }}
                />
              </div>
              {isHov && seg.invested > 0 && (
                <p className="text-white/25 text-xs mt-1">
                  {formatCurrency(seg.invested)} invested · {seg.count} physician{seg.count !== 1 ? "s" : ""}
                </p>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
        <p className="text-white/20 text-xs">Total reported interest value · all years</p>
        <button
          onClick={() => exportValueByHolderCsv(segments, companyName, companyId)}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white/70 hover:border-white/20 transition-all shrink-0"
        >
          ⬇ Export
        </button>
      </div>
    </div>
  );
}

function exportValueByHolderCsv(segments, companyName, companyId) {
  const totalVal = segments.reduce((s, seg) => s + seg.value, 0);
  const rows = [["Company", "Company ID", "Interest Holder Type", "Physician Count", "Total Interest Value ($)", "% of Total", "Total Amount Invested ($)"]];
  for (const seg of segments) {
    rows.push([
      companyName,
      `'${companyId}`,
      seg.label,
      seg.count,
      seg.value.toFixed(2),
      ((seg.value / totalVal) * 100).toFixed(1) + "%",
      seg.invested.toFixed(2),
    ]);
  }
  downloadCsv(rows, `${companyName.replace(/\s+/g, "_")}_ownership_value_by_holder.csv`);
}

// ── Chart 3: Active vs. Historical by Year ────────────────────────────────────
function OwnershipActiveTimeline({ ownership, ownershipYearlyData, companyName, companyId }) {
  const [hovered, setHovered] = useState(null);

  // Count active holders (has_active_ownership) for 2025 green segment
  const activeCount2025 = ownership.filter((o) => o.hasActiveOwnership).length;

  // Use real per-year data from raw OWNRSHP tables if available,
  // otherwise fall back to span-based calculation
  let yearData;
  if (ownershipYearlyData && ownershipYearlyData.length > 0) {
    yearData = ownershipYearlyData.map((d) => ({
      year: d.programYear,
      total: d.holderCount,
      value: d.totalValue,
      active: d.programYear === 2025 ? activeCount2025 : 0,
    }));
  } else {
    // Fallback: span-based calculation
    const yearCounts = {};
    for (const yr of ALL_YEARS) yearCounts[yr] = { active: 0, total: 0, value: 0 };
    for (const o of ownership) {
      const first = o.firstYear || 2016;
      const last = o.latestYear || 2025;
      for (let yr = first; yr <= last; yr++) {
        if (yearCounts[yr] !== undefined) {
          yearCounts[yr].total += 1;
          yearCounts[yr].value += o.valueOfInterest || 0;
        }
      }
      if (o.hasActiveOwnership && yearCounts[2025]) {
        yearCounts[2025].active += 1;
      }
    }
    yearData = ALL_YEARS.map((yr) => ({ year: yr, ...yearCounts[yr] }))
      .filter((d) => d.total > 0);
  }

  if (!yearData.length) return null;
  const maxTotal = Math.max(...yearData.map((d) => d.total), 1);

  return (
    <div className="card mb-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white/60 text-xs uppercase tracking-widest">
          Equity holders by year
        </h2>
        {hovered && (() => {
          const d = yearData.find((y) => y.year === hovered);
          return d ? (
            <div className="text-right">
              <p className="text-white/40 text-xs">{hovered}</p>
              <p className="text-amber-400 text-xs font-mono">{d.total} holder{d.total !== 1 ? "s" : ""}</p>
            </div>
          ) : null;
        })()}
      </div>

      <div className="relative" style={{ height: "140px" }}>
        {/* Y-axis */}
        <div className="absolute left-0 top-0 bottom-6 flex flex-col justify-between items-end pr-1 pointer-events-none" style={{ width: "28px" }}>
          {[1, 0.5, 0].map((pct) => (
            <span key={pct} className="text-white/20" style={{ fontSize: "8px" }}>
              {Math.round(maxTotal * pct)}
            </span>
          ))}
        </div>

        {/* Bars */}
        <div className="absolute inset-0 flex items-end gap-1" style={{ left: "30px", paddingBottom: "24px" }}>
          {yearData.map(({ year, total, active, value }) => {
            const heightPct = (total / maxTotal) * 100;
            const isHov = hovered === year;
            return (
              <div
                key={year}
                className="flex-1 flex flex-col justify-end h-full relative"
                onMouseEnter={() => setHovered(year)}
                onMouseLeave={() => setHovered(null)}
                style={{ cursor: "pointer" }}
              >
                {/* Tooltip */}
                {isHov && (
                  <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-navy-900 border border-white/10 rounded-lg px-3 py-2 z-20 pointer-events-none" style={{ minWidth: "130px" }}>
                    <p className="text-white/50 text-xs font-medium mb-1.5 pb-1.5 border-b border-white/10">{year}</p>
                    <div className="flex justify-between gap-2">
                      <span className="text-white/40 text-xs">Holders</span>
                      <span className="text-amber-400 text-xs">{total}</span>
                    </div>
                    {active > 0 && (
                      <div className="flex justify-between gap-2 mt-0.5">
                        <span className="text-white/40 text-xs">Active 2025</span>
                        <span className="text-green-400 text-xs">{active}</span>
                      </div>
                    )}
                    <div className="flex justify-between gap-2 mt-0.5">
                      <span className="text-white/40 text-xs">Value</span>
                      <span className="text-white/60 text-xs">{formatCurrency(value)}</span>
                    </div>
                  </div>
                )}

                {/* Count label above bar */}
                {total > 0 && (
                  <div className="absolute w-full text-center pointer-events-none" style={{ bottom: `${Math.max(heightPct, 2)}%`, paddingBottom: "2px" }}>
                    <span className="text-white/30" style={{ fontSize: "8px" }}>{total}</span>
                  </div>
                )}

                {/* Bar — two-tone: active (green bottom) + historical (amber top) */}
                <div
                  className="w-full rounded-t overflow-hidden flex flex-col-reverse transition-opacity duration-150"
                  style={{
                    height: total > 0 ? `${Math.max(heightPct, 2)}%` : "2px",
                    opacity: hovered && !isHov ? 0.4 : 1,
                  }}
                >
                  {/* Active portion (2025 active physicians, shown green at bottom) */}
                  {active > 0 && (
                    <div
                      style={{
                        height: `${(active / total) * 100}%`,
                        backgroundColor: "rgba(34,197,94,0.70)",
                        minHeight: "3px",
                      }}
                    />
                  )}
                  {/* Historical portion */}
                  <div
                    style={{
                      height: `${((total - active) / total) * 100}%`,
                      backgroundColor: "rgba(251,191,36,0.65)",
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Year labels */}
        <div className="absolute bottom-0 flex gap-1" style={{ left: "30px", right: "0" }}>
          {yearData.map(({ year }) => (
            <div key={year} className="flex-1 text-center">
              <span className={`transition-colors ${hovered === year ? "text-white/60" : "text-white/25"}`} style={{ fontSize: "10px" }}>
                {year}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Legend + footnote + export */}
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: "rgba(251,191,36,0.65)" }} />
            <span className="text-white/30 text-xs">Prior years</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: "rgba(34,197,94,0.70)" }} />
            <span className="text-white/30 text-xs">Active 2025</span>
          </div>
          <p className="text-white/15 text-xs hidden sm:block">Holders counted in each year they held interest</p>
        </div>
        <button
          onClick={() => exportOwnershipTimelineCsv(yearData, companyName, companyId)}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white/70 hover:border-white/20 transition-all shrink-0"
        >
          ⬇ Export
        </button>
      </div>
    </div>
  );
}

function exportOwnershipTimelineCsv(yearData, companyName, companyId) {
  const rows = [["Company", "Company ID", "Year", "Total Holders", "Active (2025)", "Total Interest Value ($)"]];
  for (const d of yearData) {
    rows.push([
      companyName,
      `'${companyId}`,
      d.year,
      d.total,
      d.active || 0,
      (d.value || 0).toFixed(2),
    ]);
  }
  downloadCsv(rows, `${companyName.replace(/\s+/g, "_")}_ownership_by_year.csv`);
}

// ── Interest type categorizer (mirrors PhysicianOwnersPage categorize()) ─────
function categorizeTerms(terms) {
  if (!terms) return "Other";
  const t = terms.toUpperCase();
  if (t.includes("COMMON") && t.includes("PREFERRED")) return "Common & Preferred Stock";
  if (t.includes("RESTRICTED STOCK") || t.includes("RSU") || t.includes("PRSU") || t.includes("DEFERRED STOCK UNIT")) return "Restricted Stock / RSUs";
  if (t.includes("STOCK OPTION") || t.includes("OPTION TO PURCHASE") || t.includes("STOCK AND OR OPTIONS") || t.includes("STOCK AND/OR OPTIONS") || t.includes("OPTION GRANT") || t.includes("OPTIONS GRANTED") || t.includes("OPTIONS EXERCISED") || t.includes("EXERCISED OPTION") || t.includes("OPTIONS FOR SERVICES") || t.includes("OPTION FOR COMMON") || t.includes("NSO GRANT") || t.includes("VESTED OPTION") || t.includes("UNEXERCISED OPTION") || t.includes("BLACK-SCHOLES") || t.includes("BLACK SCHOLES") || t.includes("ADVISOR OPTION") || (t.includes("OPTION") && t.includes("EXERCISE"))) return "Stock Options";
  if (t.includes("WARRANT")) return "Warrants";
  if (t.includes("COMMON STOCK") || t.includes("COMMON SHARE") || t.includes("COMMON UNIT") || t.includes("ORDINARY SHARE") || t.includes("FOUNDER") || t === "COMMON" || t.includes("SHARES OF STOCK") || t.includes("COMPANY STOCK") || t.includes("STOCK OWNERSHIP") || t.includes("STOCK HOLDER") || t.includes("STOCKHOLDER") || t.includes("SHARE OWNERSHIP") || t.includes("STOCK GRANT") || t.includes("STOCK PURCHASE") || t.includes("PURCHASED CORPORATE SHARE") || t.includes("EQUITY STOCK") || t.includes("SHARES OF THE COMPANY") || t.includes("SHARES OF COMPANY") || t.includes("STOCK INTEREST")) return "Common Stock";
  if (t.includes("PREFERRED") || t.includes("SERIES A") || t.includes("SERIES B") || t.includes("SERIES C") || t.includes("SERIES D") || t.includes("SERIES E") || t.includes("SERIES F") || t.includes("SERIES SEED")) return "Preferred Stock";
  if (t.includes("LLC") || t.includes("L.L.C") || t.includes("MEMBERSHIP INTEREST") || t.includes("MEMBERSHIP UNIT") || t.includes("PROFIT INTEREST") || t.includes("PROFITS INTEREST") || t.includes("OPERATING AGREEMENT") || t.includes("CLASS A UNIT") || t.includes("CLASS B UNIT") || t.includes("CLASS C UNIT") || t.includes("CLASS D UNIT") || t.includes("B UNITS") || t.includes("A UNITS") || t.includes("UNIT HOLDER") || t.includes("UNITHOLDER")) return "LLC / Membership Interest";
  if (t.includes("CONVERTIBLE") || t.includes("SAFE INVESTMENT") || t.includes("SAFE 20%") || t.includes("PROMISSORY NOTE") || t === "SAFE") return "Convertible Instruments";
  if (t.includes("PARTNERSHIP") || t.includes("LP ") || t.includes("LLP") || t.includes("CAPITAL CONTRIBUTION") || t.includes("CAPITAL INVESTMENT") || t.includes("PARTNER CAPITAL") || t.includes("PARTNERSHIP SHARES")) return "Partnership Interest";
  if (t.includes("ACQUISITION") || t.includes("LIQUIDAT") || t.includes("REPURCHAS") || t.includes("BOUGHT BACK") || t.includes("BUYOUT") || t.includes("MERGER") || t.includes("COMPANY SOLD") || t.includes("ASSETS SOLD") || t.includes("CEASED BUSINESS") || t.includes("DISSOLVED") || t.includes("BANKRUPTCY") || t.includes("OUT OF BUSINESS")) return "Acquisition / Liquidation";
  if (t.includes("ROYALT") || t.includes("DIVIDEND") || t.includes("DISTRIBUTION") || t.includes("10% OF NET PROFIT") || t.includes("PROFIT BASED ON") || t.includes("20% RETURN") || t.includes("20% PROFIT")) return "Royalties / Distributions";
  if (t === "EQUITY" || t === "INVESTMENT" || t === "STOCK" || t === "SHARES" || t === "OWNERSHIP" || t === "INVESTOR" || t.includes("EQUITY INVESTMENT") || t.includes("EQUITY INTEREST") || t.includes("EQUITY OWNERSHIP") || t === "STOCK OWNERSHIP" || t === "PURCHASED SHARES" || t.includes("CASH INVESTMENT") || t.includes("PRIVATE PLACEMENT") || t.includes("OWNERSHIP INTEREST")) return "Equity Investment";
  return "Other";
}

const INTEREST_TYPE_COLORS = {
  "Common Stock":              "rgba(251,191,36,0.85)",
  "Common & Preferred Stock":  "rgba(232,178,10,0.85)",
  "Preferred Stock":           "rgba(139,92,246,0.85)",
  "Restricted Stock / RSUs":   "rgba(167,139,250,0.80)",
  "Stock Options":             "rgba(59,130,246,0.85)",
  "Warrants":                  "rgba(96,165,250,0.80)",
  "LLC / Membership Interest": "rgba(20,184,166,0.85)",
  "Convertible Instruments":   "rgba(249,115,22,0.85)",
  "Partnership Interest":      "rgba(34,197,94,0.85)",
  "Acquisition / Liquidation": "rgba(239,68,68,0.85)",
  "Royalties / Distributions": "rgba(236,72,153,0.85)",
  "Equity Investment":         "rgba(14,165,233,0.85)",
  "Other":                     "rgba(100,116,139,0.70)",
};

function getInterestTypeColor(cat) {
  return INTEREST_TYPE_COLORS[cat] || INTEREST_TYPE_COLORS["Other"];
}

// ── Chart 4: Interest Type Breakdown (horizontal bars by value) ───────────────
function OwnershipInterestTypeChart({ ownership, companyName, companyId }) {
  const [hovered, setHovered] = useState(null);

  // Aggregate value + count by categorized interest type
  const groups = {};
  for (const o of ownership) {
    const cat = categorizeTerms(o.termsOfInterest);
    if (!groups[cat]) groups[cat] = { value: 0, count: 0, avgValue: 0 };
    groups[cat].value += o.valueOfInterest || 0;
    groups[cat].count += 1;
  }

  const segments = Object.entries(groups)
    .sort((a, b) => b[1].value - a[1].value)
    .map(([cat, d]) => ({
      cat,
      value: d.value,
      count: d.count,
      avgValue: d.count > 0 ? d.value / d.count : 0,
    }));

  if (!segments.length) return null;
  const totalValue = segments.reduce((s, seg) => s + seg.value, 0);
  if (totalValue === 0) return null;
  const maxValue = segments[0].value;

  return (
    <div className="card mb-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white/60 text-xs uppercase tracking-widest">
          Interest type breakdown · by value
        </h2>
      </div>

      <div className="space-y-3">
        {segments.map((seg) => {
          const barPct = (seg.value / maxValue) * 100;
          const sharePct = (seg.value / totalValue) * 100;
          const color = getInterestTypeColor(seg.cat);
          const isHov = hovered === seg.cat;

          return (
            <div
              key={seg.cat}
              style={{ opacity: hovered && !isHov ? 0.4 : 1, transition: "opacity 0.15s" }}
              onMouseEnter={() => setHovered(seg.cat)}
              onMouseLeave={() => setHovered(null)}
            >
              <div className="flex items-center justify-between mb-1 gap-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <div className="w-2 h-2 rounded-sm shrink-0" style={{ backgroundColor: color }} />
                  <span className="text-white/70 text-xs truncate">{seg.cat}</span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-white/30 text-xs">
                    {seg.count} physician{seg.count !== 1 ? "s" : ""}
                  </span>
                  <span className="text-white/25 text-xs w-10 text-right">
                    {sharePct.toFixed(1)}%
                  </span>
                  <span className="text-amber-400/80 text-xs font-mono w-20 text-right">
                    {seg.value >= 1000000
                      ? `$${(seg.value / 1000000).toFixed(2)}M`
                      : formatCurrency(seg.value)}
                  </span>
                </div>
              </div>
              <div className="w-full bg-white/5 rounded-full h-1.5">
                <div
                  className="h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${barPct}%`, backgroundColor: color }}
                />
              </div>
              {/* Hover detail row */}
              {isHov && seg.avgValue > 0 && (
                <p className="text-white/25 text-xs mt-1">
                  avg {formatCurrency(seg.avgValue)} per physician
                </p>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
        <p className="text-white/20 text-xs">
          Categorized from reported terms of interest · all years · sorted by value
        </p>
        <button
          onClick={() => exportInterestTypeCsv(segments, totalValue, companyName, companyId)}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white/70 hover:border-white/20 transition-all shrink-0"
        >
          ⬇ Export
        </button>
      </div>
    </div>
  );
}

function exportInterestTypeCsv(segments, totalValue, companyName, companyId) {
  const rows = [["Company", "Company ID", "Interest Type", "Physician Count", "Total Interest Value ($)", "% of Total", "Avg Value Per Physician ($)"]];
  for (const seg of segments) {
    rows.push([
      companyName,
      `'${companyId}`,
      seg.cat,
      seg.count,
      seg.value.toFixed(2),
      ((seg.value / totalValue) * 100).toFixed(1) + "%",
      seg.avgValue.toFixed(2),
    ]);
  }
  downloadCsv(rows, `${companyName.replace(/\s+/g, "_")}_ownership_interest_types.csv`);
}

// ── Research Funding Trend Chart ──────────────────────────────────────────────
function ResearchFundingTrendChart({ yearlyData, selectedResearchYear, onYearSelect, companyName, companyId }) {
  const [hovered, setHovered] = useState(null);
  if (!yearlyData || !yearlyData.length) return null;

  const maxAmount = Math.max(...yearlyData.map((y) => y.totalAmount), 1);

  function exportCsv() {
    const rows = [["Company", "Company ID", "Year", "Total Funding ($)", "Investigators", "Payments", "Studies", "Preclinical", "Avg Per Investigator ($)", "Avg Per Payment ($)"]];
    for (const y of yearlyData) {
      const avgPerInvestigator = y.investigatorCount > 0 ? (y.totalAmount / y.investigatorCount).toFixed(2) : "0.00";
      const avgPerPayment = y.paymentCount > 0 ? (y.totalAmount / y.paymentCount).toFixed(2) : "0.00";
      rows.push([companyName, `'${companyId}`, y.programYear, y.totalAmount.toFixed(2), y.investigatorCount, y.paymentCount, y.studyCount, y.preclinicalCount, avgPerInvestigator, avgPerPayment]);
    }
    const csv = rows.map((r) => r.map((v) => `"${v ?? ""}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `${companyName.replace(/\s+/g, "_")}_research_funding_trend.csv`;
    a.click();
  }

  const hovData = hovered ? yearlyData.find((y) => y.programYear === hovered) : null;

  return (
    <div className="card mb-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white/60 text-xs uppercase tracking-widest">Research funding trend · all years</h2>
        {hovData && (
          <div className="text-right">
            <p className="text-white/40 text-xs font-medium">{hovData.programYear}</p>
            <p className="text-teal-400 text-xs font-mono">{formatCurrency(hovData.totalAmount)}</p>
          </div>
        )}
      </div>

      <div className="relative" style={{ height: "180px" }}>
        {/* Y-axis */}
        <div className="absolute left-0 top-0 bottom-6 flex flex-col justify-between items-end pr-1 pointer-events-none" style={{ width: "44px" }}>
          {[1, 0.75, 0.5, 0.25, 0].map((pct) => {
            const val = maxAmount * pct;
            return (
              <span key={pct} className="text-white/20" style={{ fontSize: "8px" }}>
                {val >= 1000000 ? `$${(val / 1000000).toFixed(1)}M`
                  : val >= 1000 ? `$${(val / 1000).toFixed(0)}K`
                  : val > 0 ? `$${val.toFixed(0)}` : "$0"}
              </span>
            );
          })}
        </div>

        {/* Bars */}
        <div className="absolute inset-0 flex items-end gap-1" style={{ left: "46px", paddingBottom: "24px" }}>
          {yearlyData.map((y) => {
            const heightPct = (y.totalAmount / maxAmount) * 100;
            const isHov = hovered === y.programYear;
            return (
              <div
                key={y.programYear}
                className="flex-1 flex flex-col justify-end h-full relative"
                onMouseEnter={() => setHovered(y.programYear)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => onYearSelect(selectedResearchYear === y.programYear ? null : y.programYear)}
                style={{ cursor: "pointer" }}
              >
                {/* Tooltip */}
                {isHov && (
                  <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-navy-900 border border-white/10 rounded-lg px-3 py-2 z-20 pointer-events-none" style={{ minWidth: "170px" }}>
                    <p className="text-white/50 text-xs font-medium mb-1.5 pb-1.5 border-b border-white/10">
                      {y.programYear} · {formatCurrency(y.totalAmount)}
                    </p>
                    <div className="flex justify-between gap-2 mt-1">
                      <span className="text-white/40 text-xs">Investigators</span>
                      <span className="text-teal-400 text-xs">{y.investigatorCount.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between gap-2 mt-0.5">
                      <span className="text-white/40 text-xs">Payments</span>
                      <span className="text-white/60 text-xs">{y.paymentCount.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between gap-2 mt-0.5">
                      <span className="text-white/40 text-xs">Studies</span>
                      <span className="text-white/60 text-xs">{y.studyCount.toLocaleString()}</span>
                    </div>
                    {y.preclinicalCount > 0 && (
                      <div className="flex justify-between gap-2 mt-0.5">
                        <span className="text-white/40 text-xs">Preclinical</span>
                        <span className="text-violet-400 text-xs">{y.preclinicalCount.toLocaleString()}</span>
                      </div>
                    )}
                    {y.investigatorCount > 0 && (
                      <div className="flex justify-between gap-2 mt-1.5 pt-1.5 border-t border-white/5">
                        <span className="text-white/40 text-xs">Avg / investigator</span>
                        <span className="text-white/60 text-xs">{formatCurrency(y.totalAmount / y.investigatorCount)}</span>
                      </div>
                    )}
                    {y.paymentCount > 0 && (
                      <div className="flex justify-between gap-2 mt-0.5">
                        <span className="text-white/40 text-xs">Avg / payment</span>
                        <span className="text-white/60 text-xs">{formatCurrency(y.totalAmount / y.paymentCount)}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Amount label */}
                {y.totalAmount > 0 && (
                  <div className="absolute w-full text-center pointer-events-none" style={{ bottom: `${Math.max(heightPct, 2)}%`, paddingBottom: "2px" }}>
                    <span className="text-teal-400/70 font-medium" style={{ fontSize: "8px" }}>
                      {y.totalAmount >= 1000000 ? `$${(y.totalAmount / 1000000).toFixed(1)}M`
                        : y.totalAmount >= 1000 ? `$${(y.totalAmount / 1000).toFixed(0)}K`
                        : `$${y.totalAmount.toFixed(0)}`}
                    </span>
                  </div>
                )}

                {/* Bar */}
                <div
                  className="w-full rounded-t transition-all duration-150"
                  style={{
                    height: y.totalAmount > 0 ? `${Math.max(heightPct, 2)}%` : "2px",
                    backgroundColor: selectedResearchYear === y.programYear ? "rgba(20,184,166,0.95)" : "rgba(20,184,166,0.70)",
                    opacity: (hovered && !isHov) || (selectedResearchYear && selectedResearchYear !== y.programYear) ? 0.35 : 1,
                    outline: selectedResearchYear === y.programYear ? "2px solid rgba(20,184,166,0.6)" : "none",
                    outlineOffset: "1px",
                    borderRadius: "4px 4px 0 0",
                  }}
                />
              </div>
            );
          })}
        </div>

        {/* Year labels */}
        <div className="absolute bottom-0 flex gap-1" style={{ left: "46px", right: "0" }}>
          {yearlyData.map((y) => (
            <div key={y.programYear} className="flex-1 text-center">
              <span className={`transition-colors ${hovered === y.programYear ? "text-white/60" : "text-white/25"}`} style={{ fontSize: "10px" }}>
                {y.programYear}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
        <p className="text-white/20 text-xs">Total research funding disbursed · hover for detail</p>
        <button onClick={exportCsv} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white/70 hover:border-white/20 transition-all shrink-0">
          ⬇ Export
        </button>
      </div>
    </div>
  );
}

// ── Research Product Type Breakdown ───────────────────────────────────────────
function ResearchProductTypeChart({ productSummary, noProductNctPct, companyName, companyId, selectedYear }) {
  const [hovered, setHovered] = useState(null);
  if (!productSummary || !productSummary.length) return null;

  // Aggregate by productType from full dataset
  const groups = {};
  for (const r of productSummary) {
    const label = r.productType === "Not Specified" || !r.productType
      ? "No Product Reported"
      : r.productType;
    if (!groups[label]) groups[label] = { amount: 0, count: 0, studies: 0 };
    groups[label].amount += r.totalAmount || 0;
    groups[label].count += r.investigatorCount || 0;
    groups[label].studies += r.studyCount || 0;
  }

  const TYPE_COLORS = {
    "Drug":               "rgba(139,92,246,0.85)",
    "Biological":         "rgba(20,184,166,0.85)",
    "Device":             "rgba(59,130,246,0.85)",
    "Medical Supply":     "rgba(34,197,94,0.85)",
    "No Product Reported": "rgba(100,116,139,0.70)",
  };

  const segments = Object.entries(groups)
    .sort((a, b) => b[1].amount - a[1].amount)
    .map(([label, d]) => ({ label, ...d }));

  if (!segments.length) return null;
  const totalAmount = segments.reduce((s, seg) => s + seg.amount, 0);
  if (totalAmount === 0) return null;

  // SVG donut
  const cx = 60, cy = 60, R = 48, r = 30;
  let runAngle = -Math.PI / 2;
  const paths = segments.map((seg) => {
    const angle = (seg.amount / totalAmount) * 2 * Math.PI;
    const x1 = cx + R * Math.cos(runAngle);
    const y1 = cy + R * Math.sin(runAngle);
    runAngle += angle;
    const x2 = cx + R * Math.cos(runAngle);
    const y2 = cy + R * Math.sin(runAngle);
    const ix1 = cx + r * Math.cos(runAngle - angle);
    const iy1 = cy + r * Math.sin(runAngle - angle);
    const ix2 = cx + r * Math.cos(runAngle);
    const iy2 = cy + r * Math.sin(runAngle);
    const large = angle > Math.PI ? 1 : 0;
    const d = `M ${x1} ${y1} A ${R} ${R} 0 ${large} 1 ${x2} ${y2} L ${ix2} ${iy2} A ${r} ${r} 0 ${large} 0 ${ix1} ${iy1} Z`;
    return { ...seg, d };
  });

  const hov = hovered ? segments.find((s) => s.label === hovered) : null;

  function exportCsv() {
    const rows = [["Company", "Company ID", "Product Type", "Investigators", "Studies", "Total Funding ($)", "% of Total"]];
    for (const seg of segments) {
      rows.push([companyName, `'${companyId}`, seg.label, seg.count, seg.studies, seg.amount.toFixed(2), ((seg.amount / totalAmount) * 100).toFixed(1) + "%"]);
    }
    const csv = rows.map((r) => r.map((v) => `"${v ?? ""}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `${companyName.replace(/\s+/g, "_")}_research_product_types.csv`;
    a.click();
  }

  return (
    <div className="card mb-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-white/60 text-xs uppercase tracking-widest">Product type breakdown{selectedYear ? ` · ${selectedYear}` : ""}</h2>
      </div>

      <div className="flex items-center gap-4">
        {/* Donut */}
        <div className="shrink-0" style={{ width: 120, height: 120 }}>
          <svg viewBox="0 0 120 120" width="120" height="120">
            {paths.map((seg) => (
              <path
                key={seg.label}
                d={seg.d}
                fill={TYPE_COLORS[seg.label] || TYPE_COLORS["Not Specified"]}
                opacity={hovered && hovered !== seg.label ? 0.35 : 1}
                stroke="rgba(15,23,42,0.6)"
                strokeWidth="1"
                style={{ cursor: "pointer", transition: "opacity 0.15s" }}
                onMouseEnter={() => setHovered(seg.label)}
                onMouseLeave={() => setHovered(null)}
              />
            ))}
            {/* Center label */}
            <text x="60" y="54" textAnchor="middle" style={{ fontSize: "9px", fontWeight: 700, fill: "rgba(255,255,255,0.85)", fontFamily: "ui-sans-serif, system-ui" }}>
              {hov
                ? hov.amount >= 1000000000 ? `$${(hov.amount / 1000000000).toFixed(2)}B`
                  : hov.amount >= 1000000 ? `$${(hov.amount / 1000000).toFixed(1)}M`
                  : formatCurrency(hov.amount)
                : totalAmount >= 1000000000 ? `$${(totalAmount / 1000000000).toFixed(2)}B`
                : totalAmount >= 1000000 ? `$${(totalAmount / 1000000).toFixed(1)}M`
                : formatCurrency(totalAmount)}
            </text>
            <text x="60" y="66" textAnchor="middle" style={{ fontSize: "7px", fill: "rgba(255,255,255,0.35)", fontFamily: "ui-sans-serif, system-ui" }}>
              {hov ? hov.label.split(" ")[0].toLowerCase() : "total"}
            </text>
          </svg>
        </div>

        {/* Legend */}
        <div className="flex flex-col gap-2 flex-1 min-w-0">
          {segments.map((seg) => {
            const pct = ((seg.amount / totalAmount) * 100).toFixed(1);
            const color = TYPE_COLORS[seg.label] || TYPE_COLORS["Not Specified"];
            return (
              <div
                key={seg.label}
                className="flex items-center justify-between gap-2 cursor-pointer"
                onMouseEnter={() => setHovered(seg.label)}
                onMouseLeave={() => setHovered(null)}
                style={{ opacity: hovered && hovered !== seg.label ? 0.4 : 1, transition: "opacity 0.15s" }}
              >
                <div className="flex items-center gap-1.5 min-w-0">
                  <div className="w-2 h-2 rounded-sm shrink-0" style={{ backgroundColor: color }} />
                  <span className="text-white/50 text-xs truncate">{seg.label}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-white/25 text-xs">{pct}%</span>
                  <span className="text-teal-400 text-xs font-mono font-medium">
                    {seg.amount >= 1000000000 ? `$${(seg.amount / 1000000000).toFixed(2)}B`
                      : seg.amount >= 1000000 ? `$${(seg.amount / 1000000).toFixed(1)}M`
                      : formatCurrency(seg.amount)}
                  </span>
                </div>
              </div>
            );
          })}
          {hov && (
            <p className="text-white/25 text-xs mt-1">
              {hov.count} PI{hov.count !== 1 ? "s" : ""} · {hov.studies} {hov.studies === 1 ? "study" : "studies"}
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
        <p className="text-white/20 text-xs">
          By primary product type · "No Product Reported" reflects payments disclosed without a product name in CMS data
          {noProductNctPct >= 50 && (
            <span className="block mt-0.5 text-white/20">
              {noProductNctPct}% of "No Product Reported" payments have a registered ClinicalTrials.gov identifier — consistent with clinical trial site fee reporting
            </span>
          )}
        </p>
        <button onClick={exportCsv} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white/70 hover:border-white/20 transition-all shrink-0">
          ⬇ Export
        </button>
      </div>
    </div>
  );
}

// ── Top Products by Research Funding ─────────────────────────────────────────
function ResearchTopProductsChart({ productSummary, companyName, companyId, selectedYear }) {
  const [hovered, setHovered] = useState(null);
  if (!productSummary || !productSummary.length) return null;

  // Separate named products from no-product rows
  const namedProducts = productSummary
    .filter((r) => r.productName && r.productName.trim() !== "")
    .map((r) => ({
      label:       r.productName,
      productType: r.productType || "",
      amount:      r.totalAmount || 0,
      count:       r.investigatorCount || 0,
      studies:     r.studyCount || 0,
    }));

  // Aggregate all no-product-name rows into a single entry
  const noProductRows = productSummary.filter((r) => !r.productName || r.productName.trim() === "");
  const noProductTotal = noProductRows.reduce((s, r) => s + (r.totalAmount || 0), 0);
  const noProductInvestigators = noProductRows.reduce((s, r) => s + (r.investigatorCount || 0), 0);
  const noProductStudies = noProductRows.reduce((s, r) => s + (r.studyCount || 0), 0);

  // Build full list: top 10 named products, then "No Product Reported" if it exists
  const top10 = namedProducts.slice(0, 10);
  const allSegments = noProductTotal > 0
    ? [...namedProducts, { label: "No Product Reported", productType: "", amount: noProductTotal, count: noProductInvestigators, studies: noProductStudies }]
    : namedProducts;
  const segments = noProductTotal > 0
    ? [...top10, { label: "No Product Reported", productType: "", amount: noProductTotal, count: noProductInvestigators, studies: noProductStudies }]
    : top10;

  if (!segments.length) return null;
  const totalAmount = allSegments.reduce((s, seg) => s + seg.amount, 0);
  const maxAmount = Math.max(...segments.map((s) => s.amount), 1);

  const PRODUCT_TYPE_COLORS = {
    "Drug":           { bar: "rgba(139,92,246,0.75)", badge: "bg-purple-500/20 border-purple-500/30 text-purple-300" },
    "Biological":     { bar: "rgba(20,184,166,0.75)", badge: "bg-teal-500/20 border-teal-500/30 text-teal-300" },
    "Device":         { bar: "rgba(59,130,246,0.75)", badge: "bg-blue-500/20 border-blue-500/30 text-blue-300" },
    "Medical Supply": { bar: "rgba(34,197,94,0.75)",  badge: "bg-green-500/20 border-green-500/30 text-green-300" },
  };

  function getProductColors(type) {
    return PRODUCT_TYPE_COLORS[type] || { bar: "rgba(100,116,139,0.70)", badge: "bg-white/10 border-white/20 text-white/40" };
  }

  function exportCsv() {
    const rows = [["Company", "Company ID", "Product", "Product Type", "Investigators", "Studies", "Total Funding ($)", "% of Total"]];
    for (const seg of allSegments) {
      rows.push([companyName, `'${companyId}`, seg.label, seg.productType, seg.count, seg.studies, seg.amount.toFixed(2), ((seg.amount / totalAmount) * 100).toFixed(1) + "%"]);
    }
    const csv = rows.map((r) => r.map((v) => `"${v ?? ""}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `${companyName.replace(/\s+/g, "_")}_research_all_products.csv`;
    a.click();
  }

  return (
    <div className="card mb-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white/60 text-xs uppercase tracking-widest">Top products by research funding{selectedYear ? ` · ${selectedYear}` : ""}</h2>
        <span className="text-white/25 text-xs">{segments.length} product{segments.length !== 1 ? "s" : ""}</span>
      </div>
      <div className="space-y-3">
        {segments.map((seg, idx) => {
          const barPct = (seg.amount / maxAmount) * 100;
          const sharePct = (seg.amount / totalAmount) * 100;
          const isNoProduct = seg.label === "No Product Reported";
          const colors = isNoProduct
            ? { bar: "rgba(100,116,139,0.50)", badge: "bg-white/10 border-white/15 text-white/30" }
            : getProductColors(seg.productType);
          const isHov = hovered === seg.label;
          return (
            <div
              key={seg.label}
              style={{ opacity: hovered && !isHov ? 0.4 : 1, transition: "opacity 0.15s" }}
              onMouseEnter={() => setHovered(seg.label)}
              onMouseLeave={() => setHovered(null)}
              className={isNoProduct ? "mt-2 pt-2 border-t border-white/5" : ""}
            >
              <div className="flex items-center justify-between mb-1 gap-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="text-white/70 text-xs truncate">{seg.label}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {seg.productType && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full border shrink-0 ${colors.badge}`} style={{ fontSize: "10px" }}>
                      {seg.productType}
                    </span>
                  )}
                  <span className="text-white/25 text-xs">{sharePct.toFixed(1)}%</span>
                  <span className="text-teal-400/80 text-xs font-mono w-20 text-right">
                    {seg.amount >= 1000000 ? `$${(seg.amount / 1000000).toFixed(2)}M` : formatCurrency(seg.amount)}
                  </span>
                </div>
              </div>
              <div className="w-full bg-white/5 rounded-full h-1.5">
                <div className="h-1.5 rounded-full transition-all duration-300" style={{ width: `${barPct}%`, backgroundColor: colors.bar }} />
              </div>
              {isHov && (
                <p className="text-white/25 text-xs mt-1">{seg.count} investigator{seg.count !== 1 ? "s" : ""} · {seg.studies} {seg.studies === 1 ? "study" : "studies"}</p>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
        <p className="text-white/20 text-xs">Top 10 shown · export includes all {namedProducts.length} products{noProductTotal > 0 ? " + unlabeled payments" : ""}</p>
        <button onClick={exportCsv} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white/70 hover:border-white/20 transition-all shrink-0">
          ⬇ Export
        </button>
      </div>
    </div>
  );
}

// ── Research geographic distribution (choropleth by investigator state) ───────
function ResearchStateGrid({ stateData, selectedYear, companyName, companyId }) {
  const [hoveredState, setHoveredState] = useState(null);

  if (!stateData || !stateData.length) return null;

  const stateMap = {};
  stateData.forEach((s) => { if (s.state) stateMap[s.state] = s; });

  const maxAmount = Math.max(...stateData.map((s) => s.total_amount || 0), 1);

  function getFill(abbr) {
    const s = stateMap[abbr];
    if (!s || !s.total_amount) return "rgba(255,255,255,0.05)";
    const i = s.total_amount / maxAmount;
    if (i > 0.75) return "rgba(20,184,166,0.92)";
    if (i > 0.50) return "rgba(20,184,166,0.68)";
    if (i > 0.25) return "rgba(20,184,166,0.40)";
    if (i > 0.08) return "rgba(20,184,166,0.20)";
    return "rgba(20,184,166,0.08)";
  }

  const hovered = hoveredState ? stateMap[hoveredState] : null;
  const topStates = [...stateData]
    .filter((s) => s.state && s.total_amount > 0)
    .sort((a, b) => b.total_amount - a.total_amount)
    .slice(0, 5);

  return (
    <div className="card mb-4">
      {/* Print-only top-5 states table */}
      <div className="hidden print:block mb-4">
        <h2 className="text-gray-500 text-xs uppercase tracking-widest mb-3">
          Top States · Research Funding{selectedYear ? ` · ${selectedYear}` : " · All Years"}
        </h2>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
              <th style={{ textAlign: "left", padding: "4px 8px", color: "#6b7280", fontWeight: 600 }}>State</th>
              <th style={{ textAlign: "right", padding: "4px 8px", color: "#6b7280", fontWeight: 600 }}>Total Funding</th>
              <th style={{ textAlign: "right", padding: "4px 8px", color: "#6b7280", fontWeight: 600 }}>Investigators</th>
              <th style={{ textAlign: "right", padding: "4px 8px", color: "#6b7280", fontWeight: 600 }}>Payments</th>
            </tr>
          </thead>
          <tbody>
            {[...stateData]
              .filter((s) => s.state && (s.total_amount || 0) > 0)
              .sort((a, b) => b.total_amount - a.total_amount)
              .slice(0, 5)
              .map((s) => (
                <tr key={s.state} style={{ borderBottom: "1px solid #f3f4f6" }}>
                  <td style={{ padding: "5px 8px", color: "#111827", fontWeight: 500 }}>{s.state}</td>
                  <td style={{ padding: "5px 8px", textAlign: "right", color: "#0f766e", fontFamily: "monospace" }}>{formatCurrency(s.total_amount)}</td>
                  <td style={{ padding: "5px 8px", textAlign: "right", color: "#374151" }}>{(s.investigator_count || 0).toLocaleString()}</td>
                  <td style={{ padding: "5px 8px", textAlign: "right", color: "#374151" }}>{(s.payment_count || 0).toLocaleString()}</td>
                </tr>
              ))}
          </tbody>
        </table>
        {stateData.filter((s) => s.state && s.total_amount > 0).length > 5 && (
          <p className="text-gray-400 text-xs mt-2">
            Showing 5 of {stateData.filter((s) => s.state && s.total_amount > 0).length} states · export full list from ProviderPayWatch
          </p>
        )}
      </div>

      <div className="flex items-center justify-between mb-3">
        <h2 className="text-white/60 text-xs uppercase tracking-widest">
          Geographic Distribution — Research Funding & Direct Payments{selectedYear ? ` · ${selectedYear}` : " · all years"}
        </h2>
        {hovered ? (
          <div className="text-right">
            <p className="text-white/70 text-xs font-semibold mb-1">{hoveredState}</p>
            <div className="space-y-0.5">
              <div className="flex items-baseline justify-end gap-2">
                <span className="text-white/30 text-xs">funding</span>
                <span className="text-teal-400 text-xs font-mono">{formatCurrency(hovered.total_amount)}</span>
              </div>
              <div className="flex items-baseline justify-end gap-2">
                <span className="text-white/30 text-xs">providers</span>
                <span className="text-white/60 text-xs font-mono">{(hovered.investigator_count || 0).toLocaleString()}</span>
              </div>
              <div className="flex items-baseline justify-end gap-2">
                <span className="text-white/30 text-xs">payments</span>
                <span className="text-white/60 text-xs font-mono">{(hovered.payment_count || 0).toLocaleString()}</span>
              </div>
              {hovered.investigator_count > 0 && (
                <div className="flex items-baseline justify-end gap-2">
                  <span className="text-white/30 text-xs">avg / investigator</span>
                  <span className="text-white/60 text-xs font-mono">{formatCurrency(hovered.total_amount / hovered.investigator_count)}</span>
                </div>
              )}
              {hovered.payment_count > 0 && (
                <div className="flex items-baseline justify-end gap-2">
                  <span className="text-white/30 text-xs">avg / payment</span>
                  <span className="text-white/60 text-xs font-mono">{formatCurrency(hovered.total_amount / hovered.payment_count)}</span>
                </div>
              )}
            </div>
          </div>
        ) : (
          <p className="print:hidden text-white/20 text-xs">hover a state</p>
        )}
      </div>

      <div className="w-full rounded-lg overflow-hidden" style={{ background: "rgba(255,255,255,0.02)", marginLeft: "-8px", marginRight: "-8px", width: "calc(100% + 16px)" }}>
        <ComposableMap
          projection="geoAlbersUsa"
          style={{ width: "100%", height: "auto", display: "block" }}
          projectionConfig={{ scale: 1020, translate: [400, 240] }}
        >
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const fips = geo.id?.toString().padStart(2, "0");
                const abbr = FIPS_TO_ABBR[fips];
                const isHovered = hoveredState === abbr;
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={getFill(abbr)}
                    stroke={isHovered ? "rgba(20,184,166,0.8)" : "rgba(255,255,255,0.10)"}
                    strokeWidth={isHovered ? 1.5 : 0.5}
                    style={{
                      default: { outline: "none", cursor: stateMap[abbr] ? "pointer" : "default", transition: "fill 0.15s" },
                      hover:   { outline: "none" },
                      pressed: { outline: "none" },
                    }}
                    onMouseEnter={() => abbr && setHoveredState(abbr)}
                    onMouseLeave={() => setHoveredState(null)}
                  />
                );
              })
            }
          </Geographies>

          {Object.entries(STATE_CENTROIDS).map(([abbr, { lon, lat }]) => {
            const s = stateMap[abbr];
            const intensity = s ? s.total_amount / maxAmount : 0;
            const offset = LABEL_OFFSETS[abbr];
            const dx = offset?.dx || 0;
            const dy = offset?.dy || 0;
            const textColor = intensity > 0.5
              ? "rgba(0,0,0,0.75)"
              : "rgba(255,255,255,0.55)";
            return (
              <Annotation
                key={abbr}
                subject={[lon, lat]}
                dx={dx}
                dy={dy}
                connectorProps={offset ? {
                  stroke: "rgba(255,255,255,0.25)",
                  strokeWidth: 0.5,
                  strokeLinecap: "round",
                } : {}}
              >
                <text
                  textAnchor="middle"
                  alignmentBaseline="middle"
                  style={{
                    fontSize: abbr === "DC" ? "5px" : "7px",
                    fontWeight: 600,
                    fill: textColor,
                    pointerEvents: "none",
                    userSelect: "none",
                    fontFamily: "ui-monospace, monospace",
                  }}
                >
                  {abbr}
                </text>
              </Annotation>
            );
          })}
        </ComposableMap>
      </div>

      <div className="mt-3 pt-3 border-t border-white/5 space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-white/20 text-xs shrink-0">More</span>
          <div className="flex gap-0.5 flex-1">
            {[0.92, 0.68, 0.40, 0.20, 0.08].map((v) => (
              <div key={v} className="flex-1 h-2 rounded-sm"
                style={{ backgroundColor: `rgba(20,184,166,${v})` }} />
            ))}
          </div>
          <span className="text-white/20 text-xs shrink-0">Less</span>
        </div>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex gap-x-4 gap-y-1 flex-wrap">
            {topStates.map((s) => (
              <span key={s.state} className="text-white/30 text-xs">
                <span className="text-white/50">{s.state}</span> {formatCurrency(s.total_amount)}
              </span>
            ))}
          </div>
          <button
            onClick={() => exportResearchGeographicCsv(stateData, selectedYear, companyName, companyId)}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white/70 hover:border-white/20 transition-all shrink-0"
          >
            ⬇ Export
          </button>
        </div>
      </div>
    </div>
  );
}

function exportResearchGeographicCsv(states, year, companyName, companyId) {
  const rows = [
    ["Note: Includes both PI-based research funding and direct research payments. A provider's state is taken from their primary registered location. Providers based in multiple states could appear in more than one row."],
    [],
    ["Company", "Company ID", "Year", "State", "Total Funding ($)", "Payment Count", "Provider Count", "Avg Per Payment ($)", "Avg Per Provider ($)"],
  ];
  const sorted = [...states]
    .filter((s) => s.state && s.total_amount > 0)
    .sort((a, b) => b.total_amount - a.total_amount);
  for (const s of sorted) {
    const avgPerPayment = s.payment_count > 0 ? (s.total_amount / s.payment_count).toFixed(2) : "0.00";
    const avgPerInvestigator = s.investigator_count > 0 ? (s.total_amount / s.investigator_count).toFixed(2) : "0.00";
    rows.push([
      companyName,
      `'${companyId}`,
      year || "all years",
      s.state,
      s.total_amount.toFixed(2),
      s.payment_count || 0,
      s.investigator_count || 0,
      avgPerPayment,
      avgPerInvestigator,
    ]);
  }
  downloadCsv(rows, `${companyName.replace(/\s+/g, "_")}_research_geographic${year ? `_${year}` : ""}.csv`);
}

// ── Research Charts wrapper ───────────────────────────────────────────────────
function ResearchCharts({ yearlyData, research, productSummary, yearFilteredProducts, stateBreakdown, selectedResearchYear, onYearSelect, noProductNctPct, companyName, companyId }) {
  if (!research || !research.length) return null;

  // Use year-filtered products when a year is selected, otherwise all-years
  const displayProducts = yearFilteredProducts !== null ? yearFilteredProducts : productSummary;

  return (
    <div className="mb-6">
      <ResearchFundingTrendChart
        yearlyData={yearlyData}
        selectedResearchYear={selectedResearchYear}
        onYearSelect={onYearSelect}
        companyName={companyName}
        companyId={companyId}
      />
      {selectedResearchYear && (
        <div className="flex items-center justify-between mb-3">
          <p className="text-teal-400/60 text-xs">
            Product and geographic charts filtered to {selectedResearchYear}
          </p>
          <button
            onClick={() => onYearSelect(null)}
            className="text-xs text-white/40 hover:text-white/70 transition-colors"
          >
            ✕ Clear year filter
          </button>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <ResearchProductTypeChart productSummary={displayProducts} noProductNctPct={noProductNctPct} companyName={companyName} companyId={companyId} selectedYear={selectedResearchYear} />
        <ResearchTopProductsChart productSummary={displayProducts} companyName={companyName} companyId={companyId} selectedYear={selectedResearchYear} />
      </div>
      <ResearchStateGrid
        stateData={stateBreakdown}
        selectedYear={selectedResearchYear}
        companyName={companyName}
        companyId={companyId}
      />
    </div>
  );
}

// ── Research Tab ──────────────────────────────────────────────────────────────
function ResearchTab({
  companyId, companyName,
  excludedNpis, paymentNpis, ownershipNpis, researchNpis, dualInterestNpis, tripleInterestNpis,
  watchedNpis, setWatchedNpis,
  isPro, isEnterprise = false, setUpgradeMessage, setShowUpgradeModal,
  setPaymentNpis,
  conflictCounts, researchGeneralDualCount,
  filterParams = {}, filterActive = false,
  filterUserId = "", filterListIds = "",
  trackedNpiSet = new Set(),
}) {
  const EXPORT_LIMIT = isEnterprise ? Infinity : 500;
  const [research, setResearch] = useState([]);
  const [directResearch, setDirectResearch] = useState([]);
  const [directByYear, setDirectByYear] = useState([]); // npi+year rows for filtering
  const [directYearlyTotals, setDirectYearlyTotals] = useState([]); // year-level totals for stat cards
  const [piByYear, setPiByYear] = useState([]); // npi+year rows for PI research
  const [yearlyData, setYearlyData] = useState([]);
  const [totalInvestigatorCount, setTotalInvestigatorCount] = useState(null);
  const [productSummary, setProductSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [providerNames, setProviderNames] = useState({});
  const [providerDetails, setProviderDetails] = useState({});
  const [resExcludedNpis, setResExcludedNpis] = useState(new Map());
  const [resOwnershipNpis, setResOwnershipNpis] = useState(new Set());
  const [resDualInterestNpis, setResDualInterestNpis] = useState(new Set());
  const [resTripleInterestNpis, setResTripleInterestNpis] = useState(new Set());
  const [noProductNctPct, setNoProductNctPct] = useState(0);
  const [showMore, setShowMore] = useState(false);
  const [selectedResearchYear, setSelectedResearchYear] = useState(null);
  const [yearFilteredProducts, setYearFilteredProducts] = useState(null);
  const [stateBreakdown, setStateBreakdown] = useState([]);

  async function handleFullExport() {
    setExportLoading(true);
    try {
      // Full export needs both PI and direct research, merged the same way
      // combinedList already does on screen — the old version only fetched
      // PI data, so direct-only recipients (like triple-interest providers
      // with no PI role) never appeared even in the "full" export.
      const [piFull, directFull] = await Promise.all([
        getResearchByCompanyFull(companyId, filterParams),
        getDirectResearchByCompanyFull(companyId).catch(() => []),
      ]);
      const piMap = {};
      for (const r of piFull) piMap[r.npi] = r;
      const directMap = {};
      for (const r of directFull) directMap[r.npi] = r;
      const allNpis = new Set([...Object.keys(piMap), ...Object.keys(directMap)]);
      // Full name lookup for the complete NPI set — providerNames (page
      // state) only covers whichever NPIs made it into the capped
      // on-screen list, which isn't guaranteed to include everyone who
      // shows up in this full, uncapped export.
      const nameResults = await getProviderNamesBatch([...allNpis]).catch(() => []);
      const fullNameMap = {};
      for (const r of nameResults) fullNameMap[r.npi] = `${r.firstName} ${r.lastName}`.trim();
      const merged = [...allNpis].map((npi) => {
        const pi = piMap[npi];
        const direct = directMap[npi];
        const piAmount = pi?.totalAmount || 0;
        const directAmount = direct?.totalAmount || 0;
        return {
          npi,
          firstName: pi?.firstName || "",
          lastName: pi?.lastName || "",
          specialty: pi?.specialty || "",
          state: pi?.state || "",
          firstYear: pi?.firstYear || direct?.firstYear || null,
          latestYear: pi?.latestYear || direct?.latestYear || null,
          hasActiveResearch: pi?.hasActiveResearch || direct?.hasActiveResearch || false,
          hasPreclinical: pi?.hasPreclinical || direct?.hasPreclinical || false,
          primaryContext: pi?.primaryContext || "",
          primaryProduct: pi?.primaryProduct || direct?.productName || "",
          latestNctId: pi?.latestNctId || "",
          piAmount,
          piPayments: pi?.paymentCount || 0,
          piStudies: pi?.studyCount || 0,
          directAmount,
          directPayments: direct?.paymentCount || 0,
          totalCombined: piAmount + directAmount,
        };
      }).sort((a, b) => b.totalCombined - a.totalCombined);
      exportResearchCsv(merged, fullNameMap, companyName, resDualInterestNpis, resTripleInterestNpis, EXPORT_LIMIT, excludedNpis, trackedNpiSet);
    } catch {
      // silently fail — user can retry
    } finally {
      setExportLoading(false);
    }
  }

  useEffect(() => {
    if (!companyId) return;
    setLoading(true);
    Promise.all([
      getResearchByCompany(companyId, filterParams),
      getResearchByCompanyByYear(companyId, filterParams),
      getResearchCountByCompany(companyId, filterParams),
      getResearchProductSummaryByCompany(companyId, filterParams),
      getDirectResearchByCompany(companyId, filterParams).catch(() => []),
      getDirectResearchByCompanyByYear(companyId).catch(() => []),
      getDirectResearchByCompanyYearlyTotals(companyId).catch(() => []),
      getPiResearchByCompanyByYear(companyId).catch(() => []),
    ])
      .then(([data, yearly, countResult, productData, directData, directByYearData, directYearlyData, piByYearData]) => {
        const researchData = data || [];
        setResearch(researchData);
        setDirectResearch(directData || []);
        setDirectByYear(directByYearData || []);
        setDirectYearlyTotals(directYearlyData || []);
        setPiByYear(piByYearData || []);
        setYearlyData(yearly || []);
        setTotalInvestigatorCount(countResult?.investigatorCount || researchData.length);
        setProductSummary(productData?.products || productData || []);
        setNoProductNctPct(productData?.noProductNctPct || 0);

        if (researchData.length > 0 || (directData || []).length > 0) {
          // Include NPIs from both PI research and direct research for name lookup
          const piNpis = researchData.map((r) => r.npi).filter(Boolean);
          const directNpis = (directData || []).map((r) => r.npi).filter(Boolean);
          const allNpis = [...new Set([...piNpis, ...directNpis])];

          // Batch name lookup
          getProviderNamesBatch(allNpis)
            .then((results) => {
              const nameMap = {};
              const detailMap = {};
              for (const r of results) {
                nameMap[r.npi] = `${r.firstName} ${r.lastName}`.trim();
                detailMap[r.npi] = r;
              }
              setProviderNames(nameMap);
              setProviderDetails(detailMap);
            })
            .catch(() => {});

          // Independent badge checks
          Promise.all([
            checkExclusionsBatch(piNpis),
            checkPaymentsBatch(piNpis),
            checkOwnershipBatch(piNpis),
            checkDualInterestBatch(piNpis),
          ])
            .then(([exclusions, payNpis, ownNpis, dualResults]) => {
              setResExcludedNpis(new Map(exclusions.map((e) => [e.npi, { oig: e.oig, sam: e.sam }])));
              setResOwnershipNpis(new Set(ownNpis));
              setResDualInterestNpis(new Set(dualResults.map((d) => d.npi)));
              setResTripleInterestNpis(new Set(dualResults.filter((d) => d.hasTripleInterest).map((d) => d.npi)));
              if (payNpis.length > 0 && setPaymentNpis) {
                setPaymentNpis((prev) => {
                  const next = new Set(prev);
                  payNpis.forEach((npi) => next.add(npi));
                  return next;
                });
              }
            })
            .catch(() => {});
        }
      })
      .catch((err) => { console.error("ResearchTab fetch error:", err); })
      .finally(() => setLoading(false));
  }, [companyId, filterUserId, filterListIds]);

  // ── Fetch year-filtered product summary when a year is selected ──────────
  useEffect(() => {
    if (!companyId || !selectedResearchYear) {
      setYearFilteredProducts(null);
      return;
    }
    const fp = filterUserId && filterListIds ? { userId: filterUserId, listIds: filterListIds } : {};
    getResearchProductSummaryByCompanyByYear(companyId, selectedResearchYear, fp)
      .then((data) => setYearFilteredProducts(data?.products || []))
      .catch(() => setYearFilteredProducts(null));
  }, [companyId, selectedResearchYear, filterUserId, filterListIds]);

  // ── Fetch state breakdown — respects selectedResearchYear (null = all years) ──
  useEffect(() => {
    if (!companyId) return;
    const fp = filterUserId && filterListIds ? { userId: filterUserId, listIds: filterListIds } : {};
    getResearchStateBreakdownByCompany(companyId, selectedResearchYear, fp)
      .then((data) => setStateBreakdown(data || []))
      .catch(() => setStateBreakdown([]));
  }, [companyId, selectedResearchYear, filterUserId, filterListIds]);

  // Derived values — use yearlyData for accurate totals across all investigators
  // ── Derived stats ────────────────────────────────────────────────────────
  const totalPiResearch = yearlyData.reduce((s, y) => s + (y.totalAmount || 0), 0);
  const totalPiPayments = yearlyData.reduce((s, y) => s + (y.paymentCount || 0), 0);
  const totalDirectResearch = directYearlyTotals.reduce((s, y) => s + (y.totalAmount || 0), 0);
  const totalDirectPayments = directYearlyTotals.reduce((s, y) => s + (y.paymentCount || 0), 0);
  const latestPiYear = yearlyData.find((y) => y.programYear === 2025);
  const activeInvestigatorCount = latestPiYear?.investigatorCount || 0;

  // Build a map of npi → { year → { totalAmount, paymentCount } } from directByYear
  const directByNpiYear = {};
  for (const row of directByYear) {
    if (!directByNpiYear[row.npi]) directByNpiYear[row.npi] = {};
    directByNpiYear[row.npi][row.year] = { totalAmount: row.totalAmount, paymentCount: row.paymentCount };
  }

  // Build a map of npi → { year → { totalAmount, paymentCount, studyCount } } from piByYear
  const piByNpiYear = {};
  for (const row of piByYear) {
    if (!piByNpiYear[row.npi]) piByNpiYear[row.npi] = {};
    piByNpiYear[row.npi][row.year] = { totalAmount: row.totalAmount, paymentCount: row.paymentCount, studyCount: row.studyCount };
  }

  // Build a Set of NPIs with direct research (all years)
  const directNpiSet = new Set(directResearch.map((r) => r.npi));

  // ── Combined list — merge PI + direct, filter by year if selected ────────
  const combinedList = (() => {
    const piMap = {};
    for (const r of research) piMap[r.npi] = r;

    const directMap = {};
    for (const r of directResearch) directMap[r.npi] = r;

    const allNpis = new Set([...Object.keys(piMap), ...Object.keys(directMap)]);

    const merged = [];
    for (const npi of allNpis) {
      const pi = piMap[npi];
      const direct = directMap[npi];

      // Year filtering — include if either PI or direct has data in selected year
      if (selectedResearchYear) {
        const piInYear = piByNpiYear[npi]?.[selectedResearchYear] != null;
        const directInYear = directByNpiYear[npi]?.[selectedResearchYear] != null;
        if (!piInYear && !directInYear) continue;
      }

      // Use year-specific amounts when a year is selected, all-years totals otherwise
      const piYearRow = selectedResearchYear ? (piByNpiYear[npi]?.[selectedResearchYear] || null) : null;
      const directYearRow = selectedResearchYear ? (directByNpiYear[npi]?.[selectedResearchYear] || null) : null;

      const piAmount = piYearRow ? piYearRow.totalAmount : (pi?.totalAmount || 0);
      const piPayments = piYearRow ? piYearRow.paymentCount : (pi?.paymentCount || 0);
      const piStudies = piYearRow ? piYearRow.studyCount : (pi?.studyCount || 0);
      const directAmount = directYearRow ? directYearRow.totalAmount : (direct?.totalAmount || 0);
      const directPayments = directYearRow ? directYearRow.paymentCount : (direct?.paymentCount || 0);

      merged.push({
        npi,
        firstName: pi?.firstName || "",
        lastName: pi?.lastName || "",
        specialty: pi?.specialty || "",
        state: pi?.state || "",
        firstYear: pi?.firstYear || direct?.firstYear || null,
        latestYear: pi?.latestYear || direct?.latestYear || null,
        hasActiveResearch: pi?.hasActiveResearch || direct?.hasActiveResearch || false,
        hasPreclinical: pi?.hasPreclinical || direct?.hasPreclinical || false,
        primaryContext: pi?.primaryContext || "",
        primaryProduct: pi?.primaryProduct || direct?.productName || "",
        primaryProductType: pi?.primaryProductType || direct?.productType || "",
        latestNctId: pi?.latestNctId || "",
        piAmount,
        piPayments,
        piStudies,
        directAmount,
        directPayments,
        totalCombined: piAmount + directAmount,
      });
    }

    return merged.sort((a, b) => b.totalCombined - a.totalCombined);
  })();

  const visibleCombined = combinedList.slice(0, showMore ? 50 : 10);

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="card animate-pulse">
            <div className="h-4 bg-white/10 rounded w-1/2 mb-3" />
            <div className="h-3 bg-white/5 rounded w-3/4" />
          </div>
        ))}
      </div>
    );
  }

  if (research.length === 0) {
    return (
      <div className="card text-center py-12">
        <p className="text-white/50 text-sm">
          No research payment records found for this company.
        </p>
        <p className="text-white/25 text-xs mt-2">
          CMS Open Payments Research data covers 2016–2025.
        </p>
      </div>
    );
  }

  return (
    <>
      {/* ── My Providers filter banner ───────────────────────────────────── */}
      {filterActive && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gold-500/10 border border-gold-500/30 mb-4 text-xs">
          <span className="text-gold-400">◉</span>
          <span className="text-gold-400/80">Filtered to your providers · all data reflects your provider list only</span>
        </div>
      )}

      {/* ── Conflict banners ─────────────────────────────────────────── */}
      <ConflictBanners
        tripleCount={conflictCounts?.tripleCount || 0}
        generalOwnershipCount={
          conflictCounts?.total > 0
            ? (conflictCounts.dualCount - (conflictCounts.researchCount || 0) + conflictCounts.tripleCount)
            : 0
        }
        generalResearchCount={researchGeneralDualCount || 0}
        ownershipResearchCount={conflictCounts?.researchCount || 0}
        totalOwners={conflictCounts?.total || 0}
        hasOwnershipData={conflictCounts?.total > 0}
      />

      {/* ── Year selector ───────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white/40 text-xs uppercase tracking-widest">Research</h2>
        <select
          value={selectedResearchYear ?? "all"}
          onChange={(e) => setSelectedResearchYear(e.target.value === "all" ? null : parseInt(e.target.value))}
          className="bg-navy-700 border border-white/10 text-white rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-teal-500/60"
        >
          <option value="all">All Years</option>
          {[2025,2024,2023,2022,2021,2020,2019,2018,2017,2016].filter((y) =>
            yearlyData.some((d) => d.programYear === y && d.totalAmount > 0) ||
            directYearlyTotals.some((d) => d.programYear === y && d.totalAmount > 0)
          ).map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>

      {/* ── Stat cards ──────────────────────────────────────────────────── */}
      <div className="mb-6">
        {/* All Years row */}
        <p className="text-white/25 text-xs uppercase tracking-widest mb-2">All years</p>
        <div className="flex gap-3 flex-wrap mb-4">
          <div className="card py-3 px-4 text-center">
            <div className="text-teal-400 font-display text-xl">{formatCurrency(totalPiResearch)}</div>
            <div className="text-white/30 text-xs mt-0.5">associated research</div>
          </div>
          {totalDirectResearch > 0 && (
            <div className="card py-3 px-4 text-center">
              <div className="text-teal-300 font-display text-xl">{formatCurrency(totalDirectResearch)}</div>
              <div className="text-white/30 text-xs mt-0.5">direct payments</div>
            </div>
          )}
          <div className="card py-3 px-4 text-center">
            <div className="text-white font-display text-xl">{(totalInvestigatorCount ?? research.length).toLocaleString()}</div>
            <div className="text-white/30 text-xs mt-0.5">providers</div>
          </div>
          <div className="card py-3 px-4 text-center">
            <div className="text-white font-display text-xl">{totalPiPayments.toLocaleString()}</div>
            <div className="text-white/30 text-xs mt-0.5">PI payments</div>
          </div>
          {totalDirectPayments > 0 && (
            <div className="card py-3 px-4 text-center">
              <div className="text-white font-display text-xl">{totalDirectPayments.toLocaleString()}</div>
              <div className="text-white/30 text-xs mt-0.5">direct payments</div>
            </div>
          )}
          </div>

        {/* Selected year row */}
        {selectedResearchYear && (() => {
          const piYear = yearlyData.find((y) => y.programYear === selectedResearchYear);
          const directYear = directYearlyTotals.find((y) => y.programYear === selectedResearchYear);
          if (!piYear && !directYear) return null;
          return (
            <>
              <p className="text-white/25 text-xs uppercase tracking-widest mb-2">{selectedResearchYear}</p>
              <div className="flex gap-3 flex-wrap">
                {piYear && (
                  <div className="card py-3 px-4 text-center">
                    <div className="text-teal-400/80 font-display text-xl">{formatCurrency(piYear.totalAmount)}</div>
                    <div className="text-white/30 text-xs mt-0.5">associated research</div>
                  </div>
                )}
                {directYear && (
                  <div className="card py-3 px-4 text-center">
                    <div className="text-teal-300/80 font-display text-xl">{formatCurrency(directYear.totalAmount)}</div>
                    <div className="text-white/30 text-xs mt-0.5">direct payments</div>
                  </div>
                )}
                {piYear && (
                  <div className="card py-3 px-4 text-center">
                    <div className="text-white/80 font-display text-xl">{(piYear.investigatorCount || 0).toLocaleString()}</div>
                    <div className="text-white/30 text-xs mt-0.5">investigators</div>
                  </div>
                )}
                {piYear && (
                  <div className="card py-3 px-4 text-center">
                    <div className="text-white/80 font-display text-xl">{(piYear.paymentCount || 0).toLocaleString()}</div>
                    <div className="text-white/30 text-xs mt-0.5">PI payments</div>
                  </div>
                )}
                {directYear && (
                  <div className="card py-3 px-4 text-center">
                    <div className="text-white/80 font-display text-xl">{(directYear.paymentCount || 0).toLocaleString()}</div>
                    <div className="text-white/30 text-xs mt-0.5">direct payments</div>
                  </div>
                )}
              </div>
            </>
          );
        })()}
      </div>

      <ResearchCharts
        yearlyData={yearlyData}
        research={research}
        productSummary={productSummary}
        yearFilteredProducts={yearFilteredProducts}
        stateBreakdown={stateBreakdown}
        selectedResearchYear={selectedResearchYear}
        onYearSelect={setSelectedResearchYear}
        noProductNctPct={noProductNctPct}
        companyName={companyName}
        companyId={companyId}
      />

      {/* Print-only top-5 investigator list */}
      {research.length > 0 && (
        <div className="hidden print:block mt-6 mb-4">
          <h2 className="text-gray-500 text-xs uppercase tracking-widest mb-3">
            Top Principal Investigators · All Years
          </h2>
          <div className="space-y-2">
            {research.slice(0, 5).map((r, i) => {
              const providerName = (r.firstName || r.lastName)
                ? `${r.firstName} ${r.lastName}`.trim()
                : providerNames[r.npi] || `NPI: ${r.npi}`;
              const isTriple = resTripleInterestNpis.has(r.npi);
              const isDual = !isTriple && resDualInterestNpis.has(r.npi);
              return (
                <div key={i} className={`border rounded-lg px-4 py-3 ${r.hasActiveResearch ? "border-teal-200 bg-teal-50" : "border-gray-200"}`}>
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-gray-900 text-sm font-medium">{providerName}</p>
                        {r.hasActiveResearch && <span className="text-xs text-green-700 border border-green-200 bg-green-50 px-1.5 py-0.5 rounded-full">Active 2025</span>}
                        {r.hasPreclinical && <span className="text-xs text-violet-600 border border-violet-200 bg-violet-50 px-1.5 py-0.5 rounded-full">Preclinical</span>}
                        {isTriple && <span className="text-xs text-red-600 border border-red-200 bg-red-50 px-1.5 py-0.5 rounded-full">⚠ Triple Interest</span>}
                        {isDual && <span className="text-xs text-orange-600 border border-orange-200 bg-orange-50 px-1.5 py-0.5 rounded-full">⚑ Dual Interest</span>}
                      </div>
                      <p className="text-gray-500 text-xs mt-0.5">
                        {r.firstYear === r.latestYear ? `${r.firstYear}` : `${r.firstYear}–${r.latestYear}`}
                        {" · "}{r.studyCount} {r.studyCount === 1 ? "study" : "studies"}
                        {" · "}{r.paymentCount} payments
                        {r.npi ? ` · NPI: ${r.npi}` : ""}
                      </p>
                      {r.primaryContext && <p className="text-gray-400 text-xs mt-0.5">{r.primaryContext}</p>}
                      {r.primaryProduct && <p className="text-gray-400 text-xs mt-0.5 italic">{r.primaryProductType ? `${r.primaryProductType}: ` : ""}{r.primaryProduct}</p>}
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-teal-700 text-sm font-mono font-medium">{formatCurrency(r.totalAmount)}</p>
                      <p className="text-gray-400 text-xs">total funding</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {(totalInvestigatorCount ?? research.length) > 5 && (
            <p className="text-gray-400 text-xs mt-2">Showing 5 of {(totalInvestigatorCount ?? research.length).toLocaleString()} providers · export full list from ProviderPayWatch</p>
          )}
        </div>
      )}

      {/* ── Combined investigator list ───────────────────────────────────── */}
      {(() => {
        return (
          <>
            <div className="print:hidden flex items-center justify-between mb-3">
              <div>
                <h3 className="text-white/40 text-xs uppercase tracking-widest">
                  Research Funding or Direct Payment Providers{selectedResearchYear ? ` · ${selectedResearchYear}` : " · all years"}
                </h3>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleFullExport}
                  disabled={exportLoading}
                  className="text-gold-400/80 hover:text-gold-400 text-xs transition-colors font-medium"
                >
                  {exportLoading ? "⏳ Exporting..." : "⬇ Export all"}
                </button>
              </div>
            </div>

      {/* Show more / less toggle — top */}
      {combinedList.length > 10 && (
        <div className="mb-3">
          <button
            onClick={() => setShowMore(!showMore)}
            className="text-xs text-white/40 hover:text-white/70 transition-colors flex items-center gap-1.5"
          >
            <span>{showMore ? "▲" : "▼"}</span>
            <span>
              {(() => {
                const total = selectedResearchYear ? combinedList.length : (totalInvestigatorCount ?? combinedList.length);
                return showMore
                  ? `Show top 10 of ${total.toLocaleString()} providers`
                  : total <= 50
                    ? `Show all ${total.toLocaleString()} providers`
                    : `Show top 50 of ${total.toLocaleString()} providers`;
              })()}
            </span>
          </button>
        </div>
      )}

      {/* Combined provider cards */}
      <div className="print:hidden space-y-3">
        {visibleCombined.map((r, i) => (
          <div
            key={i}
            className={`card hover:border-teal-500/30 transition-all group relative ${
              r.hasActiveResearch
                ? "bg-teal-500/[0.03] border-teal-500/20"
                : ""
            }`}
          >
            <Link
              to={`/physician/${r.npi}?name=${encodeURIComponent(
                providerNames[r.npi] || ""
              )}&state=${providerDetails[r.npi]?.state || ""}`}
              className="block"
            >
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-white font-medium group-hover:text-teal-300 transition-colors">
                      {(r.firstName || r.lastName)
                        ? `${r.firstName} ${r.lastName}`.trim()
                        : providerNames[r.npi] || `NPI: ${r.npi}`}
                    </h2>
                    {r.hasActiveResearch && (
                      <span className="badge bg-green-500/20 border border-green-500/30 text-green-400 text-xs">
                        Active 2025
                      </span>
                    )}
                    {r.hasPreclinical && (
                      <span className="badge bg-violet-500/20 border border-violet-500/30 text-violet-300 text-xs">
                        Preclinical
                      </span>
                    )}
                    {tripleInterestNpis?.has(r.npi) ? (
                      <span className="badge bg-red-500/10 border border-red-500/30 text-red-400 text-xs shrink-0">
                        ⚠ Triple Interest
                      </span>
                    ) : dualInterestNpis?.has(r.npi) ? (
                      <span className="badge bg-orange-500/10 border border-orange-500/30 text-orange-400 text-xs shrink-0">
                        ⚑ Dual Interest
                      </span>
                    ) : null}
                  </div>
                  <p className="text-white/40 text-sm mt-0.5">
                    {(r.specialty || providerDetails[r.npi]?.specialty)
                      ? `${r.specialty || providerDetails[r.npi]?.specialty} · `
                      : ""}
                    {(r.state || providerDetails[r.npi]?.state)
                      ? `${r.state || providerDetails[r.npi]?.state} · `
                      : ""}
                    {r.firstYear === r.latestYear
                      ? `${r.firstYear}`
                      : `${r.firstYear}–${r.latestYear}`}
                    {r.piStudies > 0 && ` · ${r.piStudies} ${r.piStudies === 1 ? "study" : "studies"}`}
                  </p>
                  {r.primaryContext && (
                    <p className="text-white/30 text-xs mt-0.5">{r.primaryContext}</p>
                  )}
                  {r.primaryProduct && (
                    <p className="text-white/25 text-xs mt-0.5 italic">
                      {r.primaryProductType ? `${r.primaryProductType}: ` : ""}
                      {r.primaryProduct}
                    </p>
                  )}
                  {r.latestNctId && (
                    <a href={`https://clinicaltrials.gov/study/${r.latestNctId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-teal-400/60 hover:text-teal-400 text-xs mt-1 transition-colors inline-block"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {r.latestNctId} → ClinicalTrials.gov
                    </a>
                  )}
                </div>
                <div className="text-right shrink-0 ml-4">
                  {r.piAmount > 0 && (
                    <div className="mb-1">
                      <p className="text-teal-400 text-sm font-mono font-medium">{formatCurrency(r.piAmount)}</p>
                      <p className="text-white/30 text-xs">associated research</p>
                    </div>
                  )}
                  {r.directAmount > 0 && (
                    <div>
                      <p className="text-teal-300 text-sm font-mono font-medium">{formatCurrency(r.directAmount)}</p>
                      <p className="text-white/30 text-xs">direct payments</p>
                    </div>
                  )}
                </div>
              </div>
            </Link>

            {/* Badge row */}
            <div className="mt-3">
              <ProviderBadges
                npi={r.npi}
                excludedNpis={resExcludedNpis.size ? resExcludedNpis : excludedNpis}
                paymentNpis={paymentNpis}
                ownershipNpis={resOwnershipNpis.size ? resOwnershipNpis : ownershipNpis}
                researchNpis={researchNpis}
                dualInterestNpis={resDualInterestNpis.size ? resDualInterestNpis : dualInterestNpis}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Show more toggle */}
      {combinedList.length > 10 && (
        <div className="print:hidden mt-3 flex items-center justify-between flex-wrap gap-2">
          <button
            onClick={() => setShowMore(!showMore)}
            className="text-xs text-white/40 hover:text-white/70 transition-colors flex items-center gap-1.5"
          >
            <span>{showMore ? "▲" : "▼"}</span>
            <span>
              {(() => {
                const total = selectedResearchYear ? combinedList.length : (totalInvestigatorCount ?? combinedList.length);
                return showMore
                  ? `Show top 10 of ${total.toLocaleString()} providers`
                  : total <= 50
                    ? `Show all ${total.toLocaleString()} providers`
                    : `Show top 50 of ${total.toLocaleString()} providers`;
              })()}
            </span>
          </button>
        </div>
      )}

      {/* Footer */}
      <div className="print:hidden mt-4 flex items-center justify-between flex-wrap gap-2 border-t border-white/5 pt-3">
        <p className="text-white/20 text-xs">
          Showing {Math.min(showMore ? 50 : 10, combinedList.length)} of{" "}
          {(selectedResearchYear ? combinedList.length : (totalInvestigatorCount ?? combinedList.length)).toLocaleString()} providers
          {selectedResearchYear ? ` · ${selectedResearchYear}` : " · 2016–2025"}
        </p>
        <div className="flex items-center gap-3">
          <button
            onClick={handleFullExport}
            disabled={exportLoading}
            className="text-gold-400/80 hover:text-gold-400 text-xs transition-colors font-medium"
          >
            {exportLoading ? "⏳ Exporting..." : "⬇ Export all"}
          </button>
        </div>
      </div>
          </>
        );
      })()}

      <p className="text-white/20 text-xs text-center mt-6">
        Source: CMS Open Payments Research · Payments disclosed by manufacturers
        as required by the Physician Payments Sunshine Act · 2016–2025
      </p>
    </>
  );
}

// ── Export helpers ────────────────────────────────────────────────────────────
function exportProvidersCsv(providers, companyName, year, dualInterestNpis = new Set(), tripleInterestNpis = new Set(), exportLimit = 500, podNpiMap = new Map(), excludedNpis = new Map(), trackedNpiSet = new Set()) {
  const rows = [["Known Provider / Market Provider", "First Name", "Last Name", "Specialty", "City", "State", "NPI",
    "Total Payments", "Payment Count", "Dual Interest", "Triple Interest", "POD Risk (OIG Work Plan 2026)", "OIG/SAM Excluded"]];
  const exportData = exportLimit === Infinity ? providers : providers.slice(0, exportLimit);
  for (const p of exportData) {
    const isTriple = tripleInterestNpis.has(p.npi);
    const isDual = dualInterestNpis.has(p.npi) || isTriple;
    const podTier = podNpiMap.get(p.npi);
    const excl = excludedNpis.get(p.npi);
    const exclLabel = excl
      ? [excl.oig?.length > 0 ? "OIG" : null, excl.sam?.length > 0 ? "SAM" : null].filter(Boolean).join("+") || "Yes"
      : "No";
    rows.push([
      trackedNpiSet.has(String(p.npi)) ? "Known Provider" : "Market Provider",
      p.firstName || "", p.lastName || "", p.specialty || "",
      p.city || "", p.state || "", p.npi || "",
      p.totalPayments ? p.totalPayments.toFixed(2) : "0.00",
      p.paymentCount || 0,
      isDual ? "Yes" : "No",
      isTriple ? "Yes" : "No",
      podTier ? `Tier ${podTier}` : "No",
      exclLabel,
    ]);
  }
  downloadCsv(rows, `${companyName.replace(/\s+/g, "_")}_providers_${year}.csv`);
}

function exportOwnershipCsv(ownership, companyName, providerNames = {}, companyPaymentNpis = new Set(), companyResearchNpis = new Set(), exportLimit = 500, podNpiMap = new Map(), excludedNpis = new Map(), trackedNpiSet = new Set()) {
  const rows = [["Known Provider / Market Provider", "Provider Name", "NPI", "Interest Holder", "Terms of Interest", "Value of Interest ($)",
    "Amount Invested ($)", "First Year", "Latest Year", "Active 2025", "Dispute Status",
    "Dual Interest", "Triple Interest", "POD Risk (OIG Work Plan 2026)", "OIG/SAM Excluded"]];
  const exportData = exportLimit === Infinity ? ownership : ownership.slice(0, exportLimit);
  for (const o of exportData) {
    const name = (o.firstName || o.lastName)
      ? `${o.firstName} ${o.lastName}`.trim()
      : providerNames[o.npi] || "";
    const isTriple = companyPaymentNpis.has(o.npi) && companyResearchNpis.has(o.npi);
    const isDual = companyPaymentNpis.has(o.npi) || companyResearchNpis.has(o.npi);
    const podTier = podNpiMap.get(o.npi);
    const excl = excludedNpis.get(o.npi);
    const exclLabel = excl
      ? [excl.oig?.length > 0 ? "OIG" : null, excl.sam?.length > 0 ? "SAM" : null].filter(Boolean).join("+") || "Yes"
      : "No";
    rows.push([
      trackedNpiSet.has(String(o.npi)) ? "Known Provider" : "Market Provider",
      name,
      o.npi, o.interestHolder, o.termsOfInterest,
      o.valueOfInterest ? o.valueOfInterest.toFixed(2) : "0.00",
      o.totalAmountInvested ? o.totalAmountInvested.toFixed(2) : "0.00",
      o.firstYear, o.latestYear,
      o.hasActiveOwnership ? "Yes" : "No",
      o.disputeStatus || "",
      isDual ? "Yes" : "No",
      isTriple ? "Yes" : "No",
      podTier ? `Tier ${podTier}` : "No",
      exclLabel,
    ]);
  }
  downloadCsv(rows, `${companyName.replace(/\s+/g, "_")}_ownership.csv`);
}

function exportResearchCsv(research, providerNames, companyName, dualInterestNpis = new Set(), tripleInterestNpis = new Set(), exportLimit = 500, excludedNpis = new Map(), trackedNpiSet = new Set()) {
  // PI Funding and Direct Payments shown separately — matching the on-screen
  // combinedList display, which already shows both distinctly. A provider
  // with both types shows both amounts, not one collapsed total.
  const rows = [["Known Provider / Market Provider", "Provider Name", "NPI", "Company",
    "PI Funding ($)", "Direct Payments ($)", "Total Funding ($)",
    "Payment Count", "Study Count", "First Year", "Latest Year",
    "Active 2025", "Context", "Preclinical", "Primary Product", "NCT ID",
    "Dual Interest", "Triple Interest", "OIG/SAM Excluded"]];
  const exportData = exportLimit === Infinity ? research : research.slice(0, exportLimit);
  for (const r of exportData) {
    const name = (r.firstName || r.lastName)
      ? `${r.firstName} ${r.lastName}`.trim()
      : providerNames[r.npi] || "";
    const isTriple = tripleInterestNpis.has(r.npi);
    const isDual = dualInterestNpis.has(r.npi) || isTriple;
    const excl = excludedNpis.get(r.npi);
    const exclLabel = excl
      ? [excl.oig?.length > 0 ? "OIG" : null, excl.sam?.length > 0 ? "SAM" : null].filter(Boolean).join("+") || "Yes"
      : "No";
    const piAmount = r.piAmount || 0;
    const directAmount = r.directAmount || 0;
    rows.push([
      trackedNpiSet.has(String(r.npi)) ? "Known Provider" : "Market Provider",
      name,
      r.npi,
      companyName,
      piAmount.toFixed(2),
      directAmount.toFixed(2),
      (piAmount + directAmount).toFixed(2),
      (r.piPayments || 0) + (r.directPayments || 0),
      r.piStudies || 0,
      r.firstYear || "",
      r.latestYear || "",
      r.hasActiveResearch ? "Yes" : "No",
      r.primaryContext || "",
      r.hasPreclinical ? "Yes" : "No",
      r.primaryProduct || "",
      r.latestNctId || "",
      isDual ? "Yes" : "No",
      isTriple ? "Yes" : "No",
      exclLabel,
    ]);
  }
  downloadCsv(rows, `${companyName.replace(/\s+/g, "_")}_research_providers.csv`);
}

function downloadCsv(rows, filename) {
  const csv = rows.map((r) => r.map((v) => `"${v ?? ""}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
}